/**
 * FelixChat — Left-side slide panel AI Program Manager.
 *
 * Felix is the user's single pane of glass into the SDLC platform.
 * He is context-aware, can create any SDLC artifact, runs Q&A
 * scope-discovery sessions inline, and surfaces errors as loggable
 * feedback tickets instead of retrying in infinite loops.
 *
 * The panel slides in from the left, adjacent to the activity ribbon,
 * replacing the nav panel. The right edge can be dragged to resize.
 * Utility icons from the ribbon animate into the Felix header toolbar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Markdown from "react-markdown";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import {
  Send,
  Brain,
  User,
  Loader2,
  ExternalLink,
  CheckCircle2,
  AlertCircle,
  Zap,
  Coins,
  XCircle,
  ClipboardList,
  ShieldCheck,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Eye,
  EyeOff,
  History,
  Check,
  X,
  Sparkles,
  Paperclip,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Pencil,
  PanelLeftClose,
  Square,
  Bell,
  PenSquare,
  Mail,
  CalendarClock,
  Video,
  Sun,
  Moon,
  Monitor,
  Settings,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useFelixChat, useQASession, useSubmitQAAnswer, useNotifications } from "@/api/hooks";
import {
  projectsApi,
  objectivesApi,
  testCasesApi,
  userStoriesApi,
  userPersonasApi,
  modulesApi,
  componentsApi,
  componentSpecificationsApi,
  tasksApi,
  meetingsApi,
  meetingBotApi,
  sprintsApi,
  changeRequestsApi,
  defectsApi,
  baselinesApi,
  outcomeRecordApi,
  memoryApi,
  conversationSessionsApi,
  feedbackApi,
  devBridgeApi,
  pipelineApi,
  assistantApi,
  supervisorApi,
  importsApi,
  conversationRatingsApi,
  qaSessionsApi,
  felixProposalsApi,
  type FelixAction,
  type FelixStreamEvent,
  type FelixAutonomousEvent,
  type FelixAutonomousActionComplete,
  type FelixAutonomousDone,
  type FelixFrontendAction,
  type FeedbackDiagnostics,
  type FelixBriefing,
  type TokenUsage,
  type QuickReply,
  gmailApi,
  calendarApi,
  authApi,
} from "@/api/services";
import { chatSessionWasSaved, markChatSessionSaved } from "@/lib/felixChatSession";
// Broadcast lives in its own module. Felix advertises broadcast tools and
// the API + client are fully built (53 endpoints, 29 client methods) —
// only the FelixChat wire was missing, so asking Felix to draft a campaign
// reached the dispatcher's fallback instead of the code that does it.
import { broadcastApi } from "@/api/broadcastApi";
import { leadsApi } from "@/api/leadsApi";
import { socialApi } from "@/api/socialApi";
import { defectPayload, leadContact, normaliseSocialPlatforms } from "@/components/felixActionGuards";
import { TokenUsageIndicator } from "@/components/TokenUsageIndicator";
import FelixHealthDot from "@/components/FelixHealthDot";
import { FelixChatClarifier } from "@/components/FelixChatClarifier";
import { handleSidebarOpenCategoryAction } from "@/components/FelixChat.sidebarOpenCategory";
import { FelixChatPlan } from "@/components/FelixChatPlan";
import type { PlanState, PlanStep, PlanStepStatus } from "@/components/FelixChatPlan";
import type { QAInteraction, FelixProposal } from "@/api/types";
import { FelixProposalCard } from "@/components/FelixProposalCard";
import { cn } from "@/lib/utils";
import { FELIX_OPEN_PROMPT_EVENT } from "@/lib/openFelixWithPrompt";
import { KICKOFF_CHIP, projectHomePath } from "@/lib/kickoff";
import {
  useSpeechRecognition,
  useSpeechSynthesis,
} from "@/hooks/useVoice";
import { useDeepgramSTT } from "@/hooks/useDeepgramSTT";
import { useTemplateLabels } from "@/hooks/useTemplateLabels";
import { useToast } from "@/components/ui/toast";
import { isCreditBlockPayload, useCreditBlockStore } from "@/stores/creditBlock";
import { useSidebarStore } from "@/stores/sidebar";
import { useFelixPanelStore, MIN_WIDTH, MAX_WIDTH } from "@/stores/felixPanel";
import { usePageContextStore } from "@/stores/pageContext";
import { useAuthStore } from "@/stores/auth";
import {
  getProjectStorageKey,
  loadPersistedMessages,
  purgeLegacyChatHistory,
} from "@/lib/felixChatHistory";
import { useThemeStore, type ThemeMode } from "@/stores/theme";
import { useAutonomousRunStore } from "@/stores/autonomousRun";
import { usePresenting } from "@/stores/presenting";
import { isInternalFeedItem } from "@/lib/presentingFilters";
import { clearTokens, ApiRequestError } from "@/api/client";
import {
  fieldCompletenessGateInfo,
  planLimitInfo,
  type PlanLimitRefusal,
} from "@/lib/felixRefusal";
import { ComposeEmailDialog } from "@/components/ComposeEmailDialog";
import { FelixDog, type DogState } from "@/components/FelixDog";
import { FelixThinking, FELIX_THINKING_SEED, heldRoundStatus } from "@/components/FelixThinking";
import { BriefingCard } from "@/components/BriefingCard";
import { ParkedEntitiesPanel } from "@/components/ParkedEntitiesPanel";
import { FelixAppErrorNotice } from "@/components/FelixAppErrorNotice";
import { FelixConversationSidebar } from "@/components/FelixConversationSidebar";

// ---------------------------------------------------------------------------
// Global error buffer — other components can push errors here for Felix
// ---------------------------------------------------------------------------

const MAX_RECENT_ERRORS = 5;
let _recentErrors: { message: string; timestamp: number; source: string }[] = [];

/** Call this from anywhere to surface an error to Felix */
export function reportErrorToFelix(message: string, source = "app") {
  _recentErrors = [
    { message, timestamp: Date.now(), source },
    ..._recentErrors,
  ].slice(0, MAX_RECENT_ERRORS);
}

// ---------------------------------------------------------------------------
// JSON string coercion helper — Claude sometimes returns JSONB fields as
// serialised strings rather than parsed objects.  Parse them before sending.
// ---------------------------------------------------------------------------

function ensureParsed(value: unknown): unknown {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if ((trimmed.startsWith("[") && trimmed.endsWith("]")) ||
        (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
      try { return JSON.parse(trimmed); } catch { /* leave as-is */ }
    }
  }
  return value;
}

// ---------------------------------------------------------------------------
// FB-0002 FIX: Validate UUID fields — Felix sometimes generates placeholders
// like "PENDING" instead of real UUIDs, which cause 422 errors on the API.
// ---------------------------------------------------------------------------

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function validUuidOrUndef(v: unknown): string | undefined {
  if (typeof v === "string" && UUID_RE.test(v)) return v;
  return undefined;
}

// KI-0156: map internal FTL route patterns to the React Query key(s) that
// list that page's entities.  Used both in the navigate action handler and
// in the markdown <a> renderer so that clicking ANY internal link in Felix's
// responses clears stale cache and forces a fresh fetch on the destination page.
const _FELIX_PATH_QUERY_KEYS: Array<[RegExp, string[]]> = [
  [/\/modules(\/|$|\?)/, ["modules"]],
  [/\/components(\/|$|\?)/, ["components"]],
  [/\/component-specifications(\/|$|\?)/, ["componentSpecifications"]],
  [/\/objectives(\/|$|\?)/, ["objectives"]],
  [/\/user-stories(\/|$|\?)/, ["userStories"]],
  [/\/user-personas(\/|$|\?)/, ["userPersonas"]],
  [/\/test-cases(\/|$|\?)|\/tests(\/|$|\?)/, ["testCases"]],
  [/\/tasks(\/|$|\?)/, ["tasks"]],
  [/\/meetings(\/|$|\?)/, ["meetings"]],
  // forward-reservation: no active ["feedback"] TanStack query today;
  // annotated so the entry documents intent rather than silently being inert.
  [/\/feedback(\/|$|\?)/, ["feedback"]],
  [/\/change-requests(\/|$|\?)|\/change-control(\/|$|\?)/, ["changeRequests"]],
  [/\/sprints(\/|$|\?)/, ["sprints"]],
];

// ---------------------------------------------------------------------------
// Left-edge resize hook — drag the left border to resize the Felix panel
// ---------------------------------------------------------------------------

function usePanelResize(setWidth: (w: number) => void) {
  const resizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    resizing.current = true;
    startX.current = e.clientX;
    startWidth.current = useFelixPanelStore.getState().width;
    e.preventDefault();
    e.stopPropagation();
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizing.current) return;
      // Dragging right increases width (panel is on the left edge)
      const delta = e.clientX - startX.current;
      const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, startWidth.current + delta));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      if (resizing.current) {
        resizing.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [setWidth]);

  return { onResizeMouseDown };
}

// (useResizable removed — panel uses left-edge resize via usePanelResize)

// ---------------------------------------------------------------------------
// Popout hook — render Felix in a separate browser window via Portal
// ---------------------------------------------------------------------------

function usePopout() {
  const [popoutWindow, setPopoutWindow] = useState<Window | null>(null);
  const [popoutContainer, setPopoutContainer] = useState<HTMLDivElement | null>(null);

  const openPopout = useCallback(() => {
    const win = window.open(
      "",
      "felix-chat",
      "width=480,height=700,resizable=yes,scrollbars=no,toolbar=no,menubar=no,location=no,status=no",
    );
    if (!win) return; // popup blocked

    win.document.title = "Felix \u2014 AI Program Manager";

    // Copy all stylesheets from parent
    const parentStyles = document.querySelectorAll('style, link[rel="stylesheet"]');
    parentStyles.forEach((node) => {
      win.document.head.appendChild(node.cloneNode(true));
    });

    // Copy theme class from <html>
    win.document.documentElement.className = document.documentElement.className;

    // Create container div
    const container = win.document.createElement("div");
    container.id = "felix-popout-root";
    container.style.cssText = "width:100%;height:100vh;display:flex;flex-direction:column;";
    win.document.body.style.cssText = "margin:0;padding:0;overflow:hidden;";
    win.document.body.appendChild(container);

    setPopoutWindow(win);
    setPopoutContainer(container);

    // Close handler — snap back to inline when user closes the popout
    win.addEventListener("beforeunload", () => {
      setPopoutWindow(null);
      setPopoutContainer(null);
    });
  }, []);

  const closePopout = useCallback(() => {
    if (popoutWindow && !popoutWindow.closed) {
      popoutWindow.close();
    }
    setPopoutWindow(null);
    setPopoutContainer(null);
  }, [popoutWindow]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (popoutWindow && !popoutWindow.closed) popoutWindow.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isPopout: !!popoutContainer, popoutContainer, openPopout, closePopout };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Actions tiered by risk level for confirmation flow:
 *
 * AUTO_EXECUTE — No user confirmation needed. Non-destructive
 * UI-only operations (navigation, feedback).
 *
 * CONFIRM_REQUIRED — Standard confirmation ("Go ahead" / "Cancel").
 * All create and update operations.
 *
 * DESTRUCTIVE_CONFIRM — Destructive operations requiring confirmation
 * with prominent warning style.
 *
 * Server-side tools (trigger_development, send_guidance, etc.) are
 * NOT in any set — they execute on the backend. */
const AUTO_EXECUTE = new Set([
  "navigate",
  "log_feedback",
  "update_feedback",
  // Phase 6 Patent I — Felix-driven nav-surface state is non-destructive
  // UI-only (opens a shelf); no confirmation required.
  "sidebar.open_category",
]);

/**
 * Does this action need a confirmation prompt?
 *
 * THE SERVER DECIDES. `felix_autonomy_level` is a founder dial, live in
 * platform_settings, and `felix_autonomy.requires_confirmation()` already
 * enforces it for Felix's server-side executor. This component did not know
 * any of that existed — it decided from the hardcoded sets below — so the dial
 * changed what the server would RUN and nothing about what the user was ASKED.
 *
 * Both directions were wrong. At FULL the org has said "stop asking me" and the
 * UI kept asking. At SUPERVISED the server gates every mutating tool, but the
 * UI only prompted for its hardcoded subset, so the rest were sent, refused
 * server-side, and came back as a refusal where a prompt belonged. The
 * discrepancy is live on the DEFAULT setting: at STANDARD the server runs
 * `create_task` without asking, and CONFIRM_REQUIRED below contains it.
 *
 * The flag is per-action rather than the client re-deriving the rules from a
 * level, because two copies of a policy drift — and a UI that drifts toward
 * asking LESS is the direction that matters.
 *
 * FALLS BACK TO THE HARDCODED SETS when the field is absent (an older server,
 * or an action assembled client-side). That fallback errs toward asking, which
 * is the safe direction: the cost of one unnecessary prompt is an annoyance,
 * the cost of one skipped prompt on a delete is unrecoverable.
 */
function actionNeedsConfirmation(a: FelixAction): boolean {
  if (typeof a.requires_confirmation === "boolean") return a.requires_confirmation;
  return CONFIRM_REQUIRED.has(a.type) || DESTRUCTIVE_CONFIRM.has(a.type);
}

const CONFIRM_REQUIRED = new Set([
  "create_project",
  "create_objective",
  "create_test_case",
  "create_user_story",
  "create_user_persona",
  "create_module",
  "create_component",
  "create_component_specification",
  "create_task",
  "create_meeting",
  "create_sprint",
  "add_sprint_item",
  // OUT-02 FLX-09: the Outcome Record one-tap confirm — the card IS the
  // consent surface (draft_outcome_record itself runs server-side).
  "confirm_outcome_record",
  "start_qa",
  "update_project",
  "update_module",
  "update_component",
  "update_component_specification",
  "update_objective",
  "update_user_story",
  "update_user_persona",
  "update_task",
  "update_test_case",
  "update_meeting",
  "create_change_request",
  // Fallback only, like the server-executed creates above: chat and stream
  // file defects server-side, and the one card that reaches here (withheld by
  // the autonomy level) is raised as pending regardless of this set.
  "create_defect",
  "create_baseline",
]);

const DESTRUCTIVE_CONFIRM = new Set([
  "delete_objective",
  "delete_user_story",
  "delete_user_persona",
  "delete_module",
  "delete_component",
  "delete_component_specification",
  "delete_task",
  "delete_test_case",
  "delete_meeting",
  "delete_project",
]);

/**
 * At-most-one-active-card invariant.
 *
 * Returns `messages` with every PRIOR un-confirmed confirmation card (except
 * `exceptId`) superseded — `pendingConfirmation` cleared and `actions`
 * dropped — so only the newest pending card is ever actionable. Applied
 * whenever a new pending card is shown OR the user sends a new message
 * (implicitly moving on from the old proposal).
 *
 * Mechanism, not outcome: a stale confirm bar can re-pop or stack only if a
 * second message also satisfies the `pendingConfirmation && actions` render
 * guard at FelixChat's transcript map. Collapsing all prior cards to a single
 * active one at every card-creation edge makes that guard true for at most one
 * message, which is the structural fix.
 *
 * Diagnosed 2026-06-20 (36h Felix conversation review): superseded action
 * cards lingered in the transcript and re-popped the confirm prompt, so users
 * faced a delete/confirm they thought they had already dealt with. The
 * FB-0005 pending-action reminder is unaffected — a superseded card carries
 * `actions: undefined`, so it naturally drops out of the reminder set (a
 * superseded proposal is, by definition, no longer pending).
 */
function supersedePendingCards(messages: Message[], exceptId?: string): Message[] {
  return messages.map((m) =>
    m.pendingConfirmation && m.id !== exceptId
      ? { ...m, pendingConfirmation: false, actions: undefined }
      : m,
  );
}

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  actions?: FelixAction[];
  actionResults?: ActionResult[];
  /** When set, waiting for user to confirm/cancel these actions */
  pendingConfirmation?: boolean;
  /** Row fad9bff7: Felix's approval cards from this turn (Approve / Change). */
  proposals?: FelixProposal[];
  /** Q&A dimension badge */
  dimension?: string;
  /** Token usage and cost from this interaction */
  tokenUsage?: TokenUsage;
  /** Extra metadata for special message types (e.g. Q&A review approval) */
  metadata?: Record<string, unknown>;
  /** Tools Felix used during this response (for reasoning transparency) */
  toolsUsed?: string[];
  /**
   * Row f5098ee9: what Felix is doing while a tool runs and his words are held,
   * shown with the spinner until his next words arrive (cleared by text_delta /
   * replace_text; rendered only while this message is the one streaming).
   */
  status?: string;
  /**
   * § 7.17 in-loop self-grade flag — set to true ONLY when the
   * grader's confidence band is "low" or the verdict is
   * hallucinated_action / low_confidence. Renders a small
   * "low confidence" chip beside the message. Anchoring guard
   * (Lee & See 2004 + Report 1 §3): never render when grader
   * confidence is medium / high.
   */
  lowConfidenceChip?: boolean;
  /** § 7.15 Phase 1 typed clarifier payload (from G refusal) */
  clarifierPayload?: import("@/api/services").ClarifierPayload | null;
  /** § 7.15 Phase 2 plan progress state */
  plan?: import("./FelixChatPlan").PlanState | null;
  /** KI-0210: post-action / post-upload suggestion chips the user can click to continue */
  suggestions?: string[];
  /**
   * FE-04 — when true this is a system/status event (e.g. the
   * post-action "N actions completed" notice), NOT a Felix
   * conversational turn. Rendered as a centered status pill, never
   * as an assistant bubble, so it can't be mistaken for something
   * Felix "said" or something the user typed. Invention G §7.9:
   * system notices are not conversational intent.
   */
  isSystemEvent?: boolean;
}

interface ActionResult {
  action: FelixAction;
  status: "pending" | "running" | "success" | "error";
  detail?: string;
  data?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Page name resolver
// ---------------------------------------------------------------------------

function getPageName(pathname: string): string {
  if (pathname === "/" || pathname === "/projects") return "Dashboard";
  if (pathname.includes("/objectives")) return "Objectives";
  if (pathname.includes("/modules")) return "Modules";
  if (pathname.includes("/specifications")) return "Component Specifications";
  if (pathname.includes("/components")) return "Components";
  if (pathname.includes("/user-stories")) return "User Stories";
  if (pathname.includes("/personas")) return "Personas";
  if (pathname.includes("/test-cases")) return "Test Cases";
  if (pathname.includes("/tasks")) return "Implementation Tasks";
  if (pathname.includes("/defects")) return "Defects";
  if (pathname.includes("/documents")) return "Documents";
  if (pathname.includes("/change-requests")) return "Change Requests";
  // ROW fd6ddd66. These four are REAL routes the client could not name, so
  // Felix's triggers for them could never fire — the backend was waiting on
  // "ChangeControl", "Broadcast", "Marketing" and "Domains" and getPageName
  // returned "Unknown" on every one.
  //
  // ADDED ONLY AFTER CHECKING THE ROUTER, which is what the row insists on:
  //   /change-control and a nested change-control      App.tsx:482, :514
  //   /projects/:projectId/broadcast                   App.tsx:681
  //   /marketing/leads | /segments | /templates        App.tsx:682-684
  //   /domains/checkout/* and /projects/:id/domain     App.tsx:528, :665
  // The names with NO route — Campaigns, GoLive, Launch, DomainSearch — were
  // deleted from the backend instead. Inventing entries for those would have
  // made a guard pass while the branches stayed just as dead.
  //
  // BEFORE /documents and /domains ordering matters: "/domains" must not be
  // caught by a looser earlier rule, and change-control must be tested before
  // any bare "/change" style match is ever added.
  if (pathname.includes("/change-control")) return "ChangeControl";
  if (pathname.includes("/broadcast")) return "Broadcast";
  if (pathname.includes("/marketing")) return "Marketing";
  if (pathname.includes("/domain")) return "Domains";
  if (pathname.includes("/baselines")) return "Baselines";
  if (pathname.includes("/traceability")) return "Traceability";
  if (pathname.includes("/reports")) return "Reports";
  if (pathname.includes("/settings")) return "Settings";
  if (pathname.includes("/dashboard")) return "Dashboard";
  if (pathname.includes("/meetings")) return "Meetings";
  if (pathname.includes("/sprints")) return "Sprints";
  if (pathname.includes("/feedback")) return "Feedback";
  if (pathname.includes("/milestones")) return "Milestones";
  if (pathname.includes("/pipeline")) return "Pipeline";
  if (pathname.includes("/conflicts")) return "Conflicts";
  if (pathname.includes("/outcomes")) return "Outcomes";
  if (pathname.includes("/audit")) return "Audit Log";
  if (pathname.includes("/team")) return "Team";
  if (pathname.includes("/billing")) return "Billing";
  if (pathname.includes("/roles")) return "Roles";
  if (pathname.includes("/status-registry")) return "Status Registry";
  if (pathname.includes("/user-profile")) return "User Profile";
  if (pathname.includes("/user-management")) return "User Management";
  if (pathname.includes("/success-policies")) return "Success Policies";
  if (pathname.includes("/platform/processes")) return "Active Processes";
  if (pathname.includes("/action-items")) return "Action Items";
  if (pathname === "/workspace") return "Workspace Hub";
  if (pathname.includes("/gmail")) return "Gmail";
  if (pathname.includes("/calendar")) return "Calendar";
  if (pathname.includes("/contacts")) return "Contacts";
  if (pathname.includes("/drive")) return "Drive";
  return "Platform";
}

// Map the canonical page NAME (from getPageName) to a canonical entity-type
// slug, for the DISPLAY-ONLY header breadcrumb skin. Only the entity-type
// pages appear here; every other page name (Dashboard, Traceability, Reports …)
// is left verbatim. IMPORTANT: this is used ONLY to relabel the visible
// breadcrumb — getPageName's return value is still sent to the backend as the
// canonical page_name identifier, so Felix's server-side page context is never
// perturbed by the project's vocabulary skin.
const PAGE_NAME_TO_CANONICAL: Record<string, string> = {
  Objectives: "objective",
  "User Stories": "user_story",
  Personas: "user_persona",
  Modules: "module",
  Components: "component",
  "Test Cases": "test_case",
  "Implementation Tasks": "impl_task",
  Defects: "defect",
  Documents: "document",
};

/** Map internal Q&A dimension keys to user-friendly labels.
 *  Internal identifiers like "conflict_resolution" should never leak to the UI.
 *  Returns null for dimensions that shouldn't be shown as a badge. */
function getDimensionLabel(dimension: string): string | null {
  const LABELS: Record<string, string> = {
    functional: "Functional",
    non_functional: "Non-Functional",
    compliance: "Compliance",
    data: "Data",
    scope: "Scope",
    measurability: "Success Criteria",
    stakeholders: "Stakeholders",
    strategic_alignment: "Strategy",
    // Internal-only dimensions — hide from users
    conflict_resolution: null as unknown as string,
  };
  const label = LABELS[dimension];
  // Explicitly hidden dimensions (mapped to null)
  if (dimension in LABELS && label === null) return null;
  if (label) return label;
  // Unknown dimensions: show title-cased if it looks user-friendly, else hide
  if (dimension.startsWith("_") || dimension.includes("internal")) return null;
  return dimension.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Contextual suggestion chips — based on current page
// ---------------------------------------------------------------------------

interface SuggestionChip {
  label: string;
  message: string;
}

function getSuggestionsForPage(pageName: string): SuggestionChip[] {
  switch (pageName) {
    case "Modules":
      return [
        { label: "Create module", message: "Help me create a new module (deliverable grouping)" },
        { label: "Review modules", message: "Review all modules and identify any scope overlap" },
        { label: "Assign components", message: "Which components are not assigned to any module?" },
      ];
    case "Components":
      return [
        { label: "Link to objectives", message: "Which components aren't linked to any objectives?" },
        { label: "Create component", message: "Help me create a new component" },
        { label: "Tech stack summary", message: "Summarize the technology stack across all components" },
      ];
    case "Component Specifications":
      return [
        { label: "Create spec", message: "Help me create a new component specification" },
        { label: "Review specs", message: "Review all specifications and identify gaps in coverage" },
        { label: "Migrate requirements", message: "Help me migrate archived requirements to component specifications" },
      ];
    case "Test Cases":
      return [
        { label: "Run test analysis", message: "Analyze test case coverage and identify gaps" },
        { label: "Generate tests", message: "Generate test cases for uncovered objectives" },
        { label: "Review test quality", message: "Review the quality and completeness of existing test cases" },
      ];
    case "Implementation Tasks":
      return [
        { label: "Update task statuses", message: "Help me update task statuses for the current sprint" },
        { label: "Create tasks", message: "Create implementation tasks for the next feature" },
        { label: "Show overdue", message: "Which tasks are overdue or at risk?" },
      ];
    case "Dashboard":
      return [
        { label: "Program overview", message: "Give me an executive summary of all projects" },
        { label: "What needs attention?", message: "What items across projects need my attention?" },
        { label: "Create a project", message: "Help me create a new project" },
      ];
    case "Objectives":
      return [
        { label: "Break down objectives", message: "Break down the objectives on this page into user stories and tasks" },
        { label: "Check alignment", message: "Are all user stories aligned with project objectives?" },
        { label: "Create objective", message: "Help me define a new project objective" },
      ];
    case "User Stories":
      return [
        { label: "Generate stories", message: "Generate user stories from existing objectives" },
        { label: "Check acceptance criteria", message: "Which user stories are missing acceptance criteria?" },
        { label: "Create user story", message: "Help me write a new user story" },
      ];
    case "Personas":
      return [
        { label: "Create persona", message: "Help me define a new user persona" },
        { label: "Map to stories", message: "Which personas don't have any linked user stories?" },
      ];
    case "Documents":
      return [
        { label: "Generate document", message: "Generate a project document" },
        { label: "What docs exist?", message: "List all documents in this project and their status" },
      ];
    case "Traceability":
      return [
        { label: "Find orphans", message: "Find entities with no traceability links" },
        { label: "Coverage report", message: "Show traceability coverage across all entity types" },
      ];
    case "Meetings":
      return [
        { label: "Schedule meeting", message: "Help me schedule a new meeting" },
        { label: "Meeting summary", message: "Summarize recent meeting action items" },
      ];
    case "Sprints":
      return [
        { label: "Sprint planning", message: "Help me plan the next sprint" },
        { label: "Sprint velocity", message: "What is our sprint velocity trend?" },
      ];
    case "Feedback":
      return [
        { label: "Open issues", message: "Show me all open feedback tickets" },
        { label: "Triage feedback", message: "Help me triage unresolved feedback tickets" },
      ];
    case "Conflicts":
      return [
        { label: "Resolve conflicts", message: "Help me resolve the open conflicts on this page" },
        { label: "Detect new conflicts", message: "Run conflict detection across all entities in this project" },
        { label: "Conflict summary", message: "Summarize the current conflict status for this project" },
      ];
    case "Action Items":
      return [
        { label: "Show overdue items", message: "What action items are overdue?" },
        { label: "Check SLA status", message: "Show me the SLA compliance summary" },
        { label: "List my items", message: "List all action items assigned to me" },
      ];
    case "Gmail":
      return [
        { label: "Check inbox", message: "Check my Gmail inbox for unread messages" },
        { label: "Search emails", message: "Search my emails for recent messages about the project" },
        { label: "Compose email", message: "Help me compose an email" },
      ];
    case "Calendar":
      return [
        { label: "Today's schedule", message: "What's on my calendar today?" },
        { label: "Schedule meeting", message: "Help me schedule a meeting with the team" },
        { label: "Check availability", message: "Check team availability for a meeting this week" },
      ];
    case "Contacts":
      return [
        { label: "Find contact", message: "Help me find a contact" },
        { label: "Team directory", message: "Show me the team directory" },
      ];
    case "Drive":
      return [
        { label: "Search files", message: "Search my Google Drive for project files" },
        { label: "Export to Drive", message: "Export project documents to Google Drive" },
      ];
    default:
      return [
        { label: "Project overview", message: "Give me an overview of this project" },
        { label: "What can you do?", message: "What can you help me with?" },
      ];
  }
}

/**
 * Extract entity_type and entity_id from a URL path.
 * Mirrors the backend _extract_entity_from_path logic so Felix gets explicit
 * entity context without relying on server-side URL parsing.
 */
/** @param url pathname AND search — the open record is a query param. */
function extractEntityFromPath(
  url: string,
): { entity_type: string; entity_id: string } | null {
  const pathname = url;
  // A LIST PAGE WITH AN OPEN PANEL FIRST — that is what the product does.
  // App.tsx mounts no /projects/{id}/{type}/{uuid} route at all; every entity
  // route is a list page, and the open record used to be component state that
  // never reached the URL. So the path match below could never fire, and 0 of
  // 8,490 conversation_history rows were entity-scoped. The pages now put the
  // open record in the query string; this reads it, and assistant.py's
  // server-side twin does the same so both sides agree about what is open.
  const panel = pathname.match(
    /\/projects\/[^/]+\/(objectives|test-cases|user-stories|components|tasks|defects)\?(?:[^#]*&)?(?:story|entity|id)=([0-9a-f-]{36})/,
  );
  const match = panel ?? pathname.match(
    /\/projects\/[^/]+\/(objectives|test-cases|user-stories|components|tasks|defects)\/([0-9a-f-]{36})/,
  );
  if (!match) return null;
  const typeMap: Record<string, string> = {
    objectives: "objective",
    "test-cases": "test_case",
    "user-stories": "user_story",
    components: "component",
    tasks: "impl_task",
    defects: "defect",
  };
  const entityType = typeMap[match[1]];
  return entityType ? { entity_type: entityType, entity_id: match[2] } : null;
}

/** Compute overall coverage % from coverage_scores */
function computeCoverage(scores: Record<string, number> | null | undefined): number {
  if (!scores) return 0;
  const vals = Object.values(scores);
  if (vals.length === 0) return 0;
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Background tasks widget — shows active/recent background tasks
// ---------------------------------------------------------------------------

function BackgroundTasksWidget() {
  const queryClient = useQueryClient();
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());
  const { data, isLoading } = useQuery({
    queryKey: ["felix", "tasks"],
    queryFn: () => assistantApi.listTasks(),
    // Poll every 3s when tasks are running for real-time visibility
    refetchInterval: 3_000,
  });

  const activeTasks = data?.tasks?.filter(
    (t) => t.status === "RUNNING" || t.status === "PENDING",
  ) ?? [];
  const recentCompleted = data?.tasks?.filter(
    (t) => (t.status === "COMPLETED" || t.status === "FAILED" || t.status === "CANCELLED") &&
      t.completed_at &&
      (Date.now() - new Date(t.completed_at).getTime()) < 30_000,
  ) ?? [];

  const handleCancel = async (taskId: string) => {
    setCancellingIds((prev) => new Set(prev).add(taskId));
    try {
      await assistantApi.cancelTask(taskId);
      queryClient.invalidateQueries({ queryKey: ["felix", "tasks"] });
    } catch {
      /* ignore — task may have already finished */
    } finally {
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  if (isLoading || (activeTasks.length === 0 && recentCompleted.length === 0)) return null;

  return (
    <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
      <div className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1 flex items-center gap-1">
        {activeTasks.length > 0 && <Loader2 className="h-3 w-3 animate-spin" />}
        Background Tasks ({activeTasks.length} active{recentCompleted.length > 0 ? `, ${recentCompleted.length} just finished` : ""})
      </div>
      {activeTasks.slice(0, 3).map((task) => {
        // Parse iteration results for live success/failure counts
        let successCount = 0;
        let failCount = 0;
        try {
          const results = typeof task.results === "string" ? JSON.parse(task.results) : task.results;
          if (Array.isArray(results)) {
            for (const r of results) {
              successCount += r.successes ?? 0;
              failCount += r.errors ?? 0;
            }
          }
        } catch { /* ignore */ }
        const isCancelling = cancellingIds.has(task.id);
        return (
          <div key={task.id} className="text-xs text-slate-600 dark:text-slate-300 py-0.5">
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-16 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0">
                <div
                  className="h-full rounded-full bg-brand-500 transition-all"
                  style={{ width: task.total_steps > 0 ? `${Math.min((task.progress / task.total_steps) * 100, 100)}%` : "0%" }}
                />
              </div>
              <span className="truncate">{task.description}</span>
              <span className="shrink-0 font-medium">
                iter {task.progress}/{task.total_steps}
              </span>
              <button
                onClick={() => handleCancel(task.id)}
                disabled={isCancelling}
                className="shrink-0 ml-auto text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                title="Cancel this task"
              >
                {isCancelling ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
              </button>
            </div>
            {(successCount > 0 || failCount > 0) && (
              <div className="ml-[4.25rem] text-xs">
                <span className="text-green-700 dark:text-green-400">{successCount} succeeded</span>
                {failCount > 0 && <span className="text-red-600 dark:text-red-400 ml-1">{failCount} failed</span>}
              </div>
            )}
          </div>
        );
      })}
      {recentCompleted.slice(0, 2).map((task) => (
        <div key={task.id} className="text-xs py-0.5 flex items-center gap-1.5">
          <span className={task.status === "COMPLETED" ? "text-green-700 dark:text-green-400" : task.status === "CANCELLED" ? "text-yellow-700 dark:text-yellow-400" : "text-red-600 dark:text-red-400"}>
            {task.status === "COMPLETED" ? "✓" : task.status === "CANCELLED" ? "⊘" : "✗"}
          </span>
          <span className="truncate text-slate-600 dark:text-slate-300">{task.description}</span>
          <span className="shrink-0 text-xs text-slate-600 dark:text-slate-300">{task.status === "CANCELLED" ? "cancelled" : "just now"}</span>
        </div>
      ))}
    </div>
  );
}

/** Design Refresh Phase 4: message bubble entrance ease — same
 *  cubic-bezier as ``--ease-wow``. Defined at module scope so it's a
 *  stable reference and framer-motion doesn't re-allocate on every
 *  render (else React would re-key all the m.div children). */
const FELIX_MSG_EASE: [number, number, number, number] = [0.2, 0.8, 0.2, 1];

export function FelixChat() {
  const isOpen = useFelixPanelStore((s) => s.open);
  const setIsOpen = useFelixPanelStore((s) => s.setOpen);
  const felixWidth = useFelixPanelStore((s) => s.width);
  const setFelixWidth = useFelixPanelStore((s) => s.setWidth);
  // Phase 4 — gate message-reveal motion behind prefers-reduced-motion.
  const felixChatReducedMotion = useReducedMotion();
  // Panel-manifestation closed-X: push the panel fully off the left
  // edge of the viewport regardless of sidebar width / viewport size.
  // Tracked as state + resize listener so the spring animation knows
  // the exact off-screen distance on every breakpoint change.
  const [closedX, setClosedX] = useState(
    typeof window !== "undefined" ? -(window.innerWidth + 200) : -2200,
  );
  useEffect(() => {
    const compute = () => setClosedX(-(window.innerWidth + 200));
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);
  // Phase 4 — Modernize-inspired conversation history sidebar toggle.
  // Off by default so first-time users still see the familiar thread.
  const [conversationSidebarOpen, setConversationSidebarOpen] = useState(false);
  // Messages are loaded lazily — actual project scoping happens in useEffect below
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [felixError, setFelixError] = useState(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const chatAbortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  // KI-0120: anchor at the end of the message list — scrolling to this
  // is more reliable than querying the Radix ScrollArea viewport, which
  // may not be laid out yet when the panel reopens.
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Extract projectId early — needed for per-project session/storage scoping
  const { projectId } = useParams<{ projectId: string }>();
  // Row 2acb58cf: and the person, so the saved chat is never another user's.
  const chatUserId = useAuthStore((s) => s.user?.id);

  // Stable session ID for conversation history grouping — one per project per browser tab
  // Resets when projectId changes so each project gets its own conversation session.
  const chatSessionId = useMemo(() => {
    const KEY = projectId
      ? `felix_chat_session_id_${projectId}`
      : "felix_chat_session_id";
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
    return id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Server-side conversation session sync — debounced save
  const serverSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedCountRef = useRef(0);
  const serverLoadedRef = useRef(false);

  // Token usage display toggle — persisted in localStorage
  const [showTokenUsage, setShowTokenUsage] = useState(() => {
    try {
      return localStorage.getItem("felix_show_token_usage") !== "false";
    } catch {
      return true;
    }
  });

  // Quick reply buttons — shown when Felix asks binary/ternary questions
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);

  // Autonomous mode — skip confirmation, execute server-side
  const [autonomousMode, setAutonomousMode] = useState(() => {
    try {
      return localStorage.getItem("felix_autonomous_mode") === "true";
    } catch {
      return false;
    }
  });
  const [autonomousProgress, setAutonomousProgress] = useState<{
    active: boolean;
    completed: number;
    actions: { tool: string; detail: string; success: boolean }[];
  }>({ active: false, completed: 0, actions: [] });
  // Verified results — built from action_complete SSE events, NOT Claude's text.
  // This is the ground truth of what actually happened.
  const [verifiedResults, setVerifiedResults] = useState<{
    totalSucceeded: number;
    totalFailed: number;
    details: { tool: string; success: boolean; detail: string }[];
  } | null>(null);

  // Sketch mode — exploratory entity creation without validation gates
  const [sketchMode, setSketchMode] = useState(() => {
    try {
      return localStorage.getItem("felix_sketch_mode") === "true";
    } catch {
      return false;
    }
  });

  // Preview mode — show artifact previews before committing
  const [previewMode, setPreviewMode] = useState(false);
  const [previewArtifacts, setPreviewArtifacts] = useState<
    Array<{
      tool: string;
      result: FelixAutonomousActionComplete["result"];
      approved: boolean;
    }>
  >([]);
  const [commitPending, setCommitPending] = useState(false);

  // Personalized briefing state
  const [briefingData, setBriefingData] = useState<FelixBriefing | null>(null);
  const [briefingDismissed, setBriefingDismissed] = useState(() => {
    try { return sessionStorage.getItem("felix_briefing_dismissed") === "true"; }
    catch { return false; }
  });
  const [briefingLoading, setBriefingLoading] = useState(false);

  // Message ratings state — track which messages have been rated
  const [ratedMessages, setRatedMessages] = useState<Record<string, 'positive' | 'negative'>>({});
  // Track pending feedback explanation — after thumbs-down, Felix asks why and we capture the next user message
  const [pendingFeedbackRatingId, setPendingFeedbackRatingId] = useState<string | null>(null);

  // Persist autonomous mode preference
  useEffect(() => {
    try {
      localStorage.setItem("felix_autonomous_mode", String(autonomousMode));
    } catch { /* ignore */ }
  }, [autonomousMode]);

  // Persist sketch mode preference
  useEffect(() => {
    try {
      localStorage.setItem("felix_sketch_mode", String(sketchMode));
    } catch { /* ignore */ }
  }, [sketchMode]);

  // Track mounted state to prevent setState after unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Abort any in-flight stream on unmount
      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
        streamAbortRef.current = null;
      }
    };
  }, []);

  // Whose messages `messages` holds: the storage key they were loaded for. Set
  // only where messages are loaded for a person and project (row 5158db47). On
  // the render where the project changes, `messages` still holds the previous
  // project's thread; the persist effect below runs first and used to save it
  // under the new project's key, which the load then read back, so every
  // project's chat began with the last thread the person had open.
  const messagesOwnerRef = useRef(getProjectStorageKey(chatUserId, projectId));
  // The debounced server save still waiting, so a project switch flushes it.
  const pendingSaveRef = useRef<(() => void) | null>(null);

  // Persist chat messages to localStorage + debounced server save
  useEffect(() => {
    if (messagesOwnerRef.current !== getProjectStorageKey(chatUserId, projectId)) {
      // These messages belong to the person and project we are leaving. Save
      // what they had pending to their own session; write nothing for the new one.
      pendingSaveRef.current?.();
      return;
    }
    if (messages.length > 0) {
      try {
        const key = getProjectStorageKey(chatUserId, projectId);
        localStorage.setItem(key, JSON.stringify(messages));
      } catch { /* storage full — ignore */ }

      // Debounced server sync — save every 5 new messages or after 30s idle
      const msgDelta = messages.length - lastSavedCountRef.current;
      if (serverSaveTimerRef.current) clearTimeout(serverSaveTimerRef.current);

      const doSave = () => {
        pendingSaveRef.current = null;
        lastSavedCountRef.current = messages.length;
        const serialized = messages.map((m) => ({
          role: m.role,
          content: m.content,
          actions: m.actions,
          token_usage: m.tokenUsage,
        }));
        conversationSessionsApi
          .save(chatSessionId, {
            project_id: projectId,
            messages: serialized,
            title: messages.find((m) => m.role === "user")?.content?.slice(0, 80),
          })
          .then(() => markChatSessionSaved(chatSessionId))
          .catch((err) => {
            console.warn("[FelixChat] Session save failed:", err?.message || err);
          });
      };

      if (msgDelta >= 5) {
        doSave();
      } else {
        pendingSaveRef.current = doSave;
        serverSaveTimerRef.current = setTimeout(doSave, 30_000);
      }
    }

    return () => {
      if (serverSaveTimerRef.current) clearTimeout(serverSaveTimerRef.current);
    };
  }, [messages, projectId, chatSessionId, chatUserId]);

  // Load persisted messages when project changes — each project gets its own chat
  const prevProjectRef = useRef<string | undefined>(projectId);
  const prevChatUserRef = useRef<string | undefined>(chatUserId);
  useEffect(() => {
    if (prevProjectRef.current !== projectId || prevChatUserRef.current !== chatUserId) {
      prevProjectRef.current = projectId;
      prevChatUserRef.current = chatUserId;
      const loaded = loadPersistedMessages<Message>(chatUserId, projectId);
      messagesOwnerRef.current = getProjectStorageKey(chatUserId, projectId);
      lastSavedCountRef.current = loaded.length;
      setMessages(loaded);
      // Reset Q&A state for the new project
      setQaSessionId(null);
      setQaEntityTitle("");
      shownInteractionsRef.current.clear();
      // Reset briefing so it fetches for the new project
      setBriefingDismissed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, chatUserId]);

  // Initial message load on mount — localStorage first, then try server
  useEffect(() => {
    purgeLegacyChatHistory();
    const persisted = loadPersistedMessages<Message>(chatUserId, projectId);
    if (persisted.length > 0) {
      setMessages(persisted);
      lastSavedCountRef.current = persisted.length;
    }

    // Try loading from server if localStorage was empty, and only for a
    // session this tab has saved: asking for one it never saved is a certain
    // 404 (row bc96f3f0; see lib/felixChatSession).
    if (persisted.length === 0 && !serverLoadedRef.current && chatSessionWasSaved(chatSessionId)) {
      serverLoadedRef.current = true;
      conversationSessionsApi
        .get(chatSessionId)
        .then((session) => {
          if (session?.messages?.length) {
            const restored: Message[] = session.messages.map((m, i) => ({
              id: `restored-${i}`,
              role: (m.role as "user" | "assistant") || "assistant",
              content: m.content || "",
              timestamp: m.created_at ? new Date(m.created_at) : new Date(),
              actions: m.actions as FelixAction[] | undefined,
              tokenUsage: m.token_usage as TokenUsage | undefined,
            }));
            setMessages(restored);
            lastSavedCountRef.current = restored.length;
          }
        })
        .catch(() => { /* 404 or network error — no server session, that's fine */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Q&A mode state
  const [qaSessionId, setQaSessionId] = useState<string | null>(null);
  const [qaEntityTitle, setQaEntityTitle] = useState<string>("");
  const shownInteractionsRef = useRef<Set<string>>(new Set());

  // Listen for QASessions page resume events
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.sessionId) {
        setQaSessionId(detail.sessionId);
        if (detail.entityType) setQaEntityTitle(`${detail.entityType} Q&A`);
        setIsOpen(true);
      }
    };
    window.addEventListener("felix:open-qa-session", handler);
    return () => window.removeEventListener("felix:open-qa-session", handler);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // KI-0156: custom react-markdown renderer — intercepts internal links so
  // clicking "[View modules](/projects/…/modules)" in Felix's text clears the
  // stale React Query cache (same logic as the navigate action handler) before
  // doing SPA navigation.  External links open in a new tab as before.
  const markdownComponents = useMemo(() => ({
    a: ({ href, children, node: _node, ...rest }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) => {
      if (href && href.startsWith("/")) {
        return (
          <a
            {...rest}
            href={href}
            onClick={(e) => {
              e.preventDefault();
              for (const [re, key] of _FELIX_PATH_QUERY_KEYS) {
                if (re.test(href)) queryClient.removeQueries({ queryKey: key });
              }
              navigate(href);
            }}
          >
            {children}
          </a>
        );
      }
      return (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
  }), [navigate, queryClient]);

  const felixChat = useFelixChat();
  const sidebarCollapsed = useSidebarStore((s) => s.collapsed);
  const pageContext = usePageContextStore();
  const pathPageName = getPageName(location.pathname);
  // ROW b1b55364. A page that has ONE record open reports itself, and its name
  // wins over the pathname — Documents.tsx and Meetings.tsx open their detail
  // panel in component state without touching the URL, so getPageName cannot
  // tell an open document from the list it sits in. Falling back to the
  // pathname keeps every other page exactly as it was.
  const pageName = pageContext.openEntity?.pageName || pathPageName;
  // Template-layer SKIN for the DISPLAY-ONLY header breadcrumb. `pageName`
  // (above) stays canonical for every backend payload; this relabels only the
  // "Program Manager · <page>" line to the project's manifest vocabulary. The
  // canonical page name is passed as the fallback so a non-template project's
  // header is byte-identical to today.
  const { label: skinPageLabel } = useTemplateLabels(projectId ?? null);
  const displayPageName = (() => {
    // THE BREADCRUMB USES THE PATH NAME, not the record-aware one (row
    // b1b55364). "DocumentDetail" and "MeetingDetail" are identifiers the
    // BACKEND keys its triggers on; showing them to a user would put a
    // camel-case internal name in the header where "Documents" used to be.
    // The skin is display-only by construction, so this cannot affect any
    // payload.
    const canonical = PAGE_NAME_TO_CANONICAL[pathPageName];
    if (!canonical || !projectId) return pathPageName;
    return skinPageLabel(canonical, { plural: true, fallback: pathPageName });
  })();
  // Follow Mode (Wave 10C) — mirrors the toggle in the autonomous
  // activity indicator so users can flip it from either location.
  const followMode = useAutonomousRunStore((s) => s.followMode);
  const setFollowMode = useAutonomousRunStore((s) => s.setFollowMode);

  // Fetch personalized briefing when chat opens or project context changes
  const briefingProjectRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isOpen || briefingDismissed || briefingLoading) return;
    // Skip if we already have data for this exact project
    if (briefingData && briefingProjectRef.current === projectId) return;
    let cancelled = false;
    setBriefingLoading(true);
    briefingProjectRef.current = projectId;
    assistantApi.getBriefing(projectId).then((data) => {
      if (!cancelled && mountedRef.current) {
        setBriefingData(data);
      }
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("Briefing fetch failed:", err);
    }).finally(() => {
      if (!cancelled && mountedRef.current) setBriefingLoading(false);
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  // Dismiss briefing when user sends first message
  const dismissBriefing = useCallback(() => {
    setBriefingDismissed(true);
    try { sessionStorage.setItem("felix_briefing_dismissed", "true"); }
    catch { /* ignore */ }
  }, []);

  // Handle nudge dismissal — optimistic UI update, log API failures
  const handleNudgeDismiss = useCallback((nudgeType: string) => {
    // Optimistically remove from UI immediately
    setBriefingData((prev) =>
      prev ? { ...prev, nudges: prev.nudges.filter((n) => n.type !== nudgeType) } : prev
    );
    // Persist dismissal server-side (non-blocking, log failures)
    assistantApi.dismissNudge(nudgeType).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("Failed to persist nudge dismissal:", err);
    });
  }, []);

  // Commit approved preview artifacts to the database
  const handleCommitPreviews = useCallback(async () => {
    const approved = previewArtifacts.filter((a) => a.approved && a.result.success);
    if (approved.length === 0) return;

    setCommitPending(true);
    try {
      const previews = approved.map((a) => a.result);
      const res = await assistantApi.commitPreviews({
        project_id: projectId,
        previews,
      });

      const { succeeded, failed } = res.summary;
      const resultMsg: Message = {
        id: `felix-commit-${Date.now()}`,
        role: "assistant",
        content: `**Committed ${succeeded} artifact${succeeded !== 1 ? "s" : ""}**${failed > 0 ? ` (${failed} failed)` : ""}.\n\n${res.results
          .map((r) =>
            r.success
              ? `- Created ${r.detail ?? r.entity_id ?? "entity"}`
              : `- Failed: ${r.error ?? r.detail ?? "unknown error"}`
          )
          .join("\n")}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, resultMsg]);

      // Invalidate caches since data was written
      if (succeeded > 0) {
        for (const key of ["projects", "objectives", "userStories", "userPersonas", "components", "tasks", "testCases", "meetings", "baselines", "changeRequests"]) {
          queryClient.invalidateQueries({ queryKey: [key] });
        }
      }
    } catch (err) {
      const errorMsg: Message = {
        id: `felix-commit-err-${Date.now()}`,
        role: "assistant",
        content: `Failed to commit previews: ${err instanceof Error ? err.message : "Unknown error"}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setCommitPending(false);
      setPreviewArtifacts([]);
    }
  }, [previewArtifacts, projectId, queryClient]);

  const handleDiscardPreviews = useCallback(() => {
    setPreviewArtifacts([]);
    const discardMsg: Message = {
      id: `felix-discard-${Date.now()}`,
      role: "assistant",
      content: "Previews discarded. No changes were made.",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, discardMsg]);
  }, []);

  // Poll for proactive Felix push messages (background notifications).
  // ALWAYS poll — even when the panel is closed — so background task
  // progress updates are never lost.  Poll every 5s for timely feedback.
  const pendingMessages = useQuery({
    queryKey: ["felix", "pending-messages"],
    queryFn: () => assistantApi.pendingMessages(),
    refetchInterval: 5_000,
  });

  // Row fad9bff7: approval cards Felix proposed outside this chat (in a
  // meeting, or an earlier session) that still wait for the person. Shown
  // above the input; a card already in the transcript is not shown twice.
  // Row a0fe2825 (FB-0071): on a page with no project (Home) the person's
  // cards on every project, so Home is never left without its Approve.
  const pendingProposals = useQuery({
    queryKey: ["felix", "proposals", projectId ?? null],
    queryFn: () => felixProposalsApi.pending(projectId ?? null),
    enabled: isOpen,
    refetchInterval: 30_000,
  });
  const waitingProposals = useMemo(() => {
    const inTranscript = new Set(messages.flatMap((m) => (m.proposals ?? []).map((p) => p.id)));
    return (pendingProposals.data?.proposals ?? []).filter((p) => !inTranscript.has(p.id));
  }, [pendingProposals.data, messages]);

  // Row 053c7759: while Presenting, internal alerts and money figures in the
  // feed wait here and are shown when Presenting is turned off.
  const presenting = usePresenting((st) => st.presenting);
  // Read through a ref by the injector below, so toggling Presenting never
  // re-runs it (that would inject the same pushes twice).
  const presentingRef = useRef(presenting);
  presentingRef.current = presenting;
  const heldWhilePresenting = useRef<Message[]>([]);
  useEffect(() => {
    if (presenting || heldWhilePresenting.current.length === 0) return;
    const held = heldWhilePresenting.current;
    heldWhilePresenting.current = [];
    setMessages((prev) => [...prev, ...held]);
  }, [presenting]);

  // Inject push messages into the chat when they arrive
  useEffect(() => {
    const msgs = pendingMessages.data?.messages;
    if (!msgs || msgs.length === 0) return;

    const newMessages: Message[] = [];
    for (let idx = 0; idx < msgs.length; idx++) {
      const pm = msgs[idx];

      // Handle document analysis completion — show summary
      if (pm.type === "document_analysis_complete") {
        const counts = pm.entity_counts || {};
        const countEntries = Object.entries(counts)
          .filter(([, v]) => (v as number) > 0)
          .map(([k, v]) => `**${k}:** ${v}`)
          .join(" · ");
        const msg =
          `Document analysis complete! Extracted **${pm.total_extracted || 0}** entities from your **${pm.document_type || "document"}**.\n\n` +
          (countEntries ? `${countEntries}\n\n` : "") +
          `**Auto-created:** ${pm.auto_created || 0}\n` +
          (pm.needs_followup ? `**Needs review:** ${pm.needs_followup}\n` : "") +
          (pm.blocking_conflicts ? `**Conflicts:** ${pm.blocking_conflicts}\n` : "");
        newMessages.push({
          id: `push-doc-${Date.now()}-${idx}`,
          role: "assistant",
          content: msg,
          timestamp: new Date(pm.timestamp),
        });
        continue;
      }

      // Handle document analysis failure
      if (pm.type === "document_analysis_failed") {
        newMessages.push({
          id: `push-doc-fail-${Date.now()}-${idx}`,
          role: "assistant",
          content: `Document analysis failed: ${pm.error || "Unknown error"}. Please try uploading again or use a smaller document.`,
          timestamp: new Date(pm.timestamp),
        });
        continue;
      }

      // Handle Q&A entity review — show Approve/Reject prompt
      if (pm.type === "qa_review_entity" && pm.entity_summary) {
        const summary = pm.entity_summary;
        const reviewContent =
          `Q&A is complete for **${summary.title}**. Here's what we captured:\n\n` +
          `**Type:** ${pm.entity_type}\n` +
          `**Priority:** ${summary.priority || "N/A"}\n` +
          (summary.story_type ? `**Story Type:** ${summary.story_type}\n` : "") +
          `**Description:** ${summary.description || "N/A"}\n\n` +
          `Does this capture everything correctly?`;
        newMessages.push({
          id: `push-review-${Date.now()}-${idx}`,
          role: "assistant",
          content: reviewContent,
          timestamp: new Date(pm.timestamp),
          // Store review metadata for Approve/Reject buttons
          metadata: {
            type: "qa_review",
            session_id: pm.session_id,
            entity_type: pm.entity_type,
            entity_id: pm.entity_id,
            entity_title: summary.title,
          },
        });
        continue;
      }

      const pushed: Message = {
        id: `push-${Date.now()}-${idx}`,
        role: "assistant",
        content: pm.message,
        timestamp: new Date(pm.timestamp),
      };
      if (presentingRef.current && isInternalFeedItem(pm.message)) {
        heldWhilePresenting.current.push(pushed);
        continue;
      }
      newMessages.push(pushed);
    }

    if (newMessages.length > 0) {
      setMessages((prev) => [...prev, ...newMessages]);
    }
  }, [pendingMessages.data]);

  // Q&A hooks — only active when in QA mode
  const qaSession = useQASession(qaSessionId ?? "");
  const submitQAAnswer = useSubmitQAAnswer();

  // Voice input/output — Deepgram STT (preferred) with browser Speech API fallback
  const deepgram = useDeepgramSTT();
  const speech = useSpeechRecognition();
  const synthesis = useSpeechSynthesis();
  const toast = useToast();

  // T1 (row 7f612cac): a write the person explicitly directed runs without a
  // card (their direction is the approval). If it cannot be undone, Felix
  // ADVISES rather than asks -- the server sends that advice on the action.
  const adviseBreakRisk = useCallback(
    (actions: FelixAction[]) => {
      for (const a of actions) {
        if (a.break_risk_warning) toast.warning("Heads up", a.break_risk_warning);
      }
    },
    [toast],
  );
  // Use Deepgram when the server has it configured; fall back to browser Speech API
  const useDeepgramMode = deepgram.available && deepgram.browserSupported;
  const [autoSpeak, setAutoSpeak] = useState(() => {
    try {
      return sessionStorage.getItem("felix_auto_speak") === "true";
    } catch {
      return false;
    }
  });

  // Persist auto-speak preference
  useEffect(() => {
    try {
      sessionStorage.setItem("felix_auto_speak", String(autoSpeak));
    } catch { /* ignore */ }
  }, [autoSpeak]);

  // When speech recognition produces a transcript, APPEND it to the
  // input field rather than replace. James filed FB-0005 "STT not
  // functional" — part of the problem is that previously any text the
  // user had typed was wiped when the mic fired, making the feature
  // feel broken. We also no longer pollute the input with the
  // "Recording... Xs" interim timer — that shows elsewhere via
  // micStatusHint below.
  const lastTranscriptRef = useRef("");
  const inputBeforeMicRef = useRef("");
  useEffect(() => {
    if (useDeepgramMode) {
      if (deepgram.transcript && deepgram.transcript !== lastTranscriptRef.current) {
        lastTranscriptRef.current = deepgram.transcript;
        const prior = inputBeforeMicRef.current;
        const joiner = prior && !prior.endsWith(" ") ? " " : "";
        setInput(prior + joiner + deepgram.transcript);
      }
    } else if (speech.transcript && speech.transcript !== lastTranscriptRef.current) {
      lastTranscriptRef.current = speech.transcript;
      const prior = inputBeforeMicRef.current;
      const joiner = prior && !prior.endsWith(" ") ? " " : "";
      setInput(prior + joiner + speech.transcript);
    }
  }, [speech.transcript, deepgram.transcript, useDeepgramMode]);

  // Snapshot the input contents right as recording begins so we can
  // append the transcript to it instead of overwriting.
  useEffect(() => {
    if (deepgram.recording || speech.listening) {
      inputBeforeMicRef.current = input;
    }
    // Intentionally no reset on stop — we keep the snapshot until the
    // next start so a slow transcription still appends correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepgram.recording, speech.listening]);

  // Visible status hint while mic is active — shown beside the input
  // rather than polluting it.
  const micStatusHint: string = useDeepgramMode
    ? (deepgram.transcribing
        ? "Transcribing…"
        : (deepgram.recording ? deepgram.interimText || "Listening…" : ""))
    : (speech.listening ? "Listening…" : "");

  // Auto-speak new assistant messages.
  // #564 — the old impl spoke whenever messages.length grew past the
  // baseline, so LOADING an existing conversation (0 → N messages) auto-read
  // that old chat's last message aloud, unprompted. Fix: (1) sync the
  // baseline silently on the first run and on any BULK load (jump > 1), and
  // (2) only speak a SINGLE newly-arrived assistant reply.
  const lastMessageCountRef = useRef(messages.length);
  const autoSpeakInitializedRef = useRef(false);
  useEffect(() => {
    if (!autoSpeak || !synthesis.supported) {
      lastMessageCountRef.current = messages.length;
      return;
    }
    if (!autoSpeakInitializedRef.current) {
      // First time auto-speak is active — adopt the current history as the
      // baseline without reading any of it aloud.
      autoSpeakInitializedRef.current = true;
      lastMessageCountRef.current = messages.length;
      return;
    }
    // Exactly one new message → a genuine live reply (not a history/bulk load).
    if (messages.length === lastMessageCountRef.current + 1) {
      const newest = messages[messages.length - 1];
      if (newest.role === "assistant" && newest.content) {
        synthesis.speak(newest.content);
      }
    }
    lastMessageCountRef.current = messages.length;
  }, [messages, autoSpeak, synthesis]);

  // Left-edge resize support — drag to widen/narrow the panel
  const { onResizeMouseDown } = usePanelResize(setFelixWidth);

  // Popout mode — render in a detached browser window
  const { isPopout, popoutContainer, openPopout, closePopout } = usePopout();

  // On mobile, close panel on route change (user navigated away)
  const location2 = useLocation();
  useEffect(() => {
    // Only auto-close on small screens (mobile overlay mode)
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  }, [location2.pathname, setIsOpen]);

  // Auto-scroll to bottom on new messages or when panel opens.
  // Uses requestAnimationFrame + staggered timeouts to handle:
  // (1) Radix ScrollArea mount timing, (2) panel slide-in transition (300ms),
  // (3) content re-render after server message load, (4) long conversation DOM paint.
  useEffect(() => {
    if (!isOpen) return;
    const doScroll = () => {
      // KI-0120: prefer scrollIntoView on the bottom anchor — this works
      // even before the Radix viewport DOM is queryable and is what makes
      // "reopen chat → latest message visible" reliable.
      try {
        bottomAnchorRef.current?.scrollIntoView({ block: "end" });
      } catch {
        /* older browsers: fall through to the querySelector path below */
      }

      // Belt-and-suspenders: also set scrollTop on the viewport if we
      // can find it. Covers edge cases where the anchor was unmounted.
      const panel = scrollRef.current?.closest("[data-felix-panel]") ?? document.querySelector("[data-felix-panel]");
      const scrollEl =
        panel?.querySelector("[data-radix-scroll-area-viewport]") as HTMLElement | null
        ?? scrollRef.current?.closest("[data-radix-scroll-area-viewport]") as HTMLElement | null
        ?? scrollRef.current?.parentElement;
      if (scrollEl) {
        scrollEl.scrollTop = scrollEl.scrollHeight;
      }
    };
    // Use requestAnimationFrame for first paint, then stagger for transitions
    requestAnimationFrame(doScroll);
    const id1 = setTimeout(doScroll, 100);
    const id2 = setTimeout(doScroll, 350);  // After 300ms slide-in transition
    const id3 = setTimeout(doScroll, 700);  // After server messages load + render
    const id4 = setTimeout(doScroll, 1200); // Final catch-all for very long conversations
    return () => { clearTimeout(id1); clearTimeout(id2); clearTimeout(id3); clearTimeout(id4); };
  }, [messages, isOpen]);

  // Focus input when opened; restore focus to the previous trigger
  // when closed. WCAG 2.4.3: closing a modal-like surface MUST
  // return focus to the element that opened it, otherwise keyboard
  // users land on document.body and have to re-navigate.
  // Empirical anchor: UAT iter test_closing_felix_restores_focus_to_trigger
  // failed because closing the panel left focus on document.body.
  const previouslyFocusedRef = useRef<Element | null>(null);
  const panelRootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      // Capture whatever was focused right before we opened so we
      // can restore it when the panel closes.
      previouslyFocusedRef.current = document.activeElement;
      // MOVE FOCUS INTO THE PANEL IMMEDIATELY, then to the input on the
      // existing timer. Escape is handled by the panel root's onKeyDown,
      // which is a React handler and therefore only sees keystrokes from
      // INSIDE the panel subtree. Until this line, nothing was inside it for
      // the first ~200ms: focus was still on the topbar pill the user had
      // just clicked, so an Escape pressed promptly after opening did
      // nothing at all. Measured in the 2026-08-25 nightly —
      // test_closing_felix_restores_focus_to_trigger presses Escape as soon
      // as the close button is visible and the panel stayed open, while
      // test_escape_closes_felix_panel, which takes a screenshot first and
      // so arrives late, passed. A race, not a coin flip: the user who
      // reaches for Escape quickest is the one it fails.
      panelRootRef.current?.focus({ preventScroll: true });
      if (inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 200);
      }
    } else {
      // Restore focus to the trigger. The trigger may have aria-pressed
      // toggled or been re-rendered; use a small timeout to avoid
      // racing the panel's close-animation.
      const target = previouslyFocusedRef.current as HTMLElement | null;
      if (target && typeof target.focus === "function") {
        setTimeout(() => target.focus(), 100);
      }
    }
  }, [isOpen]);

  // -------------------------------------------------------------------
  // Q&A mode — present new questions as Felix messages
  // -------------------------------------------------------------------

  useEffect(() => {
    if (!qaSessionId || !qaSession.data) return;

    const { session, interactions } = qaSession.data;

    // Check for new unanswered questions
    for (const interaction of interactions) {
      if (
        interaction.status === "ACTIVE" &&
        !interaction.answer &&
        !shownInteractionsRef.current.has(interaction.id)
      ) {
        shownInteractionsRef.current.add(interaction.id);
        setMessages((prev) => [
          ...prev,
          {
            id: `qa-q-${interaction.id}`,
            role: "assistant",
            content: interaction.question,
            timestamp: new Date(),
            dimension: interaction.dimension ?? undefined,
          },
        ]);
      }
    }

    // Check if session completed
    if (session.status === "COMPLETED" || session.status === "FAILED") {
      const coverage = computeCoverage(session.coverage_scores);
      const completionMsg =
        session.status === "COMPLETED"
          ? `Scope discovery is complete! We've reached ${coverage}% coverage. You can now review the extracted user stories on the User Stories page.`
          : "The Q&A session encountered an issue and couldn't continue. You can try starting a new session.";

      setMessages((prev) => [
        ...prev,
        {
          id: `qa-done-${Date.now()}`,
          role: "assistant",
          content: completionMsg,
          timestamp: new Date(),
        },
      ]);

      setQaSessionId(null);
      setQaEntityTitle("");
      shownInteractionsRef.current.clear();
    }
  }, [qaSessionId, qaSession.data]);

  // -------------------------------------------------------------------
  // Write memory entries after successful action chain
  // -------------------------------------------------------------------

  const writeActionMemory = useCallback(
    async (actions: FelixAction[], results: ActionResult[]) => {
      const pid = results.find((r) => r.data?.project_id)?.data?.project_id as string | undefined;
      const targetProjectId = pid || projectId;
      if (!targetProjectId) return;

      const entries = results
        .filter((r) => r.status === "success")
        .map((r) => {
          switch (r.action.type) {
            case "create_project":
              return {
                entry_type: "milestone",
                summary: `Project created: ${r.data?.name || r.action.params.name}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_objective":
              return {
                entry_type: "milestone",
                summary: `Objective created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "confirm_outcome_record":
              // OUT-02: the confirmed definition of done is a milestone —
              // future sessions should know the outcome is locked in.
              return {
                entry_type: "milestone",
                summary: "Outcome Record confirmed (definition of done locked in)",
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_test_case":
              return {
                entry_type: "milestone",
                summary: `Test case created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_user_story":
              return {
                entry_type: "milestone",
                summary: `User story created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_user_persona":
              return {
                entry_type: "milestone",
                summary: `Persona created: ${r.data?.name || r.action.params.name}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_module":
              return {
                entry_type: "milestone",
                summary: `Module created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_component":
              return {
                entry_type: "milestone",
                summary: `Component created: ${r.data?.name || r.action.params.name}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_component_specification":
              return {
                entry_type: "milestone",
                summary: `Specification created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "create_task":
              return {
                entry_type: "milestone",
                summary: `Task created: ${r.data?.title || r.action.params.title}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_project":
              return {
                entry_type: "milestone",
                summary: `Project updated: ${r.data?.name || r.action.params.name || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_objective":
              return {
                entry_type: "milestone",
                summary: `Objective updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_module":
              return {
                entry_type: "milestone",
                summary: `Module updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_component":
              return {
                entry_type: "milestone",
                summary: `Component updated: ${r.data?.name || r.action.params.name || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_component_specification":
              return {
                entry_type: "milestone",
                summary: `Specification updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_user_story":
              return {
                entry_type: "milestone",
                summary: `User story updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_user_persona":
              return {
                entry_type: "milestone",
                summary: `Persona updated: ${r.data?.name || r.action.params.name || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_task":
              return {
                entry_type: "milestone",
                summary: `Task updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "update_test_case":
              return {
                entry_type: "milestone",
                summary: `Test case updated: ${r.data?.title || r.action.params.title || r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "start_qa":
              return {
                entry_type: "milestone",
                summary: "Q&A session started for scope discovery",
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            case "delete_objective":
            case "delete_user_story":
            case "delete_user_persona":
            case "delete_module":
            case "delete_component":
            case "delete_task":
            case "delete_test_case":
            case "delete_meeting": {
              const entityLabel = r.action.type.replace("delete_", "").replace("_", " ");
              return {
                entry_type: "milestone",
                summary: `${entityLabel} deleted: ${r.action.params.id}`,
                source_type: "felix_chat",
                source_label: "Felix action",
              };
            }
            default:
              return null;
          }
        })
        .filter((e): e is NonNullable<typeof e> => e !== null);

      if (entries.length > 0) {
        try {
          await memoryApi.addEntries(targetProjectId, entries);
        } catch {
          // Memory write is best-effort
        }
      }
    },
    [projectId],
  );

  // -------------------------------------------------------------------
  // Diagnostics gatherer — captures everything Felix can see for tickets
  // -------------------------------------------------------------------

  const gatherDiagnostics = useCallback((): FeedbackDiagnostics => {
    // 1. Full error timeline (not just the trigger — ALL recent errors)
    const errorTimeline = _recentErrors
      .filter((e) => Date.now() - e.timestamp < 15 * 60 * 1000) // last 15 min
      .map((e) => ({ message: e.message, timestamp: e.timestamp, source: e.source }));

    // 2. Browser & environment snapshot
    const environment = {
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    // 3. Q&A state (if in scope discovery mode)
    let qaState: FeedbackDiagnostics["qa_state"] = null;
    if (qaSessionId && qaSession.data) {
      qaState = {
        session_id: qaSessionId,
        entity_title: qaEntityTitle,
        coverage_percent: computeCoverage(qaSession.data.session.coverage_scores),
        round: qaSession.data.interactions?.filter((i: QAInteraction) => !!i.answer).length ?? 0,
      };
    }

    // 4. Entity context — what page/entity the user was on
    const entityContext = {
      entity_type: pageContext.openEntity?.type ?? "page",
      entity_id: pageContext.openEntity?.id ?? (projectId || "none"),
      page: pageName,
      page_path: location.pathname + location.search,
    };

    return {
      error_timeline: errorTimeline.length > 0 ? errorTimeline : undefined,
      environment,
      qa_state: qaState,
      entity_context: entityContext,
    };
    // location.search is a dependency because page_path above now carries it
    // (row #3170: the server reads ?story=<id> to know which record the user
    // has open). Without it, moving from ?story=A to ?story=B — same pathname —
    // would keep the old memo and report the previous record.
  }, [qaSessionId, qaSession.data, qaEntityTitle, projectId, pageName,
      pageContext.openEntity?.type, pageContext.openEntity?.id,
      location.pathname, location.search]);

  // -------------------------------------------------------------------
  // Error-to-ticket helper — auto-offer to log an action error as a ticket
  // -------------------------------------------------------------------

  const offerErrorTicket = useCallback(
    (errorDetail: string, actionType: string) => {
      // Silently capture ALL recent errors for the ticket — the user
      // should NOT see raw error details, only a friendly message.
      const allErrors = _recentErrors
        .filter((e) => Date.now() - e.timestamp < 15 * 60 * 1000)
        .map((e) => `[${e.source}] ${e.message}`);
      const errorMessages = [errorDetail, ...allErrors.filter((e) => !e.includes(errorDetail))];

      // The ticket itself failed (FB-0105): offering a ticket about it only files the failure as
      // the person's own feedback. Say so plainly; the technical detail stays out of the chat.
      if (actionType === "log_feedback") {
        setMessages((prev) => [
          ...supersedePendingCards(prev),
          {
            id: `error-offer-${Date.now()}`,
            role: "assistant",
            content: "I couldn't save that ticket, so it hasn't been logged. Try again, or tell me what to change.",
            timestamp: new Date(),
          },
        ]);
        return;
      }

      // Friendly user-facing message — no technical internals
      const friendlyAction = actionType
        .replace(/_/g, " ")
        .replace(/^(create |start |log )/, "$1a ");

      setMessages((prev) => [
        ...supersedePendingCards(prev),
        {
          id: `error-offer-${Date.now()}`,
          role: "assistant",
          content:
            `I ran into an issue while trying to ${friendlyAction}. ` +
            `Would you like me to log this for the team to investigate?`,
          timestamp: new Date(),
          actions: [
            {
              type: "log_feedback",
              params: {
                // Technical detail goes in the ticket, not the chat
                title: `Felix action failed: ${actionType}`,
                description:
                  `Felix attempted to execute a "${actionType}" action but encountered an error:\n\n` +
                  `${errorDetail}\n\n` +
                  `Page: ${pageName}\nPath: ${location.pathname}\n` +
                  (qaSessionId ? `Q&A session active: ${qaEntityTitle}\n` : ""),
                page_name: pageName,
                page_path: location.pathname + location.search,
                category: "AI_PERSONA",
                severity: "MEDIUM",
                error_messages: errorMessages,
              },
              label: "Log this for the team",
            },
          ],
          pendingConfirmation: true,
        },
      ]);
    },
    // location.search: see the note on gatherDiagnostics — page_path carries it.
    [pageName, location.pathname, location.search, qaSessionId, qaEntityTitle],
  );

  // ─────────────────────────────────────────────────────────────────
  // Baseline-lock detector (FB-0024 through FB-0051 root cause).
  //
  // When Felix tries to update/delete a baselined entity, the API
  // correctly returns HTTP 423 with a JSON body indicating the
  // entity is locked by a baseline. Before this helper existed,
  // these legitimate refusals were treated as generic errors and
  // auto-filed as "Felix action failed" feedback tickets — 15+ noise
  // tickets in two days.
  //
  // Real signal: the API is working correctly. The user needs to
  // create a change request to modify the entity. This helper lets
  // Felix recognize that signal and offer the CR path instead of
  // filing a ticket.
  //
  // Returns the baseline info if the error matches the lock pattern,
  // else null (fall back to normal error handling).
  // ─────────────────────────────────────────────────────────────────
  type BaselineLockInfo = {
    baseline_id: string;
    baseline_name: string;
    baseline_code: string;
    message: string;
  };

  const detectBaselineLock = useCallback(
    (err: unknown): BaselineLockInfo | null => {
      // Accept either an ApiRequestError (shape: {status, detail,
      // errors}) or a generic Error with a JSON string in .message.
      // Both paths are observed depending on which API method the
      // action executor called.
      let status: number | undefined;
      let detailStr: string | undefined;
      if (err instanceof ApiRequestError) {
        status = err.status;
        detailStr = err.detail;
      } else if (err instanceof Error) {
        // Generic Error.message sometimes looks like:
        //   "update_task failed: <status> <detail-json>"
        // Look for "423" + "locked":true literal.
        if (err.message.includes('"locked":true') && err.message.includes("423")) {
          status = 423;
          // Best-effort extract of JSON body
          const match = err.message.match(/\{.*"locked":true.*\}/);
          if (match) detailStr = match[0];
        }
      }
      if (status !== 423 || !detailStr) return null;
      try {
        // detailStr may be a JSON-stringified dict OR the raw dict
        // depending on ApiRequestError construction. Try both.
        const parsed = typeof detailStr === "string" && detailStr.startsWith("{")
          ? JSON.parse(detailStr)
          : null;
        if (!parsed || parsed.locked !== true) return null;
        return {
          baseline_id: String(parsed.baseline_id || ""),
          baseline_name: String(parsed.baseline_name || ""),
          baseline_code: String(parsed.baseline_code || ""),
          message: String(parsed.message || ""),
        };
      } catch {
        return null;
      }
    },
    [],
  );

  // ─────────────────────────────────────────────────────────────────
  // Phase-gate detector (FB-0031 root cause).
  //
  // When Felix tries to create an entity that's ahead of the current
  // SDLC phase (e.g., create_module while project is in 'objectives'
  // phase), work_focus_service returns HTTP 400 with a specific
  // human-readable message starting "Cannot create <type> —
  // currently in the '<phase>' phase (phase N of 8)...".
  //
  // Same pattern as the baseline-lock bug: legitimate API refusal,
  // treated as a generic error, auto-filed as a noise ticket. Fix:
  // detect the pattern and surface it as an informational Felix
  // message to the USER ("you're in the objectives phase; finish
  // those first") — no ticket.
  // ─────────────────────────────────────────────────────────────────
  const detectPhaseGate = useCallback(
    (err: unknown): string | null => {
      let detail: string | undefined;
      if (err instanceof ApiRequestError && err.status === 400) {
        detail = err.detail;
      } else if (err instanceof Error && err.message.includes("Cannot create")) {
        detail = err.message;
      }
      if (!detail) return null;
      // Exact server-side shape: see work_focus_service.py:213-219
      const match = detail.match(
        /Cannot create \w+ — currently in the '(\w+)' phase/,
      );
      if (!match) return null;
      // Return the full human-readable message; server already phrased
      // it well (names the current phase + what to complete).
      return detail;
    },
    [],
  );

  const explainPhaseGate = useCallback(
    (action: FelixAction, humanMessage: string) => {
      setMessages((prev) => [
        ...prev,
        {
          id: `phase-gate-${Date.now()}`,
          role: "assistant",
          content:
            `I can't ${action.type.replace(/_/g, " ")} yet — the project ` +
            `isn't at that phase. Here's what the server said:\n\n` +
            `> ${humanMessage}\n\n` +
            `Once you're done with the current phase we can move on.`,
          timestamp: new Date(),
        },
      ]);
    },
    [],
  );

  // FB-0137: a create_task (or other entity) that the server's field-
  // completeness gate refuses with a 409 ({error_code, field,
  // recovery_guidance, g_domain:"field_completeness"}) is a legitimate,
  // actionable refusal — the entity is missing a required parent/field (e.g.
  // an orphan task with no component/story). Detect it BEFORE offering an
  // error ticket so the user gets the server's guidance, not "log this error?".
  // A plan-limit 402 is a BILLING decision, not a defect. It was reaching
  // offerErrorTicket, so Felix offered to file a bug ticket against the product
  // working exactly as sold — noise in the feedback queue, and it tells a paying
  // customer something is broken when what they need is the upgrade page.
  const detectPlanLimit = useCallback((err: unknown) => planLimitInfo(err), []);

  const explainPlanLimit = useCallback(
    (action: FelixAction, info: PlanLimitRefusal) => {
      const counts =
        info.current !== undefined && info.limit !== undefined
          ? ` You're at ${info.current} of ${info.limit}.`
          : "";
      setMessages((prev) => [
        ...prev,
        {
          id: `plan-limit-${Date.now()}`,
          role: "assistant",
          content:
            `I can't ${action.type.replace(/_/g, " ")} — your plan's limit is ` +
            `reached, so this isn't something I can retry.${counts}\n\n` +
            `> ${info.message}\n\n` +
            `You can raise the limit on the Billing page, or free up room by ` +
            `archiving a project you've finished with.`,
          timestamp: new Date(),
        },
      ]);
    },
    [],
  );

  const detectFieldCompletenessGate = useCallback(
    (err: unknown) => fieldCompletenessGateInfo(err),
    [],
  );

  const explainFieldCompletenessGate = useCallback(
    (action: FelixAction, info: { field?: string; guidance?: string }) => {
      const guidance =
        info.guidance ||
        "It's missing something it needs first — for a task, a component or user story to belong to.";
      setMessages((prev) => [
        ...prev,
        {
          id: `field-gate-${Date.now()}`,
          role: "assistant",
          content:
            `I couldn't ${action.type.replace(/_/g, " ")} — it needs a bit more first.\n\n` +
            `> ${guidance}\n\n` +
            `Tell me which one it should belong to (or open the item you want it under) and I'll create it there.`,
          timestamp: new Date(),
        },
      ]);
    },
    [],
  );

  // When we detect a baseline lock, surface it as a Felix message
  // that OFFERS a change request instead of filing a noise ticket.
  // The user clicks a button → Felix creates the CR with the right
  // target_entity_type and target_entity_id pre-filled.
  const offerChangeRequest = useCallback(
    (action: FelixAction, lockInfo: BaselineLockInfo) => {
      // FB-0016: handle create_ too (baselined projects return 423 on
      // create_* actions), not just update/delete. For creates there
      // is no existing target_entity_id yet.
      const isCreate = action.type.startsWith("create_");
      const entityLabel = action.type
        .replace(/^create_/, "")
        .replace(/^update_/, "")
        .replace(/^delete_/, "")
        .replace(/_/g, " ");
      const rawEntityType = action.type
        .replace(/^create_/, "")
        .replace(/^update_/, "")
        .replace(/^delete_/, "");
      // The task verbs (create_task/update_task/delete_task) yield "task", but
      // baseline_service keys on the canonical "impl_task". A "task" CR matches
      // none of the unlock/authorize paths, so the baselined-task lock never
      // clears and Felix re-fires offerChangeRequest forever. Canonicalize it.
      const targetEntityType = rawEntityType === "task" ? "impl_task" : rawEntityType;

      const verbPhrase = isCreate
        ? `add a new ${entityLabel}`
        : `modify this ${entityLabel}`;
      const titlePhrase = isCreate
        ? `Add new ${entityLabel}`
        : `Modify ${entityLabel}`;
      const descriptionPhrase = isCreate
        ? `Change request opened automatically by Felix to add a new ` +
          `${entityLabel} to a project locked by baseline ` +
          `${lockInfo.baseline_name} (${lockInfo.baseline_code}).`
        : `Change request opened automatically by Felix because the ` +
          `${entityLabel} is locked by baseline ` +
          `${lockInfo.baseline_name} (${lockInfo.baseline_code}).`;

      setMessages((prev) => [
        ...supersedePendingCards(prev),
        {
          id: `cr-offer-${Date.now()}`,
          role: "assistant",
          content:
            `I can't ${verbPhrase} directly — it's locked ` +
            `by **${lockInfo.baseline_name}** (${lockInfo.baseline_code}). ` +
            `Baselined scope requires a change request.\n\n` +
            `Want me to open a change request for you?`,
          timestamp: new Date(),
          actions: [
            {
              type: "create_change_request",
              params: {
                title: titlePhrase,
                description: descriptionPhrase,
                baseline_id: lockInfo.baseline_id,
                target_entity_type: targetEntityType,
                ...(isCreate ? {} : { target_entity_id: action.params.id }),
                change_type: isCreate ? "scope_addition" : "scope_change",
                priority: "MEDIUM",
              },
              label: `Open change request to ${verbPhrase}`,
            },
          ],
          pendingConfirmation: true,
        },
      ]);
    },
    [],
  );

  // -------------------------------------------------------------------
  // Action execution engine
  // -------------------------------------------------------------------

/**
 * Turn a tool identifier into something sayable to a user.
 *
 * `broadcast_create_campaign` -> "Broadcast create campaign". Crude, but it is
 * a last-resort label for a tool nobody wrote a case for, and a rough English
 * phrase beats a snake_case internal name in front of a customer. Anything that
 * matters carries a real `action.label` and never reaches this.
 */
function humaniseActionType(type: string): string {
  const words = type.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "That action";
}

  const executeActions = useCallback(
    async (actions: FelixAction[], msgId: string) => {
      if (actions.length === 0) return;

      setIsExecuting(true);

      // Initialize all action results as pending
      const results: ActionResult[] = actions.map((a) => ({
        action: a,
        status: "pending" as const,
      }));
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, actionResults: [...results] } : m))
      );

      // Chain context — results from earlier actions feed into later ones
      let chainedProjectId: string | undefined = projectId;
      let chainedObjectiveId: string | undefined;
      let chainedObjectiveTitle: string | undefined;
      // The kickoff (founder 6495841758): the project Felix just created, so the person lands
      // IN it and is offered its kickoff, rather than staying where they were.
      let createdProject: { id: string; name: string } | undefined;

      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        results[i] = { action, status: "running" };
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, actionResults: [...results] } : m))
        );

        try {
          switch (action.type) {
            case "create_project": {
              const proj = await projectsApi.create({
                name: action.params.name as string,
                description: (action.params.description as string) || undefined,
                project_type: (action.params.project_type as string) || undefined,
                scope_tier: (action.params.scope_tier as string) || undefined,
              } as any);
              chainedProjectId = proj.id;
              createdProject = { id: proj.id, name: proj.name };
              results[i] = {
                action,
                status: "success",
                detail: `Created "${proj.name}"`,
                data: { project_id: proj.id, name: proj.name },
              };
              break;
            }

            // ---- Broadcast ------------------------------------------
            // The API and its client were fully built (app/api/v1/broadcast.py,
            // 53 endpoints; frontend/src/api/broadcastApi.ts, 29 methods) and
            // only these wires were missing, so every request to Felix hit the
            // dispatcher fallback. broadcast_campaigns has 0 rows in production
            // because nothing could ever write one through him.
            case "broadcast_create_campaign": {
              // DRAFT only — the tool description is explicit that this never
              // dispatches. Sending stays a deliberate act on the Broadcast
              // screen, and wiring Felix up must not quietly change that.
              const campaign = await broadcastApi.createCampaign({
                name: action.params.name as string,
                project_id: chainedProjectId || projectId || null,
                subject: (action.params.subject as string) || null,
                body_template: (action.params.body_template as string) || null,
              });
              results[i] = {
                action,
                status: "success",
                detail: `Drafted campaign "${campaign.name}" — not sent`,
                data: { campaign_id: campaign.id, name: campaign.name },
              };
              break;
            }

            case "broadcast_list_campaigns": {
              const list = await broadcastApi.listCampaigns(
                (action.params.status as never) || undefined,
              );
              const n = list.items?.length ?? 0;
              results[i] = {
                action,
                status: "success",
                detail: n === 0 ? "No campaigns yet" : `${n} campaign(s)`,
                data: { campaigns: list.items ?? [] },
              };
              break;
            }

            case "broadcast_get_report": {
              const campaignId = action.params.campaign_id as string;
              if (!campaignId) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Which campaign? I need the campaign to report on.",
                };
                break;
              }
              const report = await broadcastApi.getReport(campaignId);
              results[i] = {
                action,
                status: "success",
                detail: "Report ready",
                data: { report },
              };
              break;
            }

            case "campaign_performance": {
              // Read-only org-wide analytics, so nothing to confirm. The tool
              // param is `since_days`; the client defaults to 30 when omitted,
              // which is the sensible reading of "how are campaigns doing".
              const sinceDays = Number(action.params.since_days) || 30;
              const analytics = await broadcastApi.getAnalytics(sinceDays);
              results[i] = {
                action,
                status: "success",
                detail: `Performance over the last ${sinceDays} days`,
                data: { analytics, since_days: sinceDays },
              };
              break;
            }

            case "list_campaign_templates": {
              const tpl = await broadcastApi.listTemplates(
                (action.params.category as string) || undefined,
              );
              results[i] = {
                action,
                status: "success",
                detail:
                  tpl.count === 0
                    ? "No campaign templates available"
                    : `${tpl.count} template(s)`,
                data: { templates: tpl.templates },
              };
              break;
            }

            case "lead_capture": {
              // CaptureLeadInput and the tool schema BOTH make every field
              // optional, so a lead with neither name nor email type-checks
              // cleanly and lands as a row nobody can ever contact. That is
              // worse than refusing — it reports success and looks captured.
              const contact = leadContact(action.params);
              if (!contact) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Who is the lead — a name or an email address?",
                };
                break;
              }
              const { name, email } = contact;
              const lead = await leadsApi.capture({
                source: (action.params.source as string) || "felix",
                name: name || null,
                email: email || null,
                company: (action.params.company as string) || null,
              });
              results[i] = {
                action,
                status: "success",
                detail: `Captured ${name || email}`,
                data: { lead },
              };
              break;
            }

            case "lead_metrics": {
              const metrics = await leadsApi.metrics();
              results[i] = {
                action,
                status: "success",
                detail:
                  metrics.total === 0
                    ? "No leads captured yet"
                    : `${metrics.total} lead(s), ${metrics.qualified_or_better} qualified or better`,
                data: { metrics },
              };
              break;
            }

            case "draft_social_post": {
              // createPost drafts unless scheduled_at is given — its own contract
              // is "draft (or schedule) a post" — which is exactly what the tool
              // promises: it does NOT publish. Publishing needs a connected
              // account and stays out of this path deliberately, so this cannot
              // post to a customer's real audience.
              //
              // The tool's enum and SocialPlatform agree on all four names, so no
              // mapping is needed; an unrecognised one is dropped rather than
              // forwarded, because the backend would reject the entire post over
              // a single bad entry.
              const postBody = String(action.params.body || "").trim();
              const platforms = normaliseSocialPlatforms(action.params.platforms);
              if (!postBody) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "What should the post say?",
                };
                break;
              }
              if (platforms.length === 0) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Which platform — LinkedIn, X, Facebook, or Instagram?",
                };
                break;
              }
              const scheduledAt = (action.params.scheduled_at as string) || null;
              const post = await socialApi.createPost({
                body: postBody,
                platforms,
                scheduled_at: scheduledAt,
              });
              results[i] = {
                action,
                status: "success",
                detail: scheduledAt
                  ? `Scheduled a post for ${platforms.join(", ")}`
                  : `Drafted a post for ${platforms.join(", ")}`,
                data: { post },
              };
              break;
            }

            case "campaign_from_template": {
              // The tool passes a template NAME; the client needs an ID
              // (createCampaignFromTemplate(templateId, ...)). So resolve the
              // name first — and REFUSE unless exactly one template matches.
              //
              // Guessing between two similarly named templates would silently
              // build the wrong campaign, which the user would only discover
              // after reading it. Naming the candidates back is more useful
              // than a coin flip.
              const wanted = String(action.params.template_name || "").trim();
              if (!wanted) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Which template should I start from?",
                };
                break;
              }
              const all = await broadcastApi.listTemplates();
              const needle = wanted.toLowerCase();
              let matches = (all.templates || []).filter(
                (t) => t.name.toLowerCase() === needle,
              );
              if (matches.length === 0) {
                // Fall back to a contains match, so a close-enough name still
                // works — but the ambiguity rule below still applies to it.
                matches = (all.templates || []).filter((t) =>
                  t.name.toLowerCase().includes(needle),
                );
              }
              if (matches.length === 0) {
                const names = (all.templates || []).map((t) => t.name).slice(0, 5);
                results[i] = {
                  action,
                  status: "error",
                  detail: names.length
                    ? `No template called "${wanted}". Available: ${names.join(", ")}`
                    : `No template called "${wanted}", and none are set up yet.`,
                };
                break;
              }
              if (matches.length > 1) {
                results[i] = {
                  action,
                  status: "error",
                  detail: `"${wanted}" matches ${matches.length}: ${matches
                    .map((t) => t.name)
                    .slice(0, 4)
                    .join(", ")}. Which one?`,
                };
                break;
              }
              const created = await broadcastApi.createCampaignFromTemplate(
                matches[0].id,
                {
                  name: (action.params.campaign_name as string) || undefined,
                  project_id: chainedProjectId || projectId || undefined,
                },
              );
              results[i] = {
                action,
                status: "success",
                detail: `Drafted "${created.name}" from ${matches[0].name} — not sent`,
                data: { campaign_id: created.id, template: matches[0].name },
              };
              break;
            }

            case "generate_content": {
              // Stateless: POST /broadcast/content/generate drafts copy variants
              // and saves nothing, so running it on request costs the user
              // nothing and commits them to nothing.
              //
              // The tool schema marks NOTHING required even though the client
              // needs kind AND topic. Calling with undefined would surface a
              // server validation error as if the product were broken, when the
              // real problem is that Felix never asked what to write about — so
              // both are checked here and the gap is turned into a question.
              const kind = String(action.params.kind || "");
              const topic = String(action.params.topic || "").trim();
              const VALID_KINDS = ["caption", "post_idea", "subject_line", "ad_copy"];
              if (!topic) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "What should the copy be about?",
                };
                break;
              }
              if (!VALID_KINDS.includes(kind)) {
                results[i] = {
                  action,
                  status: "error",
                  detail:
                    "What kind of copy — a caption, post idea, subject line, or ad copy?",
                };
                break;
              }
              const generated = await broadcastApi.generateContent({
                kind: kind as never,
                topic,
                tone: (action.params.tone as string) || null,
                channel: (action.params.channel as string) || null,
                count: (action.params.count as number) || undefined,
                // Ground Felix's draft in the project's ratified outcome, the same
                // way ContentStudio does. Felix is the primary interface, so
                // omitting this left the MOST-used path to marketing copy writing
                // from nothing but a typed topic -- exactly what P-10's
                // canon-aware requirement exists to prevent.
                project_id: projectId || null,
              });
              results[i] = {
                action,
                status: "success",
                detail: `Drafted ${kind.replace(/_/g, " ")} options for "${topic}"`,
                data: { generated },
              };
              break;
            }

            case "list_pending_approvals": {
              const pending = await broadcastApi.listPendingApprovals();
              results[i] = {
                action,
                status: "success",
                detail:
                  pending.count === 0
                    ? "Nothing waiting on approval"
                    : `${pending.count} awaiting approval`,
                data: { pending: pending.pending, count: pending.count },
              };
              break;
            }

            case "request_campaign_approval": {
              const cid = action.params.campaign_id as string;
              if (!cid) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Which campaign should go for approval?",
                };
                break;
              }
              const req = await broadcastApi.requestCampaignApproval(cid);
              results[i] = {
                action,
                status: "success",
                detail: `Sent for approval (${req.status})`,
                data: { campaign_id: cid, status: req.status },
              };
              break;
            }

            case "review_campaign_approval": {
              // The tool's enum is approve | reject, and the client splits
              // those into two methods. An unrecognised decision must NOT
              // silently pick one — approving something the user meant to
              // reject is the worst outcome available here.
              const cid = action.params.campaign_id as string;
              const decision = String(action.params.decision || "").toLowerCase();
              const note = (action.params.note as string) || undefined;
              if (!cid) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Which campaign am I reviewing?",
                };
                break;
              }
              if (decision !== "approve" && decision !== "reject") {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Approve or reject? I won't guess on an approval.",
                };
                break;
              }
              const res =
                decision === "approve"
                  ? await broadcastApi.approveCampaign(cid, note)
                  : await broadcastApi.rejectCampaign(cid, note);
              results[i] = {
                action,
                status: "success",
                detail: decision === "approve" ? "Approved" : "Rejected",
                data: { campaign_id: cid, status: res.status },
              };
              break;
            }

            case "create_objective": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const obj = await objectivesApi.create({
                project_id: chainedProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
              });
              chainedObjectiveId = obj.id;
              chainedObjectiveTitle = obj.title;
              results[i] = {
                action,
                status: "success",
                detail: `Created "${obj.title}"`,
                data: { objective_id: obj.id, title: obj.title },
              };
              break;
            }

            case "create_test_case": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const tc = await testCasesApi.create({
                project_id: chainedProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
                test_type: (action.params.test_type as string) || undefined,
                priority: (action.params.priority as string) || undefined,
                expected_result: (action.params.expected_result as string) || undefined,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                steps: ensureParsed(action.params.steps) as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                preconditions: ensureParsed(action.params.preconditions) as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                postconditions: ensureParsed(action.params.postconditions) as any,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                test_data: ensureParsed(action.params.test_data) as any,
                automation_status: (action.params.automation_status as string) || undefined,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                environment_requirements: ensureParsed(action.params.environment_requirements) as any,
                execution_time_limit: action.params.execution_time_limit as number | undefined,
                tags: ensureParsed(action.params.tags) as string[] | undefined,
                objective_id: validUuidOrUndef(action.params.objective_id),
                component_id: validUuidOrUndef(action.params.component_id),
                impl_task_id: validUuidOrUndef(action.params.impl_task_id),
              });
              results[i] = {
                action,
                status: "success",
                detail: `Created "${tc.title}"`,
                data: { test_case_id: tc.id, title: tc.title },
              };
              break;
            }

            case "create_user_story": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const story = (await userStoriesApi.create({
                project_id: chainedProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
                as_a: (action.params.as_a as string) || undefined,
                i_want: (action.params.i_want as string) || undefined,
                so_that: (action.params.so_that as string) || undefined,
                priority: (action.params.priority as string) || undefined,
                story_points: action.params.story_points != null ? Number(action.params.story_points) : undefined,
                acceptance_criteria: action.params.acceptance_criteria ? ensureParsed(action.params.acceptance_criteria) as any : undefined,
                objective_id: validUuidOrUndef(action.params.objective_id),
                capability_tag: (action.params.capability_tag as string) || undefined,
              })) as { id: string; title: string };
              results[i] = {
                action,
                status: "success",
                detail: `Created "${story.title}"`,
                data: { user_story_id: story.id, title: story.title },
              };
              break;
            }

            case "create_user_persona": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const personaBody: Record<string, unknown> = {
                project_id: chainedProjectId,
                name: action.params.name as string,
                role: (action.params.role as string) || undefined,
                description: (action.params.description as string) || undefined,
                avatar_color: (action.params.avatar_color as string) || undefined,
              };
              if (action.params.demographics) personaBody.demographics = ensureParsed(action.params.demographics);
              if (action.params.goals) personaBody.goals = ensureParsed(action.params.goals);
              if (action.params.pain_points) personaBody.pain_points = ensureParsed(action.params.pain_points);
              if (action.params.behaviors) personaBody.behaviors = ensureParsed(action.params.behaviors);
              if (action.params.needs) personaBody.needs = ensureParsed(action.params.needs);
              if (action.params.scenarios) personaBody.scenarios = ensureParsed(action.params.scenarios);
              const createdPersona = (await userPersonasApi.create(personaBody)) as { id: string; name: string };
              results[i] = {
                action,
                status: "success",
                detail: `Created "${createdPersona.name}"`,
                data: { user_persona_id: createdPersona.id, name: createdPersona.name },
              };
              break;
            }

            case "create_module": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const modPayload: Record<string, unknown> = {
                project_id: chainedProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
                module_type: (action.params.module_type as string) || undefined,
                priority: (action.params.priority as string) || undefined,
                delivery_notes: (action.params.delivery_notes as string) || undefined,
              };
              if (action.params.dependencies) modPayload.dependencies = ensureParsed(action.params.dependencies);
              if (action.params.acceptance_criteria) modPayload.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.metadata) modPayload.metadata = ensureParsed(action.params.metadata);
              const mod = await modulesApi.create(modPayload as Partial<import("@/api/types").Module>);
              results[i] = {
                action,
                status: "success",
                detail: `Created "${mod.title}"`,
                data: { module_id: mod.id, title: mod.title },
              };
              break;
            }

            case "create_component": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const compPayload: Record<string, unknown> = {
                project_id: chainedProjectId,
                name: action.params.name as string,
                description: (action.params.description as string) || undefined,
                comp_type: (action.params.comp_type as string) || undefined,
                purpose: (action.params.purpose as string) || undefined,
                module_id: validUuidOrUndef(action.params.module_id),
              };
              if (action.params.technology_stack) compPayload.technology_stack = ensureParsed(action.params.technology_stack);
              if (action.params.dependencies) compPayload.dependencies = ensureParsed(action.params.dependencies);
              if (action.params.structured_inputs) compPayload.structured_inputs = ensureParsed(action.params.structured_inputs);
              if (action.params.structured_outputs) compPayload.structured_outputs = ensureParsed(action.params.structured_outputs);
              if (action.params.integration_points) compPayload.integration_points = ensureParsed(action.params.integration_points);
              const comp = await componentsApi.create(compPayload as Partial<import("@/api/types").Component>);
              results[i] = {
                action,
                status: "success",
                detail: `Created "${comp.name}"`,
                data: { component_id: comp.id, name: comp.name },
              };
              break;
            }

            case "create_component_specification": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const specPayload: Record<string, unknown> = {
                project_id: chainedProjectId,
                title: action.params.title as string,
                component_id: validUuidOrUndef(action.params.component_id) as string,
                description: (action.params.description as string) || undefined,
                spec_type: (action.params.spec_type as string) || undefined,
                priority: (action.params.priority as string) || undefined,
                rationale: (action.params.rationale as string) || undefined,
              };
              if (action.params.acceptance_criteria) specPayload.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.metadata) specPayload.metadata = ensureParsed(action.params.metadata);
              const spec = await componentSpecificationsApi.create(specPayload as Partial<import("@/api/types").ComponentSpecification>);
              results[i] = {
                action,
                status: "success",
                detail: `Created "${spec.title}"`,
                data: { component_specification_id: spec.id, title: spec.title },
              };
              break;
            }

            case "create_task": {
              if (!chainedProjectId) {
                results[i] = { action, status: "error", detail: "No project context" };
                break;
              }
              const taskPayload: Record<string, unknown> = {
                project_id: chainedProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
                task_type: (action.params.task_type as string) || undefined,
                priority: (action.params.priority as string) || undefined,
                estimated_hours: action.params.estimated_hours != null ? Number(action.params.estimated_hours) : undefined,
                technical_notes: (action.params.technical_notes as string) || undefined,
              };
              if (action.params.acceptance_criteria) taskPayload.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.implementation_details) taskPayload.implementation_details = ensureParsed(action.params.implementation_details);
              if (action.params.definition_of_done) taskPayload.definition_of_done = ensureParsed(action.params.definition_of_done);
              if (action.params.test_strategy) taskPayload.test_strategy = ensureParsed(action.params.test_strategy);
              if (action.params.tags) taskPayload.tags = ensureParsed(action.params.tags);
              // KI-0117: forward assigned_to so Felix can assign a task on creation.
              if (action.params.assigned_to !== undefined) taskPayload.assigned_to = validUuidOrUndef(action.params.assigned_to);
              // Forward due_date so a commitment Felix accepts carries its date.
              // Truthiness, not `!== undefined`: an empty string is not a date
              // and would be rejected, failing the whole create for a field the
              // model merely left blank.
              if (action.params.due_date) taskPayload.due_date = action.params.due_date;
              // KI-0223: forward architectural home fields — backend rejects tasks without one.
              if (action.params.component_id) taskPayload.component_id = validUuidOrUndef(action.params.component_id);
              if (action.params.story_id) taskPayload.story_id = validUuidOrUndef(action.params.story_id);
              const task = await tasksApi.create(taskPayload as Partial<import("@/api/types").Task>);
              results[i] = {
                action,
                status: "success",
                detail: `Created "${task.title}"`,
                data: { task_id: task.id, title: task.title },
              };
              break;
            }

            case "start_qa": {
              const entityType = (action.params.entity_type as string) || "objective";
              const entityId =
                (action.params.entity_id as string) || chainedObjectiveId;
              const entityTitle =
                (action.params.entity_title as string) ||
                chainedObjectiveTitle ||
                "Scope Discovery";
              const pid = (action.params.project_id as string) || chainedProjectId;

              if (!entityId || !pid) {
                results[i] = { action, status: "error", detail: "Missing entity or project" };
                break;
              }

              // Start the Q&A workflow
              const qa = await objectivesApi.startQA(entityId);

              // Enter Q&A mode in Felix's chat
              setQaSessionId(qa.qa_session_id);
              setQaEntityTitle(entityTitle);
              shownInteractionsRef.current.clear();

              // Navigate to the relevant page
              navigate(`/projects/${pid}/objectives`);

              results[i] = {
                action,
                status: "success",
                detail: "Q&A session started — questions will appear below",
                data: { qa_session_id: qa.qa_session_id },
              };

              // Add a Felix message explaining Q&A mode
              setMessages((prev) => [
                ...prev,
                {
                  id: `qa-start-${Date.now()}`,
                  role: "assistant",
                  content:
                    `Great, I've started scope discovery for "${entityTitle}". ` +
                    `I'll be asking you a series of questions to understand what you need. ` +
                    `Just answer naturally — there are no wrong answers! ` +
                    `You can exit the Q&A at any time using the X button.`,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "sidebar.open_category": {
              // Phase 6 Patent I — Felix-driven nav surface state.
              // Delegated to FelixChat.sidebarOpenCategory.ts so the
              // integration test can pin the publisher path without
              // booting the full FelixChat component tree.
              const r = handleSidebarOpenCategoryAction({ action });
              results[i] = { action, status: r.status, detail: r.detail };
              break;
            }

            case "navigate": {
              let path = action.params.path as string;
              // Substitute project_id placeholder if present
              if (path.includes("{id}")) {
                if (chainedProjectId) {
                  path = path.replace("{id}", chainedProjectId);
                } else {
                  results[i] = {
                    action,
                    status: "error",
                    detail: "Cannot navigate — no project selected. Please navigate to a project first.",
                  };
                  break;
                }
              }
              // KI-0156: Drop stale caches so the destination page shows a
              // proper loading state rather than empty tiles from a prior visit.
              for (const [re, key] of _FELIX_PATH_QUERY_KEYS) {
                if (re.test(path)) {
                  queryClient.removeQueries({ queryKey: key });
                }
              }
              navigate(path);
              // The reply already names the page; a raw path with the project's id means nothing to the person (FB-0114).
              results[i] = { action, status: "success", detail: "Page opened" };
              break;
            }

            case "create_meeting": {
              const meetingProjectId = (action.params.project_id as string) || chainedProjectId;
              if (!meetingProjectId) {
                results[i] = { action, status: "error", detail: "No project context — please navigate to a project first" };
                break;
              }
              if (!action.params.scheduled_start) {
                results[i] = { action, status: "error", detail: "Meeting must have a scheduled start time" };
                break;
              }
              // Validate meeting_type — coerce invalid values to AD_HOC
              const VALID_MEETING_TYPES = new Set([
                "SPRINT_PLANNING", "SPRINT_REVIEW", "RETROSPECTIVE", "STANDUP",
                "REQUIREMENTS_REVIEW", "TECHNICAL_DISCUSSION", "DEMO", "SCOPE_REVIEW", "AD_HOC",
              ]);
              const rawType = (action.params.meeting_type as string) || "AD_HOC";
              const meetingType = VALID_MEETING_TYPES.has(rawType) ? rawType : "AD_HOC";

              const mtg = await meetingsApi.create({
                project_id: meetingProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || undefined,
                meeting_type: meetingType,
                platform: (action.params.platform as string) || "IN_PERSON",
                scheduled_start: action.params.scheduled_start as string,
                scheduled_end: (action.params.scheduled_end as string) || undefined,
                ...(action.params.felix_role ? { felix_role: action.params.felix_role as string } : {}),
                ...(action.params.felix_scope ? { felix_scope: action.params.felix_scope as string } : {}),
                ...(action.params.felix_communication_mode ? { felix_communication_mode: action.params.felix_communication_mode as string } : {}),
                ...(action.params.meeting_category ? { meeting_category: action.params.meeting_category as string } : {}),
              });
              results[i] = {
                action,
                status: "success",
                detail: `Scheduled "${mtg.title}"`,
                data: { meeting_id: mtg.id, title: mtg.title },
              };
              break;
            }

            case "create_sprint": {
              const sprintProjectId = (action.params.project_id as string) || chainedProjectId;
              if (!sprintProjectId) {
                results[i] = { action, status: "error", detail: "No project context — please navigate to a project first" };
                break;
              }
              const newSprint = await sprintsApi.create({
                project_id: sprintProjectId,
                name: action.params.name as string,
                goal: (action.params.goal as string) || undefined,
                start_date: action.params.start_date as string,
                end_date: action.params.end_date as string,
                capacity_points: action.params.capacity_points != null ? Number(action.params.capacity_points) : undefined,
              });
              results[i] = {
                action,
                status: "success",
                detail: `Created sprint "${newSprint.name}"`,
                data: { sprint_id: newSprint.id, name: newSprint.name },
              };
              break;
            }

            case "add_sprint_item": {
              const itemResult = await sprintsApi.addItem(
                action.params.sprint_id as string,
                {
                  entity_type: action.params.entity_type as string,
                  entity_id: action.params.entity_id as string,
                  story_points: action.params.story_points != null ? Number(action.params.story_points) : undefined,
                },
              );
              results[i] = {
                action,
                status: "success",
                detail: `Added ${action.params.entity_type} to sprint`,
                data: itemResult as unknown as Record<string, unknown>,
              };
              break;
            }

            case "join_meeting": {
              const joinResult = await meetingBotApi.joinMeeting(action.params.meeting_id as string);
              queryClient.invalidateQueries({ queryKey: ["meeting-bot-status"] });
              results[i] = {
                action,
                status: "success",
                detail: `Felix joined the meeting (${joinResult.bot_status})`,
                data: { session_id: joinResult.id, bot_status: joinResult.bot_status },
              };
              break;
            }

            case "leave_meeting": {
              const leaveResult = await meetingBotApi.leaveMeeting(action.params.meeting_id as string);
              queryClient.invalidateQueries({ queryKey: ["meeting-bot-status"] });
              results[i] = {
                action,
                status: "success",
                detail: `Felix left the meeting`,
                data: { id: leaveResult.id, bot_status: leaveResult.bot_status },
              };
              break;
            }

            case "create_change_request": {
              const crProjectId = chainedProjectId;
              if (!crProjectId) {
                results[i] = { action, status: "error", detail: "No project context — please navigate to a project first" };
                break;
              }
              // FB-0114: carry the target entity through to the API.
              // baseline_service unlocks a locked entity only when an
              // IMPLEMENTING change request matches it by
              // (target_entity_type, target_entity_id); dropping these
              // here left every CR with NULL targets and the approved-CR
              // unlock never fired. offerChangeRequest() and Felix's
              // create_change_request tool both supply them.
              const crPayload = {
                project_id: crProjectId,
                title: action.params.title as string,
                description: (action.params.description as string) || "",
                justification: (action.params.justification as string) || "",
                priority: (action.params.priority as string) || "MEDIUM",
                target_entity_type: action.params.target_entity_type as
                  | string
                  | undefined,
                target_entity_id: action.params.target_entity_id as
                  | string
                  | undefined,
                // Row d709e931: the date the person said it is needed by
                // (stored as a change-order answer for its assessment).
                needed_by: action.params.needed_by as string | undefined,
                // Row d709e931 fix 2: Felix wrote these fields, not the person.
                drafted_by: "felix" as const,
              };
              const cr = await changeRequestsApi.create(crPayload);
              results[i] = {
                action,
                status: "success",
                detail: `Created change request "${cr.title || crPayload.title}"`,
                data: { change_request_id: cr.id, title: cr.title || crPayload.title },
              };
              break;
            }

            case "create_defect": {
              // Reached only from the card raised when the org's autonomy level
              // WITHHELD create_defect in autonomous mode (the frontend_action
              // branch below). Chat and stream file defects server-side and
              // never send this action type to the browser.
              const built = defectPayload(action.params, chainedProjectId);
              if ("refusal" in built) {
                results[i] = { action, status: "error", detail: built.refusal };
                break;
              }
              try {
                const defect = await defectsApi.create(built.payload);
                const code = defect.code ? `${defect.code} ` : "";
                results[i] = {
                  action,
                  status: "success",
                  detail: `Filed defect ${code}"${defect.title || built.payload.title}"`,
                  data: { defect_id: defect.id, code: defect.code, title: defect.title },
                };
              } catch (defectErr) {
                // A 4xx here is the server refusing the INPUT (an unmeasured
                // severity, a link to a story that does not exist) with its
                // own sentence — not a bug to offer a ticket for. Only a plain
                // string detail is shown; a structured validation body is
                // replaced with a sentence rather than printed.
                if (
                  defectErr instanceof ApiRequestError &&
                  defectErr.status >= 400 &&
                  defectErr.status < 500
                ) {
                  results[i] = {
                    action,
                    status: "error",
                    detail:
                      typeof defectErr.data === "string"
                        ? `That defect wasn't filed: ${defectErr.data}`
                        : "That defect wasn't filed: some of its details were not accepted.",
                  };
                  break;
                }
                throw defectErr;
              }
              break;
            }

            case "confirm_outcome_record": {
              // FLX-09 one-tap: the confirmation card's "Go ahead" IS the
              // consent gesture — this executor call flips DRAFT→CONFIRMED
              // server-side. Never a form (OUT-02).
              const orProjectId =
                (action.params.project_id as string) || chainedProjectId;
              if (!orProjectId) {
                results[i] = { action, status: "error", detail: "No project context — please navigate to a project first" };
                break;
              }
              const rec = await outcomeRecordApi.confirm(orProjectId);
              results[i] = {
                action,
                status: "success",
                detail: `Outcome Record confirmed: "${rec.outcome_statement}"`,
                data: { outcome_record_id: rec.id, status: rec.status },
              };
              break;
            }

            case "create_baseline": {
              const blProjectId = chainedProjectId;
              if (!blProjectId) {
                results[i] = { action, status: "error", detail: "No project context — please navigate to a project first" };
                break;
              }
              const blPayload = {
                project_id: blProjectId,
                name: action.params.name as string,
                description: (action.params.description as string) || undefined,
                include_types: (action.params.include_types as string[]) || undefined,
              };
              const bl = await baselinesApi.create(blPayload);
              results[i] = {
                action,
                status: "success",
                detail: `Created baseline "${bl.name}" — ${bl.item_count ?? 0} entities locked`,
                data: { baseline_id: bl.id, name: bl.name, item_count: bl.item_count },
              };
              break;
            }

            case "log_feedback": {
              // Pre-flight validation — title and description are required
              const fbTitle = (action.params.title as string)?.trim();
              const fbDescription = (action.params.description as string)?.trim();
              if (!fbTitle || !fbDescription) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Missing required feedback fields (title and description)",
                };
                break;
              }

              // Validate error_messages is an array if provided
              if (
                action.params.error_messages !== undefined &&
                !Array.isArray(action.params.error_messages)
              ) {
                action.params.error_messages = [String(action.params.error_messages)];
              }

              // Capture the conversation history so the dev team can see
              // the troubleshooting that led to this ticket
              const conversationSnapshot = messages.slice(-10).map((m) => ({
                role: m.role,
                content: m.content,
              }));

              // Gather full diagnostics — error timeline, browser, Q&A state, etc.
              const diagnostics = gatherDiagnostics();

              const ticket = await feedbackApi.create({
                project_id: projectId || undefined,
                title: fbTitle,
                description: fbDescription,
                page_name: (action.params.page_name as string) || pageName,
                page_path: (action.params.page_path as string) || location.pathname + location.search,
                steps_to_reproduce: action.params.steps_to_reproduce as string | undefined,
                expected_behavior: action.params.expected_behavior as string | undefined,
                actual_behavior: action.params.actual_behavior as string | undefined,
                error_messages: action.params.error_messages as string[] | undefined,
                category: action.params.category as string | undefined,
                severity: action.params.severity as string | undefined,
                felix_conversation: conversationSnapshot,
                diagnostics,
              });

              let detail = `Ticket ${ticket.code} logged`;
              if (ticket.is_known_issue && ticket.known_issue_code) {
                detail += ` (linked to known issue ${ticket.known_issue_code})`;
              }

              results[i] = {
                action,
                status: "success",
                detail,
                data: { ticket_id: ticket.id, code: ticket.code, is_known_issue: ticket.is_known_issue },
              };

              // Add a follow-up message from Felix about the ticket
              const followUp = ticket.is_known_issue
                ? `Got it — I've linked your report to known issue **${ticket.known_issue_code}**, which our team is already tracking. Your ticket code is **${ticket.code}**. You'll be notified as soon as there's an update.`
                : `Your feedback has been logged as **${ticket.code}**. The development team has been notified and you'll receive updates as the status changes.`;

              setMessages((prev) => [
                ...prev,
                {
                  id: `feedback-confirm-${Date.now()}`,
                  role: "assistant" as const,
                  content: followUp,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "update_feedback": {
              const ticketCode = (action.params.ticket_code as string)?.trim();
              if (!ticketCode) {
                results[i] = {
                  action,
                  status: "error",
                  detail: "Missing required field: ticket_code",
                };
                break;
              }
              if (!ticketCode.startsWith("FB-")) {
                results[i] = {
                  action,
                  status: "error",
                  detail: `Invalid ticket code "${ticketCode}" — must be a feedback ticket (FB-xxxx), not a known issue (KI-xxxx)`,
                };
                break;
              }

              // Find the ticket by code
              const existingTicket = await feedbackApi.findByCode(ticketCode);
              if (!existingTicket) {
                results[i] = {
                  action,
                  status: "error",
                  detail: `Could not find ticket ${ticketCode} in your submitted tickets`,
                };
                break;
              }

              // Validate error_messages is an array if provided
              let errorMsgs = action.params.error_messages as string[] | undefined;
              if (errorMsgs !== undefined && !Array.isArray(errorMsgs)) {
                errorMsgs = [String(errorMsgs)];
              }

              const updated = await feedbackApi.addInfo(existingTicket.id, {
                additional_notes: action.params.additional_notes as string | undefined,
                steps_to_reproduce: action.params.steps_to_reproduce as string | undefined,
                error_messages: errorMsgs,
                severity: action.params.severity as string | undefined,
              });

              results[i] = {
                action,
                status: "success",
                detail: `Updated ticket ${ticketCode}`,
                data: { ticket_id: updated.id, code: ticketCode },
              };

              setMessages((prev) => [
                ...prev,
                {
                  id: `feedback-update-${Date.now()}`,
                  role: "assistant" as const,
                  content: `I've added the new information to ticket **${ticketCode}**. The development team will see the updates.`,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "dev_status": {
              const devStatus = await devBridgeApi.getStatus();
              const parts: string[] = [];
              if (devStatus.recent_changes) {
                parts.push(`**Recent Changes:**\n${devStatus.recent_changes}`);
              }
              if (devStatus.dev_context) {
                parts.push(`**Dev Context:** ${JSON.stringify(devStatus.dev_context)}`);
              }
              const detail = parts.length > 0
                ? parts.join("\n\n")
                : "No development status available.";

              results[i] = { action, status: "success", detail };

              // Show the dev status as a Felix message
              setMessages((prev) => [
                ...prev,
                {
                  id: `dev-status-${Date.now()}`,
                  role: "assistant" as const,
                  content: detail,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "list_executions": {
              const statusFilter = (action.params.status_filter as string) || "ALL";
              const limit = (action.params.limit as number) || 10;
              const dashboard = await supervisorApi.getDashboard();
              let execs = dashboard.executions || [];

              if (statusFilter !== "ALL") {
                execs = execs.filter((e) => e.status === statusFilter);
              }
              execs = execs.slice(0, limit);

              const execLines = execs.map((e) => {
                const cost = e.token_usage ? `$${e.token_usage.total_cost_usd.toFixed(4)}` : "—";
                const dur = e.duration_ms ? `${(e.duration_ms / 1000).toFixed(1)}s` : "—";
                return `| \`${e.execution_id.slice(0, 8)}\` | ${e.status} | ${e.current_turn || 0} | ${cost} | ${dur} | ${e.branch || "—"} |`;
              });

              const summary = [
                `**Agent Executions** (${statusFilter === "ALL" ? "all" : statusFilter.toLowerCase()}, showing ${execs.length})`,
                "",
                `| ID | Status | Turns | Cost | Duration | Branch |`,
                `|-----|--------|-------|------|----------|--------|`,
                ...execLines,
                "",
                `**Summary:** ${dashboard.active_executions} active, ${dashboard.completed_executions} completed, ${dashboard.failed_executions} failed — Total cost: $${dashboard.total_cost_usd.toFixed(4)}`,
              ].join("\n");

              results[i] = { action, status: "success", detail: summary };
              setMessages((prev) => [
                ...prev,
                {
                  id: `list-exec-${Date.now()}`,
                  role: "assistant" as const,
                  content: summary,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "check_execution": {
              const execId = action.params.execution_id as string;
              const [execData, events, guidanceHist] = await Promise.all([
                supervisorApi.getExecution(execId),
                supervisorApi.getEvents(execId, 0, "live"),
                supervisorApi.getGuidanceHistory(execId),
              ]);

              const statusEmoji: Record<string, string> = {
                RUNNING: "running",
                COMPLETED: "completed",
                FAILED: "failed",
                PENDING: "pending",
              };
              const status = execData.status || "UNKNOWN";
              const tokenUsage = execData.token_usage;
              const recentEvents = events.slice(-5);
              const totalTokens = tokenUsage
                ? (tokenUsage.input_tokens || 0) + (tokenUsage.output_tokens || 0)
                : 0;

              const diagParts = [
                `**Execution ${execId}** — ${statusEmoji[status] || ""} ${status}`,
                "",
                `| Metric | Value |`,
                `|--------|-------|`,
                `| Turn | ${execData.current_turn ?? "—"} |`,
                `| Duration | ${execData.duration_ms ? `${(execData.duration_ms / 1000).toFixed(1)}s` : "—"} |`,
                `| Paused | ${execData.paused ? "Yes" : "No"} |`,
                `| Files Modified | ${(execData.modified_files || []).length} |`,
                `| Files Generated | ${(execData.generated_files || []).length} |`,
                tokenUsage ? `| Cost | $${tokenUsage.total_cost_usd?.toFixed(4) ?? "—"} |` : null,
                tokenUsage ? `| Tokens | ${totalTokens.toLocaleString()} |` : null,
                tokenUsage ? `| Model | ${tokenUsage.model || "—"} |` : null,
              ].filter(Boolean).join("\n");

              // Cost trend analysis
              let costTrend = "";
              if (tokenUsage && execData.current_turn && execData.current_turn > 0) {
                const costPerTurn = tokenUsage.total_cost_usd / execData.current_turn;
                const tokensPerTurn = Math.round(totalTokens / execData.current_turn);
                costTrend = [
                  "",
                  "**Cost Trend:**",
                  `- Cost per turn: $${costPerTurn.toFixed(4)}`,
                  `- Tokens per turn: ${tokensPerTurn.toLocaleString()}`,
                  totalTokens > 100000 ? "- High token usage detected" : "",
                ].filter(Boolean).join("\n");
              }

              // Guidance history summary
              let guidanceSection = "";
              if (guidanceHist.length > 0) {
                const guidanceLines = guidanceHist.slice(0, 5).map(
                  (g) =>
                    `- **${g.guidance_type}** (${g.status}) — ${(g.message || "").slice(0, 80)}${(g.message || "").length > 80 ? "..." : ""}`
                );
                guidanceSection = `\n\n**Guidance History (${guidanceHist.length} total):**\n${guidanceLines.join("\n")}`;
              }

              let eventSummary = "";
              if (recentEvents.length > 0) {
                const eventLines = recentEvents.map(
                  (e) =>
                    `- **${e.event_type}**${e.turn_number != null ? ` (turn ${e.turn_number})` : ""}: ${JSON.stringify(e.data).slice(0, 120)}`
                );
                eventSummary = `\n\n**Recent Events:**\n${eventLines.join("\n")}`;
              }

              const qualitySection = execData.quality
                ? `\n\n**Quality:** Score ${execData.quality.quality_score}/100 — ${execData.quality.assessment}`
                : "";

              const errorSection = execData.error
                ? `\n\n**Error:** ${execData.error}`
                : "";

              // Recommendations
              const recommendations: string[] = [];
              if (status === "RUNNING" && execData.paused) {
                recommendations.push("Agent is paused. Use `send_guidance` with RESUME to continue.");
              }
              if (status === "RUNNING" && totalTokens > 200000) {
                recommendations.push("Token usage is very high. Consider setting budget guardrails.");
              }
              if (status === "FAILED" && execData.error) {
                recommendations.push("Execution failed. Review the error and consider retrying with adjusted guidance.");
              }
              if (guidanceHist.length > 0 && guidanceHist.some((g) => g.status === "EXPIRED")) {
                recommendations.push("Some guidance messages expired (agent may have been unreachable).");
              }
              const recsSection = recommendations.length > 0
                ? `\n\n**Recommendations:**\n${recommendations.map(r => `- ${r}`).join("\n")}`
                : "";

              const fullDiag = diagParts + costTrend + guidanceSection + eventSummary + qualitySection + errorSection + recsSection;

              results[i] = { action, status: "success", detail: fullDiag };

              setMessages((prev) => [
                ...prev,
                {
                  id: `exec-check-${Date.now()}`,
                  role: "assistant" as const,
                  content: fullDiag,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "send_guidance": {
              const guidExecId = action.params.execution_id as string;
              const guidMessage = action.params.message as string;
              const guidType = (action.params.guidance_type as string) || "DIRECTION";

              const guidResult = await supervisorApi.sendGuidance(guidExecId, guidMessage, guidType);

              results[i] = {
                action,
                status: "success",
                detail: `Guidance sent: ${guidResult.status || "queued"}`,
              };

              setMessages((prev) => [
                ...prev,
                {
                  id: `guidance-sent-${Date.now()}`,
                  role: "assistant" as const,
                  content: [
                    `**Guidance Sent** to execution \`${guidExecId}\``,
                    "",
                    `Type: ${guidType}`,
                    `Message: ${guidMessage}`,
                    `Status: ${guidResult.status || "queued"}`,
                  ].join("\n"),
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "respond_to_agent": {
              const respondExecId = action.params.execution_id as string;
              const respondText = action.params.response as string;

              const respondResult = await supervisorApi.respondToAgent(
                respondExecId,
                respondText,
              );

              results[i] = {
                action,
                status: "success",
                detail: `Response sent: ${respondResult.status || "responded"}`,
              };

              setMessages((prev) => [
                ...prev,
                {
                  id: `agent-respond-${Date.now()}`,
                  role: "assistant" as const,
                  content: [
                    `**Response Sent** to agent \`${respondExecId}\``,
                    "",
                    `> ${respondText}`,
                    "",
                    "The agent will incorporate this answer and continue working.",
                  ].join("\n"),
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "retry_execution": {
              const retryExecId = action.params.execution_id as string;
              const retryFeedback = action.params.feedback as string;

              const retryResult = await supervisorApi.retryExecution(
                retryExecId,
                retryFeedback,
              );

              results[i] = {
                action,
                status: "success",
                detail: `Retry queued: ${(retryResult as Record<string, unknown>).execution_id || "new execution"}`,
              };

              setMessages((prev) => [
                ...prev,
                {
                  id: `retry-exec-${Date.now()}`,
                  role: "assistant" as const,
                  content: [
                    `**Execution Retry Started**`,
                    "",
                    `Original: \`${retryExecId}\``,
                    `New Execution: \`${(retryResult as Record<string, unknown>).execution_id || "pending"}\``,
                    `Attempt: ${(retryResult as Record<string, unknown>).attempt || "?"}`,
                    "",
                    "**Feedback injected:**",
                    `> ${retryFeedback.slice(0, 200)}${retryFeedback.length > 200 ? "..." : ""}`,
                    "",
                    "The agent will use this feedback to avoid previous mistakes.",
                  ].join("\n"),
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "trigger_development": {
              const triggerResult = await pipelineApi.triggerDevelopment({
                task_description: action.params.task_description as string,
                target_files: (ensureParsed(action.params.target_files) as string[]) || [],
                acceptance_criteria: (ensureParsed(action.params.acceptance_criteria) as string[]) || [],
                feedback_ticket: (action.params.feedback_ticket as string) || null,
                priority: (action.params.priority as string) || "HIGH",
              });

              results[i] = {
                action,
                status: "success",
                detail: triggerResult.message,
              };

              // Show development status as a Felix message
              const devMsg = [
                "**Development Started**",
                "",
                `Task: ${action.params.task_description}`,
                triggerResult.feedback_ticket
                  ? `Ticket: ${triggerResult.feedback_ticket}`
                  : "",
                "",
                `Workflow: \`${triggerResult.workflow_run_id}\``,
                "",
                "The AI agent is now working on this. I'll keep you updated on progress. " +
                "A pull request will be created when the work is ready for review.",
              ].filter(Boolean).join("\n");

              setMessages((prev) => [
                ...prev,
                {
                  id: `dev-trigger-${Date.now()}`,
                  role: "assistant" as const,
                  content: devMsg,
                  timestamp: new Date(),
                },
              ]);
              break;
            }

            case "update_project": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const projBody: Record<string, unknown> = {};
              if (action.params.name !== undefined) projBody.name = action.params.name;
              if (action.params.description !== undefined) projBody.description = action.params.description;
              if (action.params.project_type !== undefined) projBody.project_type = action.params.project_type;
              if (action.params.scope_tier !== undefined) projBody.scope_tier = action.params.scope_tier;
              const updatedProj = await projectsApi.update(updateId, projBody as Partial<import("@/api/types").Project>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated project "${updatedProj.name}"`,
                data: { project_id: updatedProj.id, name: updatedProj.name },
              };
              break;
            }

            case "update_module": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const modBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) modBody.title = action.params.title;
              if (action.params.description !== undefined) modBody.description = action.params.description;
              if (action.params.module_type !== undefined) modBody.module_type = action.params.module_type;
              if (action.params.priority !== undefined) modBody.priority = action.params.priority;
              if (action.params.delivery_notes !== undefined) modBody.delivery_notes = action.params.delivery_notes;
              if (action.params.dependencies !== undefined) modBody.dependencies = ensureParsed(action.params.dependencies);
              if (action.params.acceptance_criteria !== undefined) modBody.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.metadata !== undefined) modBody.metadata = ensureParsed(action.params.metadata);
              const updatedMod = await modulesApi.update(updateId, modBody as Partial<import("@/api/types").Module>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated module "${updatedMod.title}"`,
                data: { module_id: updatedMod.id, title: updatedMod.title },
              };
              break;
            }

            case "update_component": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const compBody: Record<string, unknown> = {};
              if (action.params.name !== undefined) compBody.name = action.params.name;
              if (action.params.description !== undefined) compBody.description = action.params.description;
              if (action.params.comp_type !== undefined) compBody.comp_type = action.params.comp_type;
              if (action.params.purpose !== undefined) compBody.purpose = action.params.purpose;
              if (action.params.module_id !== undefined) compBody.module_id = validUuidOrUndef(action.params.module_id) ?? null;
              if (action.params.technology_stack !== undefined) compBody.technology_stack = ensureParsed(action.params.technology_stack);
              if (action.params.dependencies !== undefined) compBody.dependencies = ensureParsed(action.params.dependencies);
              if (action.params.structured_inputs !== undefined) compBody.structured_inputs = ensureParsed(action.params.structured_inputs);
              if (action.params.structured_outputs !== undefined) compBody.structured_outputs = ensureParsed(action.params.structured_outputs);
              if (action.params.integration_points !== undefined) compBody.integration_points = ensureParsed(action.params.integration_points);
              const updatedComp = await componentsApi.update(updateId, compBody as Partial<import("@/api/types").Component>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated component "${updatedComp.name}"`,
                data: { component_id: updatedComp.id, name: updatedComp.name },
              };
              break;
            }

            case "update_component_specification": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const specBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) specBody.title = action.params.title;
              if (action.params.description !== undefined) specBody.description = action.params.description;
              if (action.params.spec_type !== undefined) specBody.spec_type = action.params.spec_type;
              if (action.params.priority !== undefined) specBody.priority = action.params.priority;
              if (action.params.component_id !== undefined) specBody.component_id = validUuidOrUndef(action.params.component_id) ?? null;
              if (action.params.rationale !== undefined) specBody.rationale = action.params.rationale;
              if (action.params.acceptance_criteria !== undefined) specBody.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.metadata !== undefined) specBody.metadata = ensureParsed(action.params.metadata);
              const updatedSpec = await componentSpecificationsApi.update(updateId, specBody as Partial<import("@/api/types").ComponentSpecification>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated specification "${updatedSpec.title}"`,
                data: { component_specification_id: updatedSpec.id, title: updatedSpec.title },
              };
              break;
            }

            case "update_objective": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const objBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) objBody.title = action.params.title;
              if (action.params.description !== undefined) objBody.description = action.params.description;
              if (action.params.success_metric !== undefined) objBody.success_metric = action.params.success_metric;
              if (action.params.metrics !== undefined) objBody.metrics = action.params.metrics;
              if (action.params.priority !== undefined) objBody.priority = action.params.priority;
              if (action.params.target_date !== undefined) objBody.target_date = action.params.target_date;
              if (Object.keys(objBody).length === 0) {
                results[i] = { action, status: "error", detail: "No fields to update — update_objective requires at least one changed field." };
                break;
              }
              const updatedObj = await objectivesApi.update(updateId, objBody as Partial<import("@/api/types").Objective>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated objective "${updatedObj.title}"`,
                data: { objective_id: updatedObj.id, title: updatedObj.title },
              };
              break;
            }

            case "update_user_story": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const storyBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) storyBody.title = action.params.title;
              if (action.params.description !== undefined) storyBody.description = action.params.description;
              if (action.params.as_a !== undefined) storyBody.as_a = action.params.as_a;
              if (action.params.i_want !== undefined) storyBody.i_want = action.params.i_want;
              if (action.params.so_that !== undefined) storyBody.so_that = action.params.so_that;
              if (action.params.story_points !== undefined) storyBody.story_points = action.params.story_points;
              if (action.params.priority !== undefined) storyBody.priority = action.params.priority;
              if (action.params.acceptance_criteria !== undefined) storyBody.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.persona_id !== undefined) storyBody.persona_id = validUuidOrUndef(action.params.persona_id);
              if (action.params.objective_id !== undefined) storyBody.objective_id = validUuidOrUndef(action.params.objective_id);
              if (action.params.module_id !== undefined) storyBody.module_id = validUuidOrUndef(action.params.module_id);
              if (action.params.capability_tag !== undefined) storyBody.capability_tag = action.params.capability_tag;
              if (Object.keys(storyBody).length === 0) {
                results[i] = { action, status: "error", detail: "No fields to update — update_user_story requires at least one changed field." };
                break;
              }
              const updatedStory = await userStoriesApi.update(updateId, storyBody) as { id: string; title: string };
              results[i] = {
                action,
                status: "success",
                detail: `Updated user story "${updatedStory.title}"`,
                data: { user_story_id: updatedStory.id, title: updatedStory.title },
              };
              break;
            }

            case "update_user_persona": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const personaBody: Record<string, unknown> = {};
              if (action.params.name !== undefined) personaBody.name = action.params.name;
              if (action.params.role !== undefined) personaBody.role = action.params.role;
              if (action.params.description !== undefined) personaBody.description = action.params.description;
              if (action.params.avatar_color !== undefined) personaBody.avatar_color = action.params.avatar_color;
              if (action.params.demographics !== undefined) personaBody.demographics = ensureParsed(action.params.demographics);
              if (action.params.goals !== undefined) personaBody.goals = ensureParsed(action.params.goals);
              if (action.params.pain_points !== undefined) personaBody.pain_points = ensureParsed(action.params.pain_points);
              if (action.params.behaviors !== undefined) personaBody.behaviors = ensureParsed(action.params.behaviors);
              if (action.params.needs !== undefined) personaBody.needs = ensureParsed(action.params.needs);
              if (action.params.scenarios !== undefined) personaBody.scenarios = ensureParsed(action.params.scenarios);
              const updatedPersona = await userPersonasApi.update(updateId, personaBody) as { id: string; name: string };
              results[i] = {
                action,
                status: "success",
                detail: `Updated persona "${updatedPersona.name}"`,
                data: { user_persona_id: updatedPersona.id, name: updatedPersona.name },
              };
              break;
            }

            case "update_task": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const taskBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) taskBody.title = action.params.title;
              if (action.params.description !== undefined) taskBody.description = action.params.description;
              if (action.params.priority !== undefined) taskBody.priority = action.params.priority;
              if (action.params.task_type !== undefined) taskBody.task_type = action.params.task_type;
              if (action.params.acceptance_criteria !== undefined) taskBody.acceptance_criteria = ensureParsed(action.params.acceptance_criteria);
              if (action.params.technical_notes !== undefined) taskBody.technical_notes = action.params.technical_notes;
              if (action.params.component_id !== undefined) taskBody.component_id = validUuidOrUndef(action.params.component_id);
              if (action.params.objective_id !== undefined) taskBody.objective_id = validUuidOrUndef(action.params.objective_id);
              if (action.params.story_id !== undefined) taskBody.story_id = validUuidOrUndef(action.params.story_id);
              if (action.params.estimated_hours !== undefined) taskBody.estimated_hours = action.params.estimated_hours;
              if (action.params.implementation_details !== undefined) taskBody.implementation_details = action.params.implementation_details;
              if (action.params.definition_of_done !== undefined) taskBody.definition_of_done = action.params.definition_of_done;
              if (action.params.test_strategy !== undefined) taskBody.test_strategy = action.params.test_strategy;
              if (action.params.tags !== undefined) taskBody.tags = ensureParsed(action.params.tags);
              // KI-0117: forward assigned_to so "assign this task to X" actions persist.
              if (action.params.assigned_to !== undefined) taskBody.assigned_to = validUuidOrUndef(action.params.assigned_to);
              // Forward due_date. `!== undefined` here, unlike create: on an
              // UPDATE an explicit empty value is how a date gets CLEARED, so
              // "" is normalised to null rather than dropped — otherwise
              // "remove the due date" would silently do nothing.
              if (action.params.due_date !== undefined) {
                taskBody.due_date = action.params.due_date || null;
              }
              if (Object.keys(taskBody).length === 0) {
                results[i] = { action, status: "error", detail: "No fields to update — update_task requires at least one changed field." };
                break;
              }
              const updatedTask = await tasksApi.update(updateId, taskBody as Partial<import("@/api/types").Task>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated task "${updatedTask.title}"`,
                data: { task_id: updatedTask.id, title: updatedTask.title },
              };
              break;
            }

            case "update_test_case": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const tcBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) tcBody.title = action.params.title;
              if (action.params.description !== undefined) tcBody.description = action.params.description;
              if (action.params.expected_result !== undefined) tcBody.expected_result = action.params.expected_result;
              if (action.params.test_type !== undefined) tcBody.test_type = action.params.test_type;
              if (action.params.priority !== undefined) tcBody.priority = action.params.priority;
              if (action.params.steps !== undefined) tcBody.steps = ensureParsed(action.params.steps);
              if (action.params.preconditions !== undefined) tcBody.preconditions = ensureParsed(action.params.preconditions);
              if (action.params.postconditions !== undefined) tcBody.postconditions = ensureParsed(action.params.postconditions);
              if (action.params.test_data !== undefined) tcBody.test_data = ensureParsed(action.params.test_data);
              if (action.params.automation_status !== undefined) tcBody.automation_status = action.params.automation_status;
              if (action.params.environment_requirements !== undefined) tcBody.environment_requirements = ensureParsed(action.params.environment_requirements);
              if (action.params.execution_time_limit !== undefined) tcBody.execution_time_limit = action.params.execution_time_limit;
              if (action.params.objective_id !== undefined) tcBody.objective_id = validUuidOrUndef(action.params.objective_id);
              if (action.params.component_id !== undefined) tcBody.component_id = validUuidOrUndef(action.params.component_id);
              if (action.params.impl_task_id !== undefined) tcBody.impl_task_id = validUuidOrUndef(action.params.impl_task_id);
              if (action.params.tags !== undefined) tcBody.tags = ensureParsed(action.params.tags);
              const updatedTc = await testCasesApi.update(updateId, tcBody as Partial<import("@/api/types").TestCase>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated test case "${updatedTc.title}"`,
                data: { test_case_id: updatedTc.id, title: updatedTc.title },
              };
              break;
            }

            case "update_meeting": {
              const updateId = action.params.id as string;
              if (!updateId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const meetBody: Record<string, unknown> = {};
              if (action.params.title !== undefined) meetBody.title = action.params.title;
              if (action.params.description !== undefined) meetBody.description = action.params.description;
              if (action.params.meeting_type !== undefined) meetBody.meeting_type = action.params.meeting_type;
              if (action.params.platform !== undefined) meetBody.platform = action.params.platform;
              if (action.params.scheduled_start !== undefined) meetBody.scheduled_start = action.params.scheduled_start;
              if (action.params.scheduled_end !== undefined) meetBody.scheduled_end = action.params.scheduled_end;
              if (action.params.agenda !== undefined) meetBody.agenda = ensureParsed(action.params.agenda);
              if (action.params.felix_role !== undefined) meetBody.felix_role = action.params.felix_role;
              if (action.params.felix_scope !== undefined) meetBody.felix_scope = action.params.felix_scope;
              if (action.params.felix_communication_mode !== undefined) meetBody.felix_communication_mode = action.params.felix_communication_mode;
              if (action.params.meeting_category !== undefined) meetBody.meeting_category = action.params.meeting_category;
              const updatedMeet = await meetingsApi.update(updateId, meetBody as Partial<import("@/api/types").Meeting>);
              results[i] = {
                action,
                status: "success",
                detail: `Updated meeting "${updatedMeet.title}"`,
                data: { meeting_id: updatedMeet.id, title: updatedMeet.title },
              };
              break;
            }

            case "delete_objective":
            case "delete_user_story":
            case "delete_user_persona":
            case "delete_module":
            case "delete_component":
            case "delete_task":
            case "delete_test_case":
            case "delete_meeting":
            case "delete_project": {
              const deleteId = action.params.id as string;
              if (!deleteId) {
                results[i] = { action, status: "error", detail: "Missing required field: id" };
                break;
              }
              const deleteApiMap: Record<string, { delete: (id: string) => Promise<void> }> = {
                delete_objective: objectivesApi,
                delete_user_story: userStoriesApi,
                delete_user_persona: userPersonasApi,
                delete_module: modulesApi,
                delete_component: componentsApi,
                delete_component_specification: componentSpecificationsApi,
                delete_task: tasksApi,
                delete_test_case: testCasesApi,
                delete_meeting: meetingsApi,
                delete_project: projectsApi,
              };
              const deleteApi = deleteApiMap[action.type];
              await deleteApi.delete(deleteId);
              const entityLabel = action.type.replace("delete_", "").replace("_", " ");
              results[i] = {
                action,
                status: "success",
                detail: `Deleted ${entityLabel} (ID: ${deleteId})`,
              };
              break;
            }

            default:
              // Felix advertises 42 client-routed tools and this switch
              // implements 20, so 22 land here — most of the Broadcast surface
              // among them. They used to render as
              // "Unknown action: broadcast_create_campaign": a raw internal
              // identifier shown to a customer, on a feature Felix had just
              // offered to do. CLAUDE.md is explicit that Felix must never show
              // raw errors to users.
              //
              // The real fix is per-tool — implement it, or stop advertising it
              // so Felix never offers it. Until then this says something true in
              // words a user can act on, and still returns status "error" so
              // Felix sees the failure and does not narrate success.
              results[i] = {
                action,
                status: "error",
                detail: `${humaniseActionType(action.type)} isn't available yet.`,
              };
          }
        } catch (err) {
          // FB-0033: Felix occasionally queues a delete action against
          // an entity that's already been deleted (stale pending_action
          // that survived a page reload). The server returns 404, we
          // used to file the 404 as a bug. Now treat 404-on-delete as
          // a no-op success — the desired end state (gone) is already
          // true. This runs BEFORE the generic error path so no ticket
          // is offered.
          const isDelete = action.type.startsWith("delete_");
          const isMissing404 =
            err instanceof ApiRequestError && err.status === 404;
          if (isDelete && isMissing404) {
            const entityLabel = action.type
              .replace(/^delete_/, "")
              .replace(/_/g, " ");
            const deletedId =
              (action.params?.id as string | undefined) ?? "unknown";
            results[i] = {
              action,
              status: "success",
              detail: `${entityLabel} (ID: ${deletedId}) was already deleted — no action needed.`,
            };
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId ? { ...m, actionResults: [...results] } : m
              )
            );
            continue;
          }

          const rawMsg = err instanceof Error ? err.message : "Action failed";
          // Include action type + entity ID for diagnostics (helps identify hallucinated IDs)
          const entityId = action.params?.id as string | undefined;
          const errorMsg = entityId
            ? `${action.type} failed for id ${entityId}: ${rawMsg}`
            : `${action.type} failed: ${rawMsg}`;
          // A failed ticket says so in its own line (FB-0105), not in the server's words.
          results[i] = {
            action,
            status: "error",
            detail: action.type === "log_feedback" ? "Not saved" : errorMsg,
          };

          // Only cascade-stop for chained create actions where subsequent
          // actions depend on the output (e.g. create_project → create_objective).
          // Independent actions (updates, navigates, feedback) continue.
          const isChainedCreate = action.type.startsWith("create_");
          if (isChainedCreate) {
            for (let j = i + 1; j < actions.length; j++) {
              if (actions[j].type.startsWith("create_")) {
                results[j] = { action: actions[j], status: "error", detail: "Skipped due to earlier error" };
              }
            }
          }

          // Update message with error results before offering ticket
          setMessages((prev) =>
            prev.map((m) => (m.id === msgId ? { ...m, actionResults: [...results] } : m))
          );

          // FB-0024..FB-0051 root cause: baseline-lock refusals are
          // legitimate API responses, not bugs. Detect them BEFORE
          // offering a feedback ticket. If the entity is baseline-
          // locked, offer a change request instead of filing noise.
          // FB-0031: same pattern for phase-gate refusals (trying to
          // create an entity ahead of the current SDLC phase).
          const lockInfo = detectBaselineLock(err);
          const phaseGateMsg = !lockInfo ? detectPhaseGate(err) : null;
          const fieldGate =
            !lockInfo && !phaseGateMsg ? detectFieldCompletenessGate(err) : null;
          // A plan-limit 402 is a billing decision, not a defect — it must be
          // ruled out before offering a ticket, same as the gates above.
          const planLimit =
            !lockInfo && !phaseGateMsg && !fieldGate
              ? detectPlanLimit(err)
              : null;
          if (lockInfo) {
            offerChangeRequest(action, lockInfo);
          } else if (phaseGateMsg) {
            explainPhaseGate(action, phaseGateMsg);
          } else if (planLimit) {
            explainPlanLimit(action, planLimit);
          } else if (fieldGate) {
            // FB-0137: legitimate field-completeness refusal (e.g. orphan task)
            // — give the server's actionable guidance, not an error ticket.
            explainFieldCompletenessGate(action, fieldGate);
          } else {
            // Auto-offer to log the error as a feedback ticket
            offerErrorTicket(errorMsg, action.type);
          }

          // Continue executing remaining independent actions
          if (!isChainedCreate) continue;
          break;
        }

        // Update message with latest results
        setMessages((prev) =>
          prev.map((m) => (m.id === msgId ? { ...m, actionResults: [...results] } : m))
        );
      }

      // Write successful actions to project memory (safe even if unmounted)
      writeActionMemory(actions, results);

      if (!mountedRef.current) return;

      // Invalidate React Query caches so UI reflects the new data.
      // FB-0034: collect the queryKeys that need refresh so we can AWAIT
      // refetches before navigating — otherwise the destination page
      // renders stale/empty cached data while the fetch is still pending.
      const createdTypes = new Set(
        results.filter((r) => r.status === "success").map((r) => r.action.type),
      );
      const affectedKeys: string[][] = [];
      if (createdTypes.has("create_objective") || createdTypes.has("update_objective") || createdTypes.has("delete_objective")) affectedKeys.push(["objectives"]);
      if (createdTypes.has("create_test_case") || createdTypes.has("update_test_case") || createdTypes.has("delete_test_case")) affectedKeys.push(["testCases"]);
      if (createdTypes.has("create_user_story") || createdTypes.has("update_user_story") || createdTypes.has("delete_user_story")) affectedKeys.push(["userStories"]);
      if (createdTypes.has("create_user_persona") || createdTypes.has("update_user_persona") || createdTypes.has("delete_user_persona")) affectedKeys.push(["userPersonas"]);
      if (createdTypes.has("create_module") || createdTypes.has("update_module") || createdTypes.has("delete_module")) affectedKeys.push(["modules"]);
      if (createdTypes.has("create_component") || createdTypes.has("update_component") || createdTypes.has("delete_component")) affectedKeys.push(["components"]);
      if (createdTypes.has("create_component_specification") || createdTypes.has("update_component_specification") || createdTypes.has("delete_component_specification")) affectedKeys.push(["componentSpecifications"]);
      if (createdTypes.has("create_project") || createdTypes.has("update_project") || createdTypes.has("delete_project")) affectedKeys.push(["projects"]);
      if (createdTypes.has("create_meeting") || createdTypes.has("update_meeting") || createdTypes.has("delete_meeting")) affectedKeys.push(["meetings"]);
      if (createdTypes.has("join_meeting") || createdTypes.has("leave_meeting")) affectedKeys.push(["meeting-bot-status"]);
      if (createdTypes.has("create_sprint") || createdTypes.has("add_sprint_item")) affectedKeys.push(["sprints"]);
      if (createdTypes.has("create_task") || createdTypes.has("update_task") || createdTypes.has("delete_task")) affectedKeys.push(["tasks"]);
      if (createdTypes.has("log_feedback") || createdTypes.has("update_feedback")) affectedKeys.push(["feedback"]);
      if (createdTypes.has("create_change_request")) affectedKeys.push(["changeRequests"]);
      if (createdTypes.has("create_defect")) affectedKeys.push(["defects"]);

      // Silent auto-navigate: if any successful action has navigate_after
      // metadata, navigate to that path without requiring user confirmation.
      const lastNavAction = [...results]
        .reverse()
        .find((r) => r.status === "success" && (r.action as unknown as Record<string, unknown>).navigate_after);
      // A new project opens on its own overview: the backend's navigate_after is built from
      // the page the person was on, which is the wrong project (or none, from the dashboard).
      const navPath = createdProject
        ? projectHomePath(createdProject.id)
        : lastNavAction
        ? ((lastNavAction.action as unknown as Record<string, unknown>).navigate_after as string)
        : null;

      if (navPath) {
        // FB-0034: Destination pages were rendering EMPTY tiles because
        // stale cached data (from a prior visit) was served immediately
        // while a background refetch ran. Remove the affected queries
        // from the cache entirely so the destination page shows a proper
        // loading state on mount, then fires a fresh fetch — no empty
        // tiles, no stale flash.
        affectedKeys.forEach((key) => {
          queryClient.removeQueries({ queryKey: key });
        });
        navigate(navPath);
      } else {
        // No navigation — plain invalidation is enough; React Query will
        // refetch on next subscribe.
        affectedKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }

      // FB-0007/FB-0003 FIX: Clear actions from the message after execution
      // so they never leak into pending_actions on subsequent requests.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, actions: undefined, pendingConfirmation: false } : m
        )
      );

      // FB-0005 FIX: Auto-send context-aware follow-up to Felix after execution.
      const successCount = results.filter((r) => r.status === "success").length;
      const failCount = results.filter((r) => r.status === "error").length;
      if (successCount > 0 && mountedRef.current) {
        const summaryParts = results
          .filter((r) => r.status === "success")
          .map((r) => r.detail || r.action.type)
          .slice(0, 5);
        const summaryText = summaryParts.join(", ");

        // Context-aware follow-up. PRIOR shape was a bracketed
        // "[Actions completed: ...]" string stamped INTO the user
        // input field and auto-sent — David Demsko 2026-05-06 03:05
        // ET filed it as "System action confirmation messages appear
        // as user messages in chat". The synthetic-user-message
        // pattern violates Invention G's user-intent gate (§7.9)
        // because Felix sees a directive that the human never typed.
        //
        // New shape: render a system-style notice + suggestion chips
        // the user can click to keep the conversation moving. No auto-
        // send; no user-bubble stamping.
        let noticeText: string;
        let chips: string[];
        if (createdProject && failCount === 0) {
          // The kickoff is one click away: the person's click sends it (never auto-sent).
          noticeText =
            `Created "${createdProject.name}". Felix can run its kickoff now: ` +
            `a few questions to set up its first objectives.`;
          chips = [KICKOFF_CHIP, "Show me what we have so far"];
        } else if (failCount > 0 && successCount > 0) {
          noticeText =
            `${successCount} action${successCount === 1 ? "" : "s"} succeeded, ` +
            `${failCount} failed — ${summaryText}.`;
          chips = ["Retry the failures", "Show me what failed", "Continue without retrying"];
        } else if (successCount >= 3) {
          noticeText =
            `${successCount} actions completed — ${summaryText}.`;
          chips = ["What's next?", "Review what we just created", "Move to the next phase"];
        } else {
          noticeText =
            `${successCount} action${successCount === 1 ? "" : "s"} completed — ${summaryText}.`;
          chips = ["What should we do next?", "Show me what we have so far"];
        }
        setTimeout(() => {
          if (mountedRef.current && !qaSessionId) {
            setMessages((prev) => [
              ...prev,
              {
                id: `action-summary-${Date.now()}`,
                role: "assistant",
                content: noticeText,
                actions: [],
                suggestions: chips,
                timestamp: new Date(),
                // FE-04 — render as a system status event, not a Felix bubble.
                isSystemEvent: true,
              },
            ]);
          }
        }, 500);
      }

      setIsExecuting(false);
    },
    // location.search: see the note on gatherDiagnostics — page_path carries it.
    [projectId, navigate, writeActionMemory, offerErrorTicket, gatherDiagnostics, messages, pageName, location.pathname, location.search, queryClient, qaSessionId],
  );

  // -------------------------------------------------------------------
  // Confirmation handlers
  // -------------------------------------------------------------------

  const handleConfirmActions = useCallback(
    (msgId: string) => {
      const msg = messages.find((m) => m.id === msgId);
      if (!msg?.actions) return;

      // Clear confirmation state
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId ? { ...m, pendingConfirmation: false } : m
        )
      );
      executeActions(msg.actions, msgId);
    },
    [messages, executeActions],
  );

  const handleCancelActions = useCallback((msgId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId
          ? { ...m, pendingConfirmation: false, actions: undefined }
          : m
      )
    );
    // Add a cancellation note
    setMessages((prev) => [
      ...prev,
      {
        id: `cancel-${Date.now()}`,
        role: "assistant" as const,
        content: "No problem — I've cancelled those actions. What else can I help with?",
        timestamp: new Date(),
      },
    ]);
  }, []);

  // -------------------------------------------------------------------
  // Exit Q&A mode
  // -------------------------------------------------------------------

  const exitQAMode = useCallback(() => {
    setQaSessionId(null);
    setQaEntityTitle("");
    shownInteractionsRef.current.clear();
    setMessages((prev) => [
      ...prev,
      {
        id: `qa-exit-${Date.now()}`,
        role: "assistant",
        content:
          "I've paused the Q&A session. You can resume it anytime — just ask me to continue scope discovery. What else can I help with?",
        timestamp: new Date(),
      },
    ]);
  }, []);

  // -------------------------------------------------------------------
  // Rate a message (thumbs up/down)
  // -------------------------------------------------------------------

  const handleRate = useCallback(async (msgId: string, rating: 'positive' | 'negative') => {
    if (ratedMessages[msgId]) return; // already rated

    const msg = messages.find(m => m.id === msgId);
    if (!msg || msg.role !== "assistant") return;

    // Find the preceding user message for context
    const msgIdx = messages.findIndex(m => m.id === msgId);
    const prevUserMsg = msgIdx > 0
      ? messages.slice(0, msgIdx).reverse().find(m => m.role === "user")
      : undefined;

    // Build conversation snapshot (last 5 messages up to and including rated message)
    const snapshot = messages
      .slice(Math.max(0, msgIdx - 4), msgIdx + 1)
      .map(m => ({ role: m.role, content: m.content.slice(0, 2000) }));

    setRatedMessages(prev => ({ ...prev, [msgId]: rating }));

    try {
      const result = await conversationRatingsApi.submit({
        rating,
        message_content: msg.content,
        user_message: prevUserMsg?.content,
        conversation_snapshot: snapshot,
        page_context: pageName || location.pathname,
        project_id: projectId || undefined,
      });

      // On thumbs-down, Felix asks why — capture the explanation on the next user message
      if (rating === "negative" && result?.id) {
        setPendingFeedbackRatingId(result.id);
        setMessages(prev => [...prev, {
          id: `feedback-ask-${Date.now()}`,
          role: "assistant",
          content: "Thanks for the feedback. Could you tell me what wasn't helpful about that response? Your input helps me improve.",
          timestamp: new Date(),
        }]);
      }
    } catch {
      // Non-critical — don't disrupt chat experience
    }
  }, [messages, ratedMessages, pageName, location.pathname, projectId]);

  // -------------------------------------------------------------------
  // Send message (handles both normal chat and Q&A answers)
  // -------------------------------------------------------------------

  const handleSend = useCallback(async (overrideText?: string) => {
    // KI-0218: quick-reply buttons used to set `input` then rely on an
    // effect to re-fire handleSend once the state flushed. That
    // indirection was timing-fragile — handleSend closes over `input`,
    // so a caller passing freshly-set state could be served a stale
    // closure and the click did nothing. Callers with a known message
    // (quick replies, chips, clarifier) pass it directly via
    // overrideText, bypassing the `input` round-trip entirely.
    const text = (typeof overrideText === "string" ? overrideText : input).trim();
    if (!text || felixChat.isPending || isExecuting || isStreaming || submitQAAnswer.isPending) return;

    // Stop mic if it was recording or transcribing
    if (useDeepgramMode) {
      if (deepgram.recording) deepgram.stop();
      deepgram.clear();
      lastTranscriptRef.current = ""; // Prevent stale transcript from refilling input
    } else {
      if (speech.listening) speech.stop();
      speech.clear();
    }

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content: text,
      timestamp: new Date(),
    };
    // Sending a new message supersedes any prior un-confirmed action card —
    // the user chose to move on rather than click confirm/cancel, so a stale
    // bar must not linger or re-pop. The FB-0005 pending-action reminder below
    // is computed from the pre-send `messages` snapshot, so Felix is still told
    // what was pending and can re-propose or execute as the new turn warrants.
    setMessages((prev) => [...supersedePendingCards(prev), userMsg]);
    setInput("");
    setQuickReplies([]);  // Clear quick replies on send
    setFelixError(false);
    // Dismiss briefing on first message send
    if (!briefingDismissed) dismissBriefing();

    // If user is explaining why they thumbs-downed, save the comment on the rating
    const isFeedbackExplanation = !!pendingFeedbackRatingId;
    if (pendingFeedbackRatingId) {
      const ratingId = pendingFeedbackRatingId;
      setPendingFeedbackRatingId(null);
      conversationRatingsApi.updateComment(ratingId, text).catch(() => {
        // Non-critical — explanation is still in the chat history for analysis
      });
    }

    // Q&A mode — send answer to the QA API
    if (qaSessionId) {
      try {
        await submitQAAnswer.mutateAsync({ sessionId: qaSessionId, answer: text });
        // The polling effect will pick up the next question automatically
      } catch (err) {
        const status = err instanceof ApiRequestError ? err.status : 0;
        const errorMsg = err instanceof Error ? err.message : "Failed to submit answer";

        let userMessage: string;
        if (status === 404) {
          userMessage =
            "This Q&A session could not be found — it may have expired or been deleted. " +
            "Try starting a new session.";
        } else if (status === 409) {
          userMessage =
            "There's no pending question to answer right now. " +
            "The session may have already moved on — I'll check for the next question.";
        } else {
          userMessage =
            "I had trouble saving your answer. You can try again, or I can log this for the team to look into.";
        }

        setMessages((prev) => [
          ...prev,
          {
            id: `qa-error-${Date.now()}`,
            role: "assistant",
            content: userMessage,
            timestamp: new Date(),
          },
        ]);

        // Only offer to log a feedback ticket for server errors, not expected conflicts
        if (status === 0 || status >= 500) {
          offerErrorTicket(errorMsg, "qa_answer");
        }
      }
      return;
    }

    // Normal Felix chat mode — uses streaming for real-time response
    // Trim history to prevent context overflow.  30 messages for both modes
    // (was 10 for normal, but that caused user approvals/confirmations to
    // fall off the window, making Felix re-ask the same questions).
    const historyLimit = 30;
    const history = messages.slice(-historyLimit).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    // Include recent errors so Felix can speak intelligently about issues
    const recentErrors = _recentErrors
      .filter((e) => Date.now() - e.timestamp < 5 * 60 * 1000) // last 5 min
      .map((e) => `[${e.source}] ${e.message}`);

    // Extract entity context from URL so Felix gets explicit entity info.
    // pathname + search, NOT pathname alone: the open record is a query param
    // (the entity routes are list pages with a side panel), and `pathname`
    // drops the query — so the panel branch of the parser could never fire on
    // the value this call used to pass.
    const entityInfo = extractEntityFromPath(location.pathname + location.search);

    // FB-0005 FIX: Collect any pending (unconfirmed) actions from previous
    // turns so the backend can inject a strong reminder into Felix's context.
    // Without this, Felix has no way to know that actions are still pending
    // and may falsely claim they are done.
    const pendingActions = messages
      .filter((m) => m.pendingConfirmation && m.actions?.length)
      .flatMap((m) =>
        (m.actions || []).map((a) => ({ type: a.type, label: a.label || a.type }))
      );

    const requestBody = {
      message: text,
      // Invention G — 6th domain: provenance-aware intent gate
      // (spec § 7.13). Every message MUST tag its origin. The
      // handleSend transport reaches the server with this provenance
      // value; the server-side gate at felix_provenance_gate.check()
      // refuses autonomous-action tools when the value is anything
      // other than "human".
      //
      // handleSend is reached from three transports today:
      //   1. The user-input field + Send button (genuine human
      //      authorship — "human" is correct).
      //   2. Suggestion-chip clicks (`pendingSuggestionRef` +
      //      setInput auto-fire). The CLICK is a user gesture, so
      //      "human" remains semantically correct — the user
      //      explicitly authorised the message by clicking the chip.
      //   3. (Removed 2026-05-07) Synthetic-message transports —
      //      upload-auto-trigger and post-actions auto-stamp. Both
      //      were pulled; if a future regression re-introduces them,
      //      the regression test
      //      tests/test_personas/test_felixchat_synthetic_user_message_invariants.py
      //      will fail.
      //
      // The classification here is correct as long as the only paths
      // reaching handleSend are user gestures (typed input or chip
      // click). Any non-gesture caller MUST pass `provenance:
      // "client_synthetic"` explicitly so the server gate refuses.
      provenance: "human" as const,
      // KI-0214/0215 (FB-0008): tell the backend the user's IANA tz so
      // Felix's "today" / weekday references use local date, not UTC.
      client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page: pageName,
      page_path: location.pathname + location.search,
      project_id: projectId,
      // ROW b1b55364 item 3: WHICH record is open travels as its own named
      // field. It was only ever inside the generic selected_items list, which
      // the backend reads as "things the user ticked" — indistinguishable from
      // a multi-select. The URL parse still wins where a URL says which record
      // is open, because that is the stronger signal and covers the six entity
      // list pages; openEntity fills the gap for panels that never touch it.
      entity_type: entityInfo?.entity_type ?? pageContext.openEntity?.type,
      entity_id: entityInfo?.entity_id ?? pageContext.openEntity?.id,
      session_id: chatSessionId,
      history,
      recent_errors: recentErrors.length > 0 ? recentErrors : undefined,
      ...(pendingActions.length > 0 && { pending_actions: pendingActions }),
      ...(sketchMode && { mode: "sketch" }),
      ui_state: {
        viewport_width: window.innerWidth,
        viewport_height: window.innerHeight,
        sidebar_collapsed: sidebarCollapsed,
        ...(Object.keys(pageContext.activeFilters).length > 0 && {
          active_filters: pageContext.activeFilters,
        }),
        ...(pageContext.searchQuery && {
          search_query: pageContext.searchQuery,
        }),
        ...(pageContext.selectedItems.length > 0 && {
          selected_items: pageContext.selectedItems,
        }),
        ...(pageContext.openDialog && {
          open_dialog: pageContext.openDialog,
        }),
      },
      // Preview mode: previews artifacts instead of executing them
      ...(autonomousMode && previewMode && { preview_mode: true }),
      // Feedback explanation — Felix should acknowledge briefly and move on
      ...(isFeedbackExplanation && { is_feedback_explanation: true }),
    };

    const msgId = `felix-${Date.now()}`;

    // ===============================================================
    // AUTONOMOUS MODE — server-side execution, no confirmation
    // ===============================================================
    if (autonomousMode) {
      let fullText = "";
      try {
        // Seed with a "thinking" line so the bubble is never empty while Felix
        // reasons before his first tool/text (Bob 2026-06-20). Replaced by the
        // first text delta or tool narration.
        const streamingMsg: Message = {
          id: msgId,
          role: "assistant",
          content: FELIX_THINKING_SEED,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, streamingMsg]);
        setIsStreaming(true);
        setAutonomousProgress({ active: true, completed: 0, actions: [] });
        setVerifiedResults(null); // Reset verification on new request

        const abortController = new AbortController();
        streamAbortRef.current = abortController;

        let streamError = false;
        // A card the autonomy level withheld IS this turn's answer: its message
        // carries the reason, and nothing ran, so the empty-response safety net
        // below must not replace that with "I wasn't able to generate a response".
        let withheldForConfirmation = false;
        const completedActions: { tool: string; detail: string; success: boolean }[] = [];

        for await (const event of assistantApi.chatAutonomous(requestBody, abortController.signal)) {
          const streamEvent = event as FelixAutonomousEvent;

          switch (streamEvent.type) {
            case "text_delta":
              fullText += streamEvent.text;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, content: fullText, status: undefined } : m
                )
              );
              break;

            case "replace_text": {
              const replaced = (streamEvent as { text?: string }).text ?? "";
              fullText = replaced;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, content: fullText, status: undefined } : m
                )
              );
              break;
            }

            case "action_complete": {
              const acEvent = streamEvent as FelixAutonomousActionComplete;
              const result = acEvent.result;

              // In preview mode, collect artifacts for approval
              if (result.preview) {
                setPreviewArtifacts((prev) => [
                  ...prev,
                  { tool: acEvent.tool, result, approved: true },
                ]);
              }

              completedActions.push({
                tool: acEvent.tool,
                detail: result.detail || acEvent.tool,
                success: result.success,
              });
              setAutonomousProgress({
                active: true,
                completed: completedActions.length,
                actions: [...completedActions],
              });
              break;
            }

            case "progress": {
              // Show progress message when no text has been received yet
              // This prevents the "empty bubble" appearance during initialization
              const progressMsg = (streamEvent as { message?: string }).message;
              if (!fullText && progressMsg) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === msgId ? { ...m, content: `*${progressMsg}*` } : m
                  )
                );
              }
              break;
            }

            case "verification": {
              // System-verified results — ground truth from actual tool execution
              const v = streamEvent as import("@/api/services").FelixAutonomousVerification;
              setVerifiedResults((prev) => ({
                totalSucceeded: (prev?.totalSucceeded ?? 0) + v.succeeded,
                totalFailed: (prev?.totalFailed ?? 0) + v.failed,
                details: [...(prev?.details ?? []), ...v.details],
              }));
              break;
            }

            case "conflict_alert": {
              const ca = streamEvent as {
                blocking_conflicts?: number;
                escalated_conflicts?: number;
                total_conflicts?: number;
                conflicts?: Array<{
                  id: string;
                  severity: string;
                  conflict_type: string;
                  description: string;
                  entity_a_type: string;
                  entity_b_type: string;
                }>;
              };
              const conflictCount = ca.blocking_conflicts ?? 0;
              const escalatedCount = ca.escalated_conflicts ?? 0;
              let alertText = `\n\n> **Conflict Alert**: ${conflictCount} blocking conflict(s) detected.`;
              if (escalatedCount > 0) {
                alertText += ` ${escalatedCount} escalated to you for manual review.`;
              }
              alertText += " Felix is attempting to self-correct...";
              if (ca.conflicts) {
                ca.conflicts.forEach((c) => {
                  alertText += `\n> - **${c.severity}** ${c.conflict_type}: ${c.description}`;
                });
              }
              fullText += alertText;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, content: fullText } : m
                )
              );
              break;
            }

            case "conflict_escalation": {
              const ce = streamEvent as {
                escalated_count?: number;
                message?: string;
                conflicts?: Array<{
                  id: string;
                  severity: string;
                  conflict_type: string;
                  description: string;
                  attempts: number;
                }>;
              };
              let escText = `\n\n> **Escalation**: ${ce.message || "Conflicts require your manual review."}`;
              if (ce.conflicts) {
                ce.conflicts.forEach((c) => {
                  escText += `\n> - **${c.severity}** ${c.conflict_type} (${c.attempts} attempts): ${c.description}`;
                });
              }
              escText += "\n> Navigate to **Conflicts** to review and resolve.";
              fullText += escText;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, content: fullText } : m
                )
              );
              break;
            }

            case "tool_executing": {
              const toolNames = (streamEvent as { tools?: string[] }).tools || [];
              // Bob 2026-06-20: surface a pithy, Claude-style progress line so
              // the user knows Felix is working — NEVER the raw tool names /
              // inner workings ("we do not need to expose his inner workings").
              // The backend supplies `narration`; fall back to a generic
              // working line, never to the tool list. Tool names are still
              // tracked in `toolsUsed` for the opt-in reasoning expander only.
              const narration = (streamEvent as { narration?: string }).narration;
              // Row f5098ee9: the status shows with the spinner until his next words.
              const status = heldRoundStatus(toolNames) ?? (narration || "Working on it…");
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== msgId) return m;
                  const existing = m.toolsUsed || [];
                  const merged = [...new Set([...existing, ...toolNames])];
                  return { ...m, content: fullText || FELIX_THINKING_SEED, status, toolsUsed: merged };
                })
              );
              break;
            }

            case "tool_started": {
              // R4 — per-tool progress. Fires before each tool runs so
              // the UI shows "Calling create_objective (3 of 5)" live
              // instead of staying frozen on the round's list event for
              // 30s+. We don't replace fullText here — we just set a
              // transient status hint shown while no text is streaming.
              const e = streamEvent as {
                tool?: string;
                index?: number;
                total?: number;
                round?: number;
              };
              // Hide the tool name (Bob 2026-06-20: no inner workings). Show a
              // calm, pithy line plus the round's progress count so the user
              // feels movement without seeing the mechanics.
              const idx = (e.index ?? 0) + 1;
              const total = e.total ?? 1;
              const status = heldRoundStatus([e.tool]) ?? (total > 1
                ? `Working through it… (${idx} of ${total})`
                : "Working on it…");
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== msgId) return m;
                  // Text already streamed this turn stays as it is; the status
                  // shows under it (row f5098ee9).
                  return { ...m, content: fullText || FELIX_THINKING_SEED, status };
                })
              );
              break;
            }

            case "frontend_action": {
              const action = (streamEvent as FelixFrontendAction).action;
              if (action?.type === "start_qa") {
                setQaSessionId(action.params.qa_session_id as string);
                setQaEntityTitle((action.params.entity_title as string) || "Scope Discovery");
                shownInteractionsRef.current.clear();
                setMessages(prev => [...prev, {
                  id: `qa-transition-${Date.now()}`,
                  role: "assistant",
                  content: `I've started a structured Q&A session to properly define "${action.params.entity_title}". ` +
                           `The Business Analyst will guide you through targeted questions. ` +
                           `Once we have good coverage, I can automatically generate all the downstream artifacts.`,
                  timestamp: new Date(),
                }]);
              } else if (action) {
                // An action the org's autonomy level WITHHELD — the server
                // stopped the loop rather than executing it (felix_autonomy.
                // requires_confirmation). Raise the normal confirmation card so
                // the user can approve it.
                //
                // This branch is why the backend gate is not disconnected
                // scaffolding: until it existed, `frontend_action` handled ONLY
                // start_qa and every other action fell straight through to
                // `break` — silently discarded. A withheld delete would have
                // vanished, the loop would have stopped, and the user would
                // have been shown "Done!" with nothing done and no card.
                // The SSE envelope types `action.type` as a plain string, but
                // Message.actions is FelixAction[], whose `type` is a literal
                // union — deliberately, so that dispatching an action the
                // client cannot perform is a BUILD error (services.ts:3386).
                // Narrowing here rather than widening that union keeps the
                // property. The narrowing is safe because the backend only
                // withholds tools from felix_executor's registries, and
                // test_felix_autonomy.py asserts list-vs-list, in both
                // directions, that every one of those has a deleteApiMap entry
                // here — so a tool that could not be executed is a red build
                // before it can ever reach this line.
                const withheld: FelixAction = {
                  type: action.type as FelixAction["type"],
                  params: action.params,
                  label: action.label ?? `Confirm ${action.type}`,
                };
                // The card keeps the action's own label ("File defect 'X'"),
                // which is what goes back as pending_actions[].label and what
                // the server reconciles against the database. WHY it was held
                // (the autonomy level) is the message text. An older server
                // sent the reason AS the label and no `reason`; fall back.
                withheldForConfirmation = true;
                setMessages((prev) =>
                  supersedePendingCards(prev, msgId).map((m) =>
                    m.id === msgId
                      ? {
                          ...m,
                          content:
                            (m.content ? m.content + "\n\n" : "") +
                            (action.reason ?? withheld.label),
                          actions: [withheld],
                          pendingConfirmation: true,
                        }
                      : m
                  )
                );
              }
              break;
            }

            case "done": {
              // If a Q&A session was started, skip the normal completion summary
              const qaStarted = (streamEvent as FelixAutonomousDone).qa_started;
              if (qaStarted) {
                break;
              }
              // Same for an action held back by the autonomy level: the
              // confirmation card raised above IS the end of this turn, and
              // overwriting the message with "Done!" would both erase the card's
              // explanation and tell the user something happened that did not.
              if ((streamEvent as { awaiting_confirmation?: boolean }).awaiting_confirmation) {
                break;
              }
              // Row 6ccf911b: the final text the server settled on. Both the
              // autonomous grader and the chat stream (which answers a question
              // sent in Auto mode) send it on done; the streamed text loses.
              const autoReplaceText = (streamEvent as { replace_text?: string }).replace_text;
              if (autoReplaceText) {
                fullText = autoReplaceText;
              }

              // Build verified results footer from action_complete events
              // This is the GROUND TRUTH — generated from actual SSE events,
              // not from Claude's text narration.
              const succeeded = completedActions.filter(a => a.success);
              const failedActions = completedActions.filter(a => !a.success);
              let verifiedFooter = "";
              if (previewMode && previewArtifacts.length > 0) {
                // Preview mode — show review prompt instead of verified results
                verifiedFooter = `\n\n---\n**Preview** *(${succeeded.length} artifact${succeeded.length !== 1 ? "s" : ""} ready for review)*\nReview the previews below and click **Approve All** or individually select which to commit.`;
              } else if (completedActions.length > 0) {
                verifiedFooter = "\n\n---\n**Verified Results** *(system-generated)*\n";
                verifiedFooter += `- **${succeeded.length}** action${succeeded.length !== 1 ? "s" : ""} succeeded`;
                if (failedActions.length > 0) {
                  verifiedFooter += `\n- **${failedActions.length}** action${failedActions.length !== 1 ? "s" : ""} failed`;
                  failedActions.forEach(a => {
                    verifiedFooter += `\n  - ${a.tool}: ${a.detail}`;
                  });
                }
              }
              const finalContent = (fullText || "Done!") + verifiedFooter;
              // Extract token usage from the done event (enriched by backend)
              const autoTokenUsage = (streamEvent as { token_usage?: TokenUsage }).token_usage;
              const autoProposals = (streamEvent as FelixAutonomousDone).proposals;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId
                    ? {
                        ...m,
                        content: finalContent,
                        tokenUsage: autoTokenUsage,
                        proposals: autoProposals?.length ? autoProposals : undefined,
                      }
                    : m
                )
              );
              break;
            }

            case "error": {
              streamError = true;
              setFelixError(true);
  // Out of AI credits is an upsell moment, not "I'm having trouble".
  // The stream now emits CREDIT_EXHAUSTED with the same payload the
  // REST 402 carries, so route it to the credit modal instead of
  // telling a paying customer to try again — retrying cannot work
  // until they top up.
  const creditPayload = streamEvent as unknown;
  if (isCreditBlockPayload(creditPayload)) {
    useCreditBlockStore.getState().show(creditPayload);
  }
              const autoErrorDetail = (streamEvent as { error?: string }).error || "";
              const autoErrorMsg = autoErrorDetail
                ? `Sorry, I encountered an issue: ${autoErrorDetail}`
                : "Sorry, I encountered an issue during autonomous execution.";
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId
                    ? {
                        ...m,
                        content: fullText || autoErrorMsg,
                      }
                    : m
                )
              );
              break;
            }
          }
        }

        if (!mountedRef.current) return;
        setIsStreaming(false);
        streamAbortRef.current = null;
        setAutonomousProgress((prev) => ({ ...prev, active: false }));

        // SAFETY NET: empty response fallback (same as normal mode)
        // CRITICAL FIX: Do NOT overwrite when streamError is true — the error
        // handler already set the message content with the real error detail.
        // Previously, this would replace the actual error message with a generic
        // "I wasn't able to generate a response" because fullText (local var)
        // was never updated by the error handler (it uses setMessages instead).
        if (!fullText && completedActions.length === 0 && !streamError && !withheldForConfirmation) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msgId
                ? {
                    ...m,
                    content:
                      "I wasn't able to generate a response. This may be a temporary connection issue — please try again.",
                  }
                : m
            )
          );
        }

        // Invalidate all entity queries since autonomous mode may have modified many
        // Invalidate even on partial failures — some actions may have succeeded
        // But NOT in preview mode — nothing was actually written
        const hasAnySuccess = !previewMode && completedActions.some(a => a.success);
        if (hasAnySuccess) {
          queryClient.invalidateQueries({ queryKey: ["projects"] });
          queryClient.invalidateQueries({ queryKey: ["objectives"] });
          queryClient.invalidateQueries({ queryKey: ["userStories"] });
          queryClient.invalidateQueries({ queryKey: ["userPersonas"] });
          queryClient.invalidateQueries({ queryKey: ["modules"] });
          queryClient.invalidateQueries({ queryKey: ["components"] });
          queryClient.invalidateQueries({ queryKey: ["tasks"] });
          queryClient.invalidateQueries({ queryKey: ["testCases"] });
          queryClient.invalidateQueries({ queryKey: ["meetings"] });
        }
      } catch (err) {
        if (!mountedRef.current) return;
        setIsStreaming(false);
        streamAbortRef.current = null;
        setAutonomousProgress((prev) => ({ ...prev, active: false }));

        if ((err as Error)?.name === "AbortError") return;

        const errorMessage = (err as Error)?.message || String(err);
        const isTimeout = errorMessage.toLowerCase().includes("timeout");
        const isConnectionDrop =
          errorMessage === "Load failed" ||
          errorMessage === "Failed to fetch" ||
          errorMessage.toLowerCase().includes("network");
        const displayMessage = isTimeout
          ? "The operation is taking longer than expected. Check background tasks for status, " +
            "or ask Felix what was completed."
          : isConnectionDrop
          ? "Connection lost during autonomous execution. Your work up to this point has been saved. " +
            "Check background tasks or ask Felix what was completed."
          : `Autonomous session interrupted: ${errorMessage}`;
        console.error("Felix autonomous mode error:", err);
        setFelixError(true);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content: fullText
                    ? `${fullText}\n\n---\n**Error**: ${displayMessage}`
                    : `Sorry, I encountered an error in autonomous mode: ${displayMessage}`,
                }
              : m
          )
        );
      }
      return;
    }

    // ===============================================================
    // NORMAL MODE — client-side execution with confirmation
    // ===============================================================
    try {
      // Create a placeholder message that will be progressively filled.
      // Seed it with a pithy "thinking" line so the bubble is never empty
      // while Felix reasons BEFORE his first tool/text (Bob 2026-06-20: the
      // user must never be left wondering if he stalled). The first text_delta
      // or tool narration replaces it.
      const streamingMsg: Message = {
        id: msgId,
        role: "assistant",
        content: FELIX_THINKING_SEED,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, streamingMsg]);
      setIsStreaming(true);

      const abortController = new AbortController();
      streamAbortRef.current = abortController;

      let fullText = "";
      const collectedActions: FelixAction[] = [];
      let streamError = false;

      for await (const event of assistantApi.chatStream(requestBody, abortController.signal)) {
        const streamEvent = event as FelixStreamEvent;

        switch (streamEvent.type) {
          case "text_delta":
            fullText += streamEvent.text;
            // Update the message content progressively
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId ? { ...m, content: fullText, status: undefined } : m
              )
            );
            break;

          case "replace_text": {
            // Backend overrides any text streamed so far with a safe,
            // system-generated message (truthfulness guardrail).  Emitted
            // when action tools execute after Claude has already narrated
            // a fabricated response.  See persona.py:_stream_response.
            const replaced = (streamEvent as { text?: string }).text ?? "";
            fullText = replaced;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId ? { ...m, content: fullText, status: undefined } : m
              )
            );
            break;
          }

          case "action":
            collectedActions.push(streamEvent.action);
            break;

          case "stream_resumed": {
            // Wave 8 — the upstream hiccuped mid-text and Felix is
            // continuing. Don't replace the streamed prefix; just log
            // a small hint so the pause isn't mysterious.
            const e = streamEvent as {
              attempt?: number;
              reason?: string;
              retry_in_seconds?: number;
            };
            console.info(
              "[FelixChat] stream resumed after",
              e.reason || "unknown",
              `(attempt ${e.attempt}, waited ~${e.retry_in_seconds}s)`,
            );
            break;
          }

          case "plan.started" as const: {
            // § 7.15 Phase 2 — backend signals plan-as-first-class
            // for this round. Initialize the plan state so per-step
            // events can update it.
            const ev = streamEvent as {
              type: string;
              plan_id?: string;
              summary?: string;
              step_count?: number;
            };
            const initialPlan: PlanState = {
              plan_id: ev.plan_id || "stream-plan",
              summary: ev.summary || `${ev.step_count ?? 0} steps planned`,
              steps: [],
            };
            setMessages((prev) =>
              prev.map((m) => (m.id === msgId ? { ...m, plan: initialPlan } : m))
            );
            break;
          }

          case "plan.step.started" as const: {
            const ev = streamEvent as {
              type: string;
              step_id?: string;
              description?: string;
              tool?: string;
            };
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId || !m.plan) return m;
                const newStep: PlanStep = {
                  step_id: ev.step_id || "",
                  description: ev.description || ev.tool || "step",
                  tool: ev.tool || "",
                  status: "running" as PlanStepStatus,
                };
                const existing = m.plan.steps.findIndex(
                  (s) => s.step_id === newStep.step_id,
                );
                const steps =
                  existing >= 0
                    ? m.plan.steps.map((s, i) =>
                        i === existing ? { ...s, status: "running" as PlanStepStatus } : s,
                      )
                    : [...m.plan.steps, newStep];
                return { ...m, plan: { ...m.plan, steps } };
              })
            );
            break;
          }

          case "plan.step.completed" as const:
          case "plan.step.failed" as const: {
            const ev = streamEvent as {
              type: string;
              step_id?: string;
            };
            const newStatus: PlanStepStatus =
              streamEvent.type === "plan.step.failed" ? "failed" : "completed";
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId || !m.plan) return m;
                const steps = m.plan.steps.map((s) =>
                  s.step_id === ev.step_id ? { ...s, status: newStatus } : s,
                );
                return { ...m, plan: { ...m.plan, steps } };
              })
            );
            break;
          }

          case "tool_executing": {
            const toolNames = (streamEvent as { tools?: string[] }).tools || [];
            // Bob 2026-06-20: a pithy, Claude-style progress line — NEVER the
            // raw tool names ("we do not need to expose his inner workings").
            // The backend supplies `narration`; fall back to a generic working
            // line. Tool names stay only in `toolsUsed` for the opt-in
            // reasoning expander. (This is the in-app chat STREAM path — the
            // earlier #1287 edit only fixed the autonomous loop's handler.)
            // Row f5098ee9: the status shows with the spinner, under any words
            // already shown, until his next words arrive.
            const narration = (streamEvent as { narration?: string }).narration;
            const status = heldRoundStatus(toolNames) ?? (narration || "Working on it…");
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId) return m;
                const existing = m.toolsUsed || [];
                const merged = [...new Set([...existing, ...toolNames])];
                return { ...m, content: fullText || FELIX_THINKING_SEED, status, toolsUsed: merged };
              })
            );
            break;
          }

          case "tool_started": {
            // R4 — per-tool progress during chat streaming.
            const e = streamEvent as {
              tool?: string;
              index?: number;
              total?: number;
            };
            // Hide the tool name (Bob 2026-06-20: no inner workings); keep only
            // the movement count so the user feels progress. Row f5098ee9: a
            // held round's tool says what Felix is doing instead.
            const idx = (e.index ?? 0) + 1;
            const total = e.total ?? 1;
            const status = heldRoundStatus([e.tool]) ?? (total > 1
              ? `Working through it… (${idx} of ${total})`
              : "Working on it…");
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== msgId) return m;
                return { ...m, content: fullText || FELIX_THINKING_SEED, status };
              })
            );
            break;
          }

          case "done": {
            // Finalize the message with actions and token usage.
            // CRITICAL: If the backend sent a replace_text field, use it
            // instead of the streamed text.  This happens when Claude's
            // text from earlier rounds claimed "Creating now" but client
            // tools were only collected in a later round — the backend
            // replaces ALL text with a safe system-generated message.
            const streamTokenUsage = (streamEvent as { token_usage?: TokenUsage }).token_usage;
            const replaceText = (streamEvent as { replace_text?: string }).replace_text;
            if (replaceText) {
              fullText = replaceText;
            }
            // Invention H validator correction: if the backend's
            // post-stream validator pass downgraded the verdict from
            // 'verified' to 'partial' or 'hallucinated', replace the
            // streamed text with the honest enumeration. Action-tool
            // replace_text takes precedence above; this fires when no
            // action tools were involved but the validator caught a
            // hallucinated success summary at the receipt layer (e.g.
            // 'All 6 added 🎉' while 3 actually failed). See
            // docs/FTL_PATENT_TECHNICAL_SPEC.md § 7B.1 primitive (iii).
            const validatorCorrection = (streamEvent as { h_validator_correction?: string }).h_validator_correction;
            if (validatorCorrection && !replaceText) {
              fullText = validatorCorrection;
            }
            // § 7.17 anchoring guard — surface the chip ONLY when
            // the backend's grader returned low-band confidence or a
            // hallucinated_action / low_confidence verdict. Medium
            // and high never reach the UI (Lee & See 2004).
            const lowConfidenceChip = Boolean(
              (streamEvent as { low_confidence_chip?: boolean }).low_confidence_chip
            );
            // § 7.15 Phase 1 — clarifier payload threaded onto the
            // assistant message; FelixChatClarifier renders it.
            const clarifierPayload = (
              streamEvent as {
                clarifier_payload?:
                  | import("@/api/services").ClarifierPayload
                  | null;
              }
            ).clarifier_payload;
            const doneProposals = (streamEvent as { proposals?: FelixProposal[] }).proposals;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId
                  ? {
                      ...m,
                      content: fullText || "I'm here to help! What would you like to do?",
                      actions: collectedActions.length > 0 ? collectedActions : undefined,
                      tokenUsage: streamTokenUsage,
                      lowConfidenceChip,
                      clarifierPayload: clarifierPayload ?? null,
                      proposals: doneProposals?.length ? doneProposals : undefined,
                    }
                  : m
              )
            );
            // Surface quick reply buttons from the done event
            const doneQuickReplies = (streamEvent as { quick_replies?: QuickReply[] }).quick_replies;
            if (doneQuickReplies?.length && collectedActions.length === 0) {
              setQuickReplies(doneQuickReplies);
            }
            break;
          }

          case "error": {
            streamError = true;
            setFelixError(true);
// Out of AI credits is an upsell moment, not "I'm having trouble".
// The stream now emits CREDIT_EXHAUSTED with the same payload the
// REST 402 carries, so route it to the credit modal instead of
// telling a paying customer to try again — retrying cannot work
// until they top up.
const creditPayload = streamEvent as unknown;
if (isCreditBlockPayload(creditPayload)) {
  useCreditBlockStore.getState().show(creditPayload);
}
            const errorDetail = (streamEvent as { error?: string }).error || "";
            const errorMsg = errorDetail
              ? `Sorry, I'm having trouble right now: ${errorDetail}. Please try again in a moment.`
              : "Sorry, I'm having trouble connecting right now. Please try again in a moment.";
            setMessages((prev) =>
              prev.map((m) =>
                m.id === msgId
                  ? {
                      ...m,
                      content: fullText || errorMsg,
                    }
                  : m
              )
            );
            break;
          }
        }
      }

      if (!mountedRef.current) return;
      setIsStreaming(false);
      streamAbortRef.current = null;

      // SAFETY NET: If the stream completed without any text and no done
      // event was received (e.g., SSE connection closed prematurely, proxy
      // timeout, server error before any events were sent), set a fallback
      // message so the user doesn't see an empty chat bubble.
      if (!fullText && !streamError && collectedActions.length === 0) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content:
                    "I wasn't able to generate a response. This may be a temporary connection issue — please try again.",
                }
              : m
          )
        );
      }

      // Handle actions after stream completes
      if (!streamError && collectedActions.length > 0) {
        const needsConfirm = collectedActions.some(actionNeedsConfirmation);
        if (needsConfirm) {
          setMessages((prev) =>
            supersedePendingCards(prev, msgId).map((m) =>
              m.id === msgId ? { ...m, pendingConfirmation: true } : m
            )
          );
        } else {
          adviseBreakRisk(collectedActions);
          setTimeout(() => {
            if (mountedRef.current) executeActions(collectedActions, msgId);
          }, 400);
        }
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setIsStreaming(false);
      streamAbortRef.current = null;

      // If stream fails, fall back to non-streaming request.
      // KI-0109: the synchronous fallback is the 504 trap for long-
      // running batch operations (e.g. "generate personas+stories
      // for all 12 objectives"). nginx caps `/api/` at proxy_read_timeout
      // 3600s but the synchronous endpoint serialises every Claude
      // round before returning, so the round-trip easily blows past
      // any client-side patience. Race the fallback against a hard
      // 150s timeout and surface a friendly "use the streaming/long-
      // task path" error instead of letting the 504 reach the user
      // raw.
      if ((err as Error)?.name === "AbortError") return;

      try {
        // KI-0233 (2026-05-27): generate_decomposition_plan with haiku
        // takes ~60-80s (tool) + ~20s Felix overhead = ~80s total on the
        // non-streaming path. 90s was too tight — any haiku variance past
        // 100 tok/s pushed it over. 150s gives 70s headroom for the ~80s
        // call and matches the streaming path's keepalive-backed budget.
        const FALLBACK_TIMEOUT_MS = 150_000;
        // The pending setTimeout from a naive Promise.race keeps the
        // Node event loop alive past the racing promise's resolution,
        // which trips vitest's "write EPIPE" worker-shutdown crash.
        // Track the handle and clear it once the race settles so the
        // event loop drains cleanly.
        let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutHandle = setTimeout(
            () =>
              reject(
                new Error(
                  "KI-0109: Felix took longer than 150s on the non-streaming " +
                    "fallback. The streaming connection probably failed mid-way " +
                    "— retry the message, or for long batch operations (generating " +
                    "personas/stories across many objectives at once) split the " +
                    "request into smaller chunks."
                )
              ),
            FALLBACK_TIMEOUT_MS
          );
        });
        let result;
        try {
          result = await Promise.race([
            felixChat.mutateAsync(requestBody),
            timeoutPromise,
          ]);
        } finally {
          if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
        }
        if (!mountedRef.current) return;

        // § 7.17 — non-streaming path also surfaces the chip when
        // the backend's grader returned low-band / hallucinated /
        // low_confidence. Same anchoring guard as the SSE path.
        const fallbackLowConfidence = Boolean(
          (result as { low_confidence_chip?: boolean }).low_confidence_chip
        );
        const fallbackClarifier = (
          result as {
            clarifier_payload?:
              | import("@/api/services").ClarifierPayload
              | null;
          }
        ).clarifier_payload;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content: result.response,
                  actions: result.actions?.length ? result.actions : undefined,
                  tokenUsage: result.token_usage,
                  lowConfidenceChip: fallbackLowConfidence,
                  clarifierPayload: fallbackClarifier ?? null,
                  proposals: result.proposals?.length ? result.proposals : undefined,
                }
              : m
          )
        );

        // Surface quick reply buttons when Felix asks a binary question
        setQuickReplies(result.quick_replies || []);

        if (result.actions?.length) {
          // Suppress quick replies when CRUD actions are pending
          setQuickReplies([]);
          const needsConfirm = result.actions.some(actionNeedsConfirmation);
          if (needsConfirm) {
            setMessages((prev) =>
              supersedePendingCards(prev, msgId).map((m) =>
                m.id === msgId ? { ...m, pendingConfirmation: true } : m
              )
            );
          } else {
            adviseBreakRisk(result.actions);
            setTimeout(() => {
              if (mountedRef.current) executeActions(result.actions, msgId);
            }, 400);
          }
        }
      } catch (err) {
        if (!mountedRef.current) return;
        const errorMessage = (err as Error)?.message || String(err);
        console.error("Felix chat error:", err);
        setFelixError(true);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? {
                  ...m,
                  content: `Sorry, I encountered an error: ${errorMessage}. Please try again in a moment.`,
                }
              : m
          )
        );
      }
    }
    // location.search: this callback parses pathname + search to find the open
    // record, so a change of ?story= with no change of pathname must rebuild it.
  }, [input, messages, felixChat, pageName, projectId, isExecuting, isStreaming, executeActions, qaSessionId, submitQAAnswer, offerErrorTicket, location.pathname, location.search, speech, deepgram, useDeepgramMode, sidebarCollapsed, pageContext, autonomousMode, queryClient]);

  // Toggle mic on/off — uses Deepgram when available, browser Speech API otherwise
  const handleMicToggle = useCallback(async () => {
    try {
      // If no STT path is available at all, surface a clear toast
      // instead of the silent no-op that James experienced on
      // 2026-04-19 (FB-0005).
      if (!deepgram.browserSupported && !speech.supported) {
        toast.error(
          "Voice input unavailable",
          "Your browser doesn't support microphone capture. Try Chrome, Edge, or Safari.",
        );
        return;
      }
      if (!useDeepgramMode && !speech.supported) {
        toast.error(
          "Voice input not configured",
          "Deepgram speech-to-text isn't available and this browser lacks built-in recognition. Your admin needs to set DEEPGRAM_API_KEY.",
        );
        return;
      }

      if (useDeepgramMode) {
        if (deepgram.recording) {
          deepgram.stop();
        } else {
          deepgram.clear();
          await deepgram.start();
        }
      } else {
        if (speech.listening) {
          speech.stop();
        } else {
          speech.clear();
          speech.start();
        }
      }
    } catch (err) {
      console.warn("[FelixChat] Mic toggle error:", err);
      toast.error(
        "Microphone failed",
        err instanceof Error ? err.message : "Could not start recording.",
      );
    }
  }, [speech, deepgram, useDeepgramMode, toast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // File upload handler — uploads file via importsApi then auto-sends to Felix
  // DI-04 — core upload path shared by the paperclip picker AND
  // drag-and-drop. Takes a File directly so both entry points run the
  // exact same store → import_id → analyze-chip flow (no duplicate
  // scaffolding).
  const uploadFile = useCallback(async (file: File) => {
    if (!file || !projectId) return;

    const allowedExts = [".docx", ".pdf", ".txt", ".md", ".csv", ".xlsx", ".json"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!allowedExts.includes(ext)) {
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          role: "assistant" as const,
          content: `**Unsupported file type** "${ext}". Please upload one of: ${allowedExts.join(", ")}`,
          timestamp: new Date(),
        },
      ]);
      return;
    }

    // Show uploading status in chat immediately
    const uploadingMsgId = `uploading-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      {
        id: uploadingMsgId,
        role: "user" as const,
        content: `Uploading **${file.name}**...`,
        timestamp: new Date(),
      },
    ]);

    setIsUploading(true);
    try {
      const importResult = await importsApi.upload(projectId, file);

      // Surface the upload + a Felix-side suggestion chip the USER
      // must click to invoke analyze_document. Auto-emitting a
      // user-shaped directive ("Please analyze it...") was filed as
      // FB by David Demsko 2026-05-02 20:48 ET — synthetic
      // user-message provenance violated G's user-intent domain
      // (the 3rd domain in §7.9). Patent spec §7.13 (incoming) names
      // this as the 6th G-domain "provenance-aware intent gate".
      // Until the provenance field is wired through the message
      // envelope, the fix is to require an explicit user click.
      setMessages((prev) =>
        prev.map((m) =>
          m.id === uploadingMsgId
            ? {
                ...m,
                content:
                  `Uploaded **${file.name}** — click below to begin analysis.`,
              }
            : m,
        ),
      );

      // Append a Felix-side message offering the action chip. The
      // user clicks the chip to start the analysis conversation;
      // Felix will ask ONE clarifying question about what to extract
      // before calling analyze_document (KI-0198 fix).
      const suggestionChip =
        `Analyze document: ${file.name} (import_id: ${importResult.id})`;
      setMessages((prev) => [
        ...prev,
        {
          id: `analyze-suggest-${importResult.id}`,
          role: "assistant",
          content:
            `**${file.name}** is ready. Click below and I'll ask what ` +
            `you'd like me to extract before running the analysis.`,
          actions: [],
          suggestions: [suggestionChip],
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      // Replace "uploading..." with error
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setMessages((prev) =>
        prev.map((m) =>
          m.id === uploadingMsgId
            ? { ...m, role: "assistant" as const, content: `**Upload failed:** ${errMsg}. Please try again.` }
            : m
        )
      );
    } finally {
      setIsUploading(false);
    }
  }, [projectId, setMessages, setInput]);

  // Thin adapter for the hidden <input type="file"> picker.
  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset input so the same file can be re-selected next time.
    e.target.value = "";
    if (file) await uploadFile(file);
  }, [uploadFile]);

  // DI-04 — drag-and-drop a document anywhere onto the composer.
  const [isDragOver, setIsDragOver] = useState(false);
  const handleComposerDragOver = useCallback((e: React.DragEvent) => {
    // Only react when files are being dragged (not text selections).
    if (!projectId || !e.dataTransfer.types.includes("Files")) return;
    e.preventDefault();
    setIsDragOver(true);
  }, [projectId]);
  const handleComposerDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);
  const handleComposerDrop = useCallback((e: React.DragEvent) => {
    if (!projectId) return;
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void uploadFile(file);
  }, [projectId, uploadFile]);

  // Coverage for Q&A mode header
  const qaCoverage = qaSession.data?.session
    ? computeCoverage(qaSession.data.session.coverage_scores)
    : 0;
  const qaRound = qaSession.data?.interactions
    ? qaSession.data.interactions.filter((i: QAInteraction) => !!i.answer).length
    : 0;

  const isThinking = felixChat.isPending || submitQAAnswer.isPending || isStreaming;
  const canStop = isThinking || isExecuting;
  const dogState: DogState = felixError ? "dead" : isThinking ? "walking" : "idle";

  // Stop button handler — aborts in-flight streaming/chat requests
  const handleStop = useCallback(() => {
    // Abort streaming request
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
    // Abort non-streaming fallback
    if (chatAbortRef.current) {
      chatAbortRef.current.abort();
      chatAbortRef.current = null;
    }
    // Reset all loading states
    setIsStreaming(false);
    setIsExecuting(false);
    // Add a system message so the user knows Felix was stopped
    setMessages((prev) => {
      // If the last message is an assistant message with no content (still loading),
      // update it to show it was stopped
      const last = prev[prev.length - 1];
      if (last && last.role === "assistant" && !last.content) {
        return prev.map((m, i) =>
          i === prev.length - 1
            ? { ...m, content: "*Stopped by user.*" }
            : m
        );
      }
      // If the last assistant message is still streaming partial content, append stopped note
      if (last && last.role === "assistant" && isStreaming) {
        return prev.map((m, i) =>
          i === prev.length - 1
            ? { ...m, content: m.content + "\n\n*Stopped by user.*" }
            : m
        );
      }
      return prev;
    });
  }, [isStreaming]);
  const isWaitingForQAQuestion = !!qaSessionId && !isThinking && qaSession.data?.interactions &&
    !qaSession.data.interactions.some((i: QAInteraction) => !i.answer && i.status === "ACTIVE");

  // Contextual suggestion chips — personalized from briefing, fallback to static
  const suggestions = useMemo(() => {
    // If briefing has personalized suggestions and briefing is visible, use those
    if (briefingData && !briefingDismissed && briefingData.suggested_actions.length > 0) {
      return briefingData.suggested_actions.map((s) => ({
        label: s.label,
        message: s.chip_text,
      }));
    }
    // THE PATH NAME FOR CHIPS, not the record-aware one (row b1b55364).
    // getSuggestionsForPage switches on the list-page names; "DocumentDetail"
    // and "MeetingDetail" match no case, so making pageName record-aware
    // silently emptied the chip row on exactly the three surfaces this change
    // was meant to improve. The chips are a per-PAGE affordance; the record is
    // carried separately in entity_type/entity_id.
    return getSuggestionsForPage(pathPageName);
  }, [pageName, briefingData, briefingDismissed]);
  const showSuggestions = suggestions.length > 0 && !input.trim() && !isThinking && !isExecuting && !qaSessionId;
  const pendingSuggestionRef = useRef<string | null>(null);

  // Auto-send when a suggestion chip sets the input
  useEffect(() => {
    if (pendingSuggestionRef.current && input === pendingSuggestionRef.current) {
      pendingSuggestionRef.current = null;
      handleSend();
    }
  }, [input, handleSend]);

  // HOME-01 (Claire findings 2/5) — subscriber half of the "open Felix
  // coaching" bridge. Attention-pane items / briefing chips that hold no
  // deep-linkable route publish `felix:open-prompt`
  // (lib/openFelixWithPrompt.ts); we open the panel and reuse the
  // suggestion-chip auto-send mechanism above so the coaching prompt
  // fires immediately instead of sitting dead in the composer.
  // Same window-event idiom as "felix:open-qa-session".
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { prompt?: string; autoSend?: boolean }
        | undefined;
      const prompt =
        typeof detail?.prompt === "string" ? detail.prompt.trim() : "";
      if (!prompt) return;
      setIsOpen(true);
      if (detail?.autoSend !== false) pendingSuggestionRef.current = prompt;
      setInput(prompt);
    };
    window.addEventListener(FELIX_OPEN_PROMPT_EVENT, handler);
    return () => window.removeEventListener(FELIX_OPEN_PROMPT_EVENT, handler);
  }, [setIsOpen]);

  // Auto-dismiss the dead dog after 5 seconds
  useEffect(() => {
    if (!felixError) return;
    const timer = setTimeout(() => setFelixError(false), 5000);
    return () => clearTimeout(timer);
  }, [felixError]);

  // -------------------------------------------------------------------
  // Mic button computed state (extracted from JSX for clarity)
  // -------------------------------------------------------------------
  const micIsRecording = useDeepgramMode ? (deepgram.recording || deepgram.transcribing) : speech.listening;
  const micError = useDeepgramMode ? deepgram.error : speech.error;
  const micSupported = useDeepgramMode ? deepgram.browserSupported : speech.supported;
  // Keep the mic button visible even when STT is unavailable so the user
  // sees a consistent UI and gets an explanatory toast on click instead
  // of the button silently disappearing. This is part of the FB-0005 fix
  // — "STT not functional" was aggravated by the button vanishing on
  // Firefox / when the /transcription/status call transiently 401'd.
  const micVisible = true;
  const micDisabled = isThinking || isExecuting || deepgram.transcribing;
  const micUnavailableReason: string | null = !deepgram.browserSupported && !speech.supported
    ? "Your browser doesn't support voice input. Try Chrome, Edge, or Safari."
    : !useDeepgramMode && !speech.supported
      ? "Deepgram is not configured and this browser doesn't support built-in speech recognition. Ask your admin to set DEEPGRAM_API_KEY."
      : null;
  const micTitle = micError
    ? micError
    : micUnavailableReason
      ? micUnavailableReason
      : deepgram.transcribing
        ? "Transcribing..."
        : !micSupported
          ? "Voice input requires a modern browser with microphone support"
          : micIsRecording
            ? "Tap to stop"
            : useDeepgramMode
              ? "Tap to speak (Deepgram)"
              : "Tap to speak";

  // -------------------------------------------------------------------
  // Panel JSX — used inline or portaled to popout window
  // -------------------------------------------------------------------

  const panelMotionProps = isPopout || felixChatReducedMotion
    ? {}
    : {
        initial: { x: closedX, scale: 0.96, opacity: 0 },
        animate: isOpen
          ? { x: 0, scale: 1, opacity: 1 }
          : { x: closedX, scale: 0.96, opacity: 0 },
        transition: isOpen
          ? {
              // Spring on the position, quick opacity fade-in so the
              // panel feels like it "manifests" rather than just
              // slides. Scale overshoots slightly (mass < 1) so it
              // settles into place with a subtle bounce.
              x: { type: "spring" as const, stiffness: 260, damping: 28, mass: 0.9 },
              scale: { type: "spring" as const, stiffness: 220, damping: 18, mass: 0.9 },
              opacity: { duration: 0.22, ease: "easeOut" as const },
            }
          : {
              // Closing is less dramatic — straight spring, no
              // bounce, so users see Felix retreat rather than
              // collapse.
              x: { type: "spring" as const, stiffness: 260, damping: 34 },
              scale: { duration: 0.18, ease: "easeIn" as const },
              opacity: { duration: 0.16, ease: "easeIn" as const, delay: 0.08 },
            },
      };

  const felixPanel = (
    <m.div
      className={cn(
        isPopout
          ? "relative flex h-full w-full flex-col bg-white dark:bg-slate-900"
          // KI-0195: z-[45] resolves two defeat mechanisms.
          // (1) z-40 backdrops (Objectives, Meetings, etc.) were later in
          //     DOM than Felix — same z-index → DOM order wins → they
          //     painted over Felix.  z-[45] beats them by index.
          // (2) z-50 detail panels (Objectives, Tests, Documents, etc.)
          //     already beat Felix by z-index and remain above.  They are
          //     right-anchored and never spatially intersect the
          //     left-anchored Felix panel, so no new visual conflicts.
          // CommandPalette / Radix dialogs / ConversationPanel (all z-50)
          // are unaffected: higher z-index wins over DOM order.
          : "fixed top-0 left-[var(--sidebar-width)] z-[45] flex flex-col border-r border-slate-200/70 bg-white shadow-2xl h-[100dvh] overflow-hidden max-lg:left-0 max-lg:w-full dark:border-slate-800 dark:bg-slate-900",
        // FB-0105: when panel is closed (off-screen transform only), it
        // still receives pointer events and can briefly clip into the
        // viewport on small displays. Hard-disable pointer + accessibility
        // while closed so it never blocks UI beneath.
        !isPopout && !isOpen && "pointer-events-none"
      )}
      ref={panelRootRef}
      // -1: reachable by script (see the focus effect above) but never a Tab
      // stop, so the panel does not add a keyboard step for everyone else.
      tabIndex={-1}
      aria-hidden={!isPopout && !isOpen ? true : undefined}
      // WCAG 4.1.2 / aria-hidden-focus: when the panel is closed
      // (aria-hidden=true), its focusable children (textarea, buttons,
      // message log with role=log) must NOT be focusable. axe-core
      // flagged this on every authenticated page as a SERIOUS
      // violation. `inert` is the canonical fix — removes pointer +
      // a11y tree + focus from the subtree in one attribute.
      // React 19+ types support inert as a boolean prop.
      inert={!isPopout && !isOpen}
      data-felix-panel=""
      // WCAG 2.1.1: keyboard-only users must be able to close the
      // panel without a mouse. Empirical anchor: UAT iter 16
      // test_escape_closes_felix_panel failed because the only
      // keydown handler was on the textarea (Enter→send). Escape
      // is now handled at the panel root: closes inline panel,
      // closes popout window, suppresses propagation so it doesn't
      // also dismiss command palettes / dialogs the user wasn't
      // targeting.
      onKeyDown={(e) => {
        if (e.key === "Escape" && isOpen) {
          e.stopPropagation();
          if (isPopout) {
            closePopout();
          }
          setIsOpen(false);
        }
      }}
      style={isPopout ? undefined : {
        width: `${felixWidth}px`,
        // FB-0105 / FB-0127 follow-up: Claire 2026-05-04 saw the panel
        // clipped on the right mid-session even though no window resize
        // had fired. Root cause: the persisted `felixWidth` was wider
        // than the viewport could accommodate after the sidebar
        // expanded (collapsed-sidebar → expanded-sidebar widens
        // --sidebar-width by 232px without firing a `resize` event).
        // Cap with a CSS calc so the panel can never exceed
        // `viewport - sidebar - 24px-of-content-breathing-room`,
        // regardless of how stale the JS-side width clamp is.
        maxWidth: "calc(100vw - var(--sidebar-width) - 24px)",
        willChange: "transform",
      }}
      {...panelMotionProps}
    >
      {/* Right-edge resize handle */}
      {!isPopout && (
        <div
          onMouseDown={onResizeMouseDown}
          className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-brand-500/30 transition-colors z-10"
          // role FIRST, then the name. A bare <div> has an implicit role of
          // `generic`, which ARIA prohibits from carrying an accessible name
          // — so this aria-label was silently discarded and axe reported
          // aria-prohibited-attr (serious) on every Felix panel scan. A
          // separator is what this actually is: the draggable divider
          // between the panel and the page.
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
        />
      )}
      {/* Header — Three-row layout: utility toolbar, identity + close, mode toggles */}
      <div className="shrink-0 select-none">
        {/* Animated utility toolbar row — icons that migrated from ribbon */}
        {!isPopout && <FelixUtilityToolbar />}
        {/* Identity row: Felix branding + window controls.
            Modernize: cleaner solid brand-600 header (was a 3-stop
            gradient). Solid color reads sharper alongside the new
            flat sidebar chrome. */}
        <div className="flex items-center gap-3 bg-brand-600 px-5 py-3.5">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm shadow-inner">
            <Brain className="h-5 w-5 text-white" />
            {/* Teams-style presence dot — combines Anthropic status + FTL Felix error rate. */}
            <FelixHealthDot className="absolute -bottom-0.5 -right-0.5 ring-brand-600/40" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-bold text-white tracking-tight">Felix</h3>
            {/* NOT text-white/60. On the bg-brand-600 header (#1d5ff5) that
                composites to 2.85:1, and at 11px this is small text, so the
                floor is 4.5:1. axe measured 2.85 on the 2026-08-25 nightly and
                my own calculation reproduces it exactly, which is why I trust
                the rest of this table:

                  white/60  2.85   white/80  3.92   white/90  4.55
                  white/70  3.36   white/85  4.23   white    5.24

                white/90 passes by 0.05 — a margin one brand-colour tweak
                erases, and re-arming that timer is the mistake this codebase
                has now made twice. Full white instead: hierarchy is already
                carried by 11px against the bold 14px title beside it, the same
                trade as the briefing-card chips.

                The sibling icon buttons keep text-white/60: they are icons,
                not text nodes, axe flagged only this <p>, and changing them
                would restyle the header on no evidence. */}
            <p className="truncate text-xs text-white font-medium">
              {qaSessionId
                ? `Scope Discovery \u00B7 ${qaEntityTitle}`
                : `Program Manager${projectId ? ` \u00B7 ${displayPageName}` : ""}`
              }
            </p>
          </div>
          <div className="flex items-center gap-0.5">
            {qaSessionId && (
              <button
                onClick={(e) => { e.stopPropagation(); exitQAMode(); }}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
                aria-label="Exit Q&A"
                title="Exit scope discovery"
              >
                <XCircle className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setConversationSidebarOpen((prev) => !prev);
              }}
              className={cn(
                "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
                conversationSidebarOpen
                  ? "bg-white/15 text-white"
                  : "text-white/60 hover:bg-white/10 hover:text-white",
              )}
              aria-pressed={conversationSidebarOpen}
              aria-label={
                conversationSidebarOpen
                  ? "Hide conversation history"
                  : "Show conversation history"
              }
              title={
                conversationSidebarOpen
                  ? "Hide conversations"
                  : "Conversation history"
              }
            >
              <History className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFollowMode(!followMode);
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
              aria-pressed={followMode}
              aria-label={followMode ? "Turn Follow Mode off" : "Turn Follow Mode on"}
              title={followMode
                ? "Follow Mode ON — auto-navigate as Felix creates entities"
                : "Follow Mode OFF — stay on current page during autonomous runs"
              }
            >
              {followMode ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                // Save current conversation to server before clearing
                if (messages.length > 0) {
                  const serialized = messages.map((m) => ({
                    role: m.role,
                    content: m.content,
                    actions: m.actions,
                    token_usage: m.tokenUsage,
                  }));
                  conversationSessionsApi
                    .save(chatSessionId, {
                      project_id: projectId,
                      messages: serialized,
                      title: messages.find((m) => m.role === "user")?.content?.slice(0, 80),
                    })
                    .then(() => markChatSessionSaved(chatSessionId))
                    .catch(() => { /* ignore */ });
                }
                setMessages([]);
                lastSavedCountRef.current = 0;
                try { localStorage.removeItem(getProjectStorageKey(chatUserId, projectId)); } catch { /* ignore */ }
                setQaSessionId(null);
                setQaEntityTitle("");
                shownInteractionsRef.current.clear();
                setBriefingDismissed(false);
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="New conversation"
              title="Save current chat and start fresh"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isPopout) closePopout(); else openPopout();
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
              aria-label={isPopout ? "Dock to main window" : "Pop out to separate window"}
              title={isPopout ? "Dock to main window" : "Pop out"}
            >
              <ExternalLink className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (isPopout) closePopout();
                setIsOpen(false);
              }}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close panel"
              title="Close"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        {/* Bottom row: Mode toggles — scrollable toolbar */}
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-3 py-1.5">
          <TooltipProvider delayDuration={300}>
            {/* Sketch mode */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); setSketchMode((prev) => !prev); }}
                  className={cn(
                    "flex items-center gap-1.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-all",
                    sketchMode
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                  )}
                  aria-label={sketchMode ? "Disable sketch mode" : "Enable sketch mode"}
                >
                  <Pencil className="h-3 w-3" />
                  Sketch
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p className="font-medium">Sketch Mode</p>
                <p className="text-xs">{sketchMode ? "ON — Creating freely" : "Create without validation"}</p>
              </TooltipContent>
            </Tooltip>

            {/* Autonomous mode */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => { e.stopPropagation(); setAutonomousMode((prev) => !prev); }}
                  className={cn(
                    "flex items-center gap-1.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-all",
                    autonomousMode
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                  )}
                  aria-label={autonomousMode ? "Disable autonomous mode" : "Enable autonomous mode"}
                >
                  <Zap className="h-3 w-3" />
                  Auto
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p className="font-medium">Autonomous Mode</p>
                <p className="text-xs">{autonomousMode ? "ON — Acting independently" : "Let Felix act independently"}</p>
              </TooltipContent>
            </Tooltip>

            {/* Preview mode (autonomous only) */}
            {autonomousMode && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => { e.stopPropagation(); setPreviewMode((prev) => !prev); }}
                    className={cn(
                      "flex items-center gap-1.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-all",
                      previewMode
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        : "text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                    aria-label={previewMode ? "Disable preview mode" : "Enable preview mode"}
                  >
                    <Eye className="h-3 w-3" />
                    Preview
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px]">
                  <p className="font-medium">Preview Mode</p>
                  <p className="text-xs">{previewMode ? "ON — Review before saving" : "Review before saving"}</p>
                </TooltipContent>
              </Tooltip>
            )}

            <div className="w-px h-4 bg-border mx-0.5 shrink-0" />

            {/* Token usage */}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowTokenUsage((prev) => {
                      const next = !prev;
                      try { localStorage.setItem("felix_show_token_usage", String(next)); } catch { /* ignore */ }
                      return next;
                    });
                  }}
                  className={cn(
                    "flex items-center gap-1.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-all",
                    showTokenUsage
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                      : "text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                  )}
                  aria-label={showTokenUsage ? "Hide token usage" : "Show token usage"}
                >
                  <Coins className="h-3 w-3" />
                  Tokens
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[200px]">
                <p className="font-medium">Token Usage</p>
                <p className="text-xs">{showTokenUsage ? "Visible" : "Show AI costs per message"}</p>
              </TooltipContent>
            </Tooltip>

            {/* Voice */}
            {synthesis.supported && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (synthesis.speaking) synthesis.cancel();
                      setAutoSpeak((prev) => !prev);
                    }}
                    className={cn(
                      "flex items-center gap-1.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-all",
                      autoSpeak
                        ? "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                        : "text-slate-600 dark:text-slate-300 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40",
                    )}
                    aria-label={autoSpeak ? "Disable auto-speak" : "Enable auto-speak"}
                  >
                    {autoSpeak ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
                    Voice
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[200px]">
                  <p className="font-medium">Voice</p>
                  <p className="text-xs">{autoSpeak ? "ON" : "Auto-read responses"}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </TooltipProvider>
        </div>
      </div>

      {/* Sketch mode banner */}
      {sketchMode && (
        <div className="shrink-0 border-b border-purple-300 bg-purple-50 px-4 py-1.5 text-xs text-purple-700 dark:border-purple-700 dark:bg-purple-900/20 dark:text-purple-300 flex items-center gap-2">
          <Pencil className="h-3 w-3" />
          <span className="font-medium">Sketch Mode</span>
          <span className="text-purple-600 dark:text-purple-400">— Entities created as SKETCH (no validation gates). Promote to DRAFT when ready.</span>
        </div>
      )}

      {/* Q&A coverage bar with template progress */}
      {qaSessionId && (
        <div className="shrink-0 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-2">
          {/* Template category indicator */}
          {qaSession.data?.template_plan?.phase === "template" && (() => {
            const plan = qaSession.data.template_plan;
            const cats = plan.categories || [];
            const activeCat = cats.find((c: Record<string, unknown>) => c.status === "active");
            const completedCount = cats.filter((c: Record<string, unknown>) => c.status === "completed").length;
            if (!activeCat) return null;
            return (
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 mb-1.5">
                <span className="font-medium text-brand-600 dark:text-brand-400">
                  {activeCat.name as string}
                </span>
                <span className="text-slate-600 dark:text-slate-300">
                  ({completedCount + 1}/{cats.length} categories)
                </span>
                <div className="flex-1 h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-brand-400 transition-all duration-500"
                    style={{ width: `${((completedCount + 1) / cats.length) * 100}%` }}
                  />
                </div>
              </div>
            );
          })()}
          <div className="flex items-center justify-between text-xs text-slate-600 dark:text-slate-300 mb-1">
            <span className="flex items-center gap-1">
              <ClipboardList className="h-3 w-3" />
              Scope Discovery &mdash; Round {qaRound}
            </span>
            <span className="font-medium text-brand-600 dark:text-brand-400">{qaCoverage}% coverage</span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-500 transition-all duration-500"
              style={{ width: `${Math.min(100, qaCoverage)}%` }}
            />
          </div>
        </div>
      )}

      {/* Autonomous mode banner */}
      {autonomousMode && !qaSessionId && (
        <div className="shrink-0 border-b border-amber-500/30 bg-amber-50 dark:bg-amber-900/20 px-4 py-2">
          <div className="flex items-center gap-2 text-xs">
            <Zap className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            <span className="font-medium text-amber-700 dark:text-amber-300">
              Autonomous Mode{previewMode ? " + Preview" : ""}
            </span>
            <span className="text-amber-700 dark:text-amber-400">
              {previewMode
                ? "— artifacts shown for approval before saving"
                : "— Felix will execute actions without asking"}
            </span>
          </div>
          {autonomousProgress.active && (() => {
            const successCount = autonomousProgress.actions.filter(a => a.success).length;
            const failCount = autonomousProgress.actions.filter(a => !a.success).length;
            return (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-300 mb-1">
                  <span>Working...</span>
                  <span className="font-medium">
                    {successCount} succeeded
                    {failCount > 0 && (
                      <span className="text-red-600 dark:text-red-400 ml-1">
                        · {failCount} failed
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-amber-200/50 dark:bg-amber-800/40 overflow-hidden">
                  <div
                    className={cn(
                      "h-full rounded-full transition-all duration-300 animate-pulse",
                      failCount > 0 && successCount === 0
                        ? "bg-red-500"
                        : failCount > 0
                          ? "bg-amber-500"
                          : "bg-green-500",
                    )}
                    style={{ width: "100%" }}
                  />
                </div>
                {autonomousProgress.actions.length > 0 && (
                  <div className="mt-1.5 max-h-24 overflow-y-auto space-y-0.5">
                    {autonomousProgress.actions.slice(-8).map((a, idx) => (
                      <div
                        key={`${a.tool}-${idx}-${a.success}`}
                        className={cn(
                          "text-xs flex items-center gap-1",
                          a.success
                            ? "text-green-700 dark:text-green-400"
                            : "text-red-600 dark:text-red-400",
                        )}
                      >
                        {a.success ? (
                          <CheckCircle2 className="h-2.5 w-2.5 shrink-0" />
                        ) : (
                          <AlertCircle className="h-2.5 w-2.5 shrink-0" />
                        )}
                        <span className="truncate">{a.detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Phase 4 — Modernize conversation history sidebar + thread.
          Sidebar toggles in from the left edge of the Felix panel
          when the user clicks History in the header. It collapses to
          0 width cleanly so the thread reclaims the whole panel when
          closed. */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {conversationSidebarOpen && (
          <div className="shrink-0 overflow-hidden" style={{ width: 220 }}>
            <FelixConversationSidebar
              projectId={projectId}
              activeSessionId={chatSessionId}
              onSelect={(sessionId) => {
                // Load the selected conversation's messages into the
                // current view. Full session-swap (rotating
                // chatSessionId) is a deeper refactor — for this phase
                // we surface past conversations as a read-back without
                // breaking the live session the user is in.
                conversationSessionsApi
                  .get(sessionId)
                  .then((session) => {
                    if (session?.messages?.length) {
                      const restored: Message[] = session.messages.map((mm, i) => ({
                        id: `recall-${sessionId}-${i}`,
                        role: (mm.role as "user" | "assistant") || "assistant",
                        content: mm.content || "",
                        timestamp: mm.created_at ? new Date(mm.created_at) : new Date(),
                        actions: mm.actions as FelixAction[] | undefined,
                        tokenUsage: mm.token_usage as TokenUsage | undefined,
                      }));
                      setMessages(restored);
                    }
                  })
                  .catch(() => {
                    /* non-fatal */
                  });
                setConversationSidebarOpen(false);
              }}
              onNewConversation={() => {
                // Reuse the "New conversation" action — clear messages
                // and drop the sessionStorage key so chatSessionId
                // regenerates on next render.
                setMessages([]);
                try {
                  const KEY = projectId
                    ? `felix_chat_session_id_${projectId}`
                    : "felix_chat_session_id";
                  sessionStorage.removeItem(KEY);
                } catch { /* ignore */ }
                setConversationSidebarOpen(false);
              }}
            />
          </div>
        )}

      {/* Messages — role="log" + aria-live="polite" so screen-reader
          users get streaming assistant turns announced as they arrive
          (WCAG 4.1.3 / 1.3.1). Empirical anchor: UAT iter 9 cascaded
          6 felix_chat_a11y test failures because no aria-live region
          existed on the conversation surface. */}
      <ScrollArea className="flex-1 min-h-0 overflow-hidden">
        <div
          ref={scrollRef}
          role="log"
          aria-live="polite"
          aria-label="Felix conversation"
          className="space-y-3 p-4 pb-6 min-w-0 w-full overflow-x-hidden"
          style={{ overflowWrap: "break-word", wordBreak: "break-word" }}
        >
          {/* Personalized briefing card — shown at top when available */}
          {briefingData && !briefingDismissed && (
            <BriefingCard
              briefing={briefingData}
              onDismiss={dismissBriefing}
              onSuggestionClick={(chipText) => {
                dismissBriefing();
                pendingSuggestionRef.current = chipText;
                setInput(chipText);
              }}
              onNudgeDismiss={handleNudgeDismiss}
            />
          )}

          {messages.length === 0 && !briefingData && !briefingLoading && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-brand-500/10">
                <Brain className="h-6 w-6 text-brand-600" />
              </div>
              <p className="mb-1 text-sm font-medium text-slate-900 dark:text-slate-100">
                Hi, I&apos;m Felix
              </p>
              <p className="max-w-[300px] text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                Your AI Program Manager. I can create projects, objectives,
                test cases, user stories &mdash; anything you need. I run scope
                discovery sessions, check project status, and explain any
                feature. Just tell me what you need!
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {(projectId
                  ? [
                      "What is this page?",
                      "Show me project status",
                      "Start scope discovery",
                      "Create a user story",
                      "Report an issue",
                    ]
                  : [
                      "Create a new project",
                      "What is this page?",
                      "Report an issue",
                    ]
                ).map((suggestion) => (
                  <button
                    type="button"
                    key={suggestion}
                    onClick={() => {
                      setInput(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-1.5 text-xs text-slate-600 dark:text-slate-300 transition-colors hover:border-brand-300 hover:text-brand-600"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <m.div
              key={msg.id}
              // KI-0230 (the load-bearing fix): the row wrapper is a
              // block-in-block child of the log container. Without
              // w-full + max-w-full, its intrinsic width = content
              // width, and a long unbreakable token (URL, base64,
              // markdown table) makes the row push past the panel.
              // Every prior fix piled wrap rules deeper inside the
              // bubble; none constrained THIS node, so the bubble's
              // own max-w-full resolved against an already-overflown
              // parent. Adding w-full max-w-full here makes the whole
              // CSS stack downstream load-bearing instead of
              // decorative. data-felix-message-row is a stable test
              // hook that survives Tailwind-class refactors.
              data-felix-message-row=""
              className="w-full max-w-full min-w-0"
              initial={felixChatReducedMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
              animate={felixChatReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
              transition={
                felixChatReducedMotion
                  ? undefined
                  : { duration: 0.32, ease: FELIX_MSG_EASE }
              }
            >
              {/* FB-0037: removed overflow-hidden from the row wrapper.
                  The inner bubble already clips text overflow; the row
                  wrapper was clipping the absolute-positioned hover
                  buttons (thumbs up/down sit at `-bottom-2 right-0`
                  outside the bubble) so the user never saw them. */}
              <div className="flex gap-2.5 min-w-0">
                {msg.isSystemEvent ? (
                  /* FE-04 — system/status event: a centered status pill,
                     NOT a Felix bubble and NOT a user turn. */
                  <div
                    data-felix-system-event=""
                    className="w-full flex flex-col items-center gap-2 py-1"
                  >
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                      <Check className="h-3.5 w-3.5" />
                      <span>{msg.content}</span>
                    </div>
                    {msg.suggestions && msg.suggestions.length > 0 && (
                      <div className="flex flex-wrap justify-center gap-1.5">
                        {msg.suggestions.map((chip) => (
                          <button
                            key={chip}
                            onClick={() => {
                              setMessages((prev) =>
                                prev.map((m) =>
                                  m.id === msg.id ? { ...m, suggestions: [] } : m
                                )
                              );
                              void handleSend(chip);
                            }}
                            className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs text-brand-700 transition-colors hover:bg-brand-100 hover:border-brand-300 dark:border-brand-700/50 dark:bg-brand-900/20 dark:text-brand-300 dark:hover:bg-brand-900/40"
                          >
                            {chip.replace(/\s*\(import_id:[^)]+\)/g, "")}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : msg.role === "assistant" ? (
                  <>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-500/10">
                      <Brain className="h-3.5 w-3.5 text-brand-600" />
                    </div>
                    {/*
                      KI-0230 ARCHITECTURAL fix (the recurrence-killer).
                      Wrap CSS now lives on the message COLUMN, not the
                      bubble. Every prior fix put `overflowWrap: anywhere`
                      + `[&_*]:max-w-full [&_*]:min-w-0` on the bubble
                      itself. That protected the bubble's children but
                      none of the bubble's *siblings* in this column —
                      FelixChatPlan, FelixChatClarifier, suggestion-chip
                      rows, action-button rows, etc. Every new sibling
                      we add (KI-0210 suggestion chips, plan UI, clarifier)
                      becomes a fresh overflow vector that the bubble-
                      level CSS can't reach.

                      Hoisting `[&_*]:max-w-full [&_*]:min-w-0` and
                      `overflowWrap: anywhere; wordBreak: break-word`
                      onto the column means *every descendant in this
                      column* — current and future — inherits wrap
                      containment by construction. The bubble keeps its
                      own CSS for prose-specific styling (pre, code,
                      table) but the load-bearing wrap guarantee is
                      now at the column level where it cannot be
                      bypassed by adding a new sibling.

                      `data-felix-message-column=""` is the stable test
                      anchor — the new real-render geometric test in
                      FelixChatOverflowRendered.test.tsx asserts every
                      descendant of this node has wrap-capable CSS via
                      actual DOM traversal (not source-string match),
                      so a refactor that violates the contract fails
                      CI before shipping.
                    */}
                    <div
                      data-felix-message-column="assistant"
                      className="min-w-0 w-0 flex-1 space-y-1 [&_*]:max-w-full [&_*]:min-w-0"
                      style={{
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-brand-600 dark:text-brand-400">
                          Felix
                        </span>
                        {msg.dimension && getDimensionLabel(msg.dimension) && (
                          <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                            {getDimensionLabel(msg.dimension)}
                          </span>
                        )}
                      </div>
                      {/* FB-0052 fix: separated text-containment from
                          button-positioning. The OUTER wrapper is
                          `relative` with no overflow clipping so the
                          absolute-positioned hover buttons are never
                          clipped. The INNER bubble has overflow-hidden
                          + aggressive wrap CSS so text content always
                          stays inside regardless of markdown content.
                          Prior structure combined both concerns on
                          one element, which meant fixing one broke
                          the other — the "recurring regression" the
                          ticket called out.
                      */}
                      <div className="group/msg relative max-w-full">
                        {/*
                          FB-0057 / KI-0196 (David 2026-04-29): the
                          Tailwind Typography `prose` modifier sets
                          body text via CSS vars that override the
                          bubble's text-slate-100 in dark mode —
                          resulting in dark-gray text on dark
                          background. `dark:prose-invert` flips the
                          prose palette for dark mode.

                          2026-05-14 follow-up: dark:prose-invert was
                          partial — inline code, blockquotes, table
                          cells, and headings still rendered dark in
                          dark mode because prose-invert sets distinct
                          CSS vars for those that don't always inherit
                          the body color. Added explicit dark text
                          colors for every block element we render so
                          contrast is WCAG-AA across the board.
                        */}
                        <div
                          data-felix-bubble="assistant"
                          className="rounded-lg rounded-tl-sm bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 prose prose-sm prose-slate dark:prose-invert max-w-full overflow-hidden [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0 [&_p]:text-slate-900 dark:[&_p]:text-slate-100 [&_li]:text-slate-900 dark:[&_li]:text-slate-100 [&_strong]:text-slate-900 dark:[&_strong]:text-slate-100 [&_em]:text-slate-900 dark:[&_em]:text-slate-100 [&_h1]:text-slate-900 dark:[&_h1]:text-slate-100 [&_h2]:text-slate-900 dark:[&_h2]:text-slate-100 [&_h3]:text-slate-900 dark:[&_h3]:text-slate-100 [&_h4]:text-slate-900 dark:[&_h4]:text-slate-100 [&_blockquote]:text-slate-700 dark:[&_blockquote]:text-slate-300 [&_blockquote]:border-l-slate-300 dark:[&_blockquote]:border-l-slate-600 [&_a]:text-brand-600 dark:[&_a]:text-brand-400 [&_a]:break-all [&_pre]:overflow-x-hidden [&_pre]:whitespace-pre-wrap [&_pre]:max-w-full [&_pre]:min-w-0 [&_pre]:bg-slate-200 dark:[&_pre]:bg-slate-900 [&_pre]:text-slate-900 dark:[&_pre]:text-slate-100 [&_code]:break-words [&_code]:whitespace-pre-wrap [&_code]:text-[12px] [&_code]:text-slate-900 dark:[&_code]:text-slate-100 [&_code]:bg-slate-200/70 dark:[&_code]:bg-slate-900/70 [&_code]:px-1 [&_code]:rounded [&_table]:table-fixed [&_table]:w-full [&_table]:max-w-full [&_th]:text-slate-900 dark:[&_th]:text-slate-100 [&_td]:text-slate-900 dark:[&_td]:text-slate-100 [&_img]:max-w-full [&_*]:max-w-full [&_*]:min-w-0"
                          style={{
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            hyphens: "auto",
                          }}
                        >
                          {msg.content === FELIX_THINKING_SEED ? (
                            <FelixThinking label={isStreaming ? msg.status : undefined} />
                          ) : (
                            <>
                              <Markdown components={markdownComponents}>{msg.content}</Markdown>
                              {/* Row f5098ee9: a held round after words were shown
                                  says what Felix is doing until his next words. */}
                              {isStreaming && msg.status && msg.id === messages[messages.length - 1]?.id && (
                                <div className="not-prose mt-2 border-t border-slate-200 pt-2 dark:border-slate-700">
                                  <FelixThinking label={msg.status} />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* Hover action buttons — speech + thumbs (touch devices: always visible) */}
                        <div className={cn(
                          "absolute right-0 -bottom-2 flex items-center gap-0.5 transition-opacity",
                          ratedMessages[msg.id]
                            ? "opacity-100"
                            : "opacity-0 group-hover/msg:opacity-100 [@media(hover:none)]:opacity-100"
                        )}>
                          {synthesis.supported && (
                            <button
                              onClick={() => {
                                if (synthesis.speaking) {
                                  synthesis.cancel();
                                } else {
                                  synthesis.speak(msg.content);
                                }
                              }}
                              className="flex h-6 w-6 items-center justify-center rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:text-brand-600 hover:border-brand-300"
                              aria-label={synthesis.speaking ? "Stop speaking" : "Read aloud"}
                              title={synthesis.speaking ? "Stop" : "Read aloud"}
                            >
                              {synthesis.speaking ? (
                                <VolumeX className="h-3 w-3" />
                              ) : (
                                <Volume2 className="h-3 w-3" />
                              )}
                            </button>
                          )}
                          {/* Thumbs up */}
                          <button
                            onClick={() => handleRate(msg.id, "positive")}
                            className={cn(
                              "flex h-6 w-6 items-center justify-center rounded-full border transition-colors",
                              ratedMessages[msg.id] === "positive"
                                ? "bg-green-50 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-400 opacity-100"
                                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-green-600 hover:border-green-300",
                              ratedMessages[msg.id] && ratedMessages[msg.id] !== "positive" && "hidden"
                            )}
                            aria-label="Thumbs up"
                            title="Helpful"
                            disabled={!!ratedMessages[msg.id]}
                          >
                            <ThumbsUp className="h-3 w-3" />
                          </button>
                          {/* Thumbs down */}
                          <button
                            onClick={() => handleRate(msg.id, "negative")}
                            className={cn(
                              "flex h-6 w-6 items-center justify-center rounded-full border transition-colors",
                              ratedMessages[msg.id] === "negative"
                                ? "bg-red-50 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400 opacity-100"
                                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-red-600 hover:border-red-300",
                              ratedMessages[msg.id] && ratedMessages[msg.id] !== "negative" && "hidden"
                            )}
                            aria-label="Thumbs down"
                            title="Not helpful"
                            disabled={!!ratedMessages[msg.id]}
                          >
                            <ThumbsDown className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      {/* Token usage indicator — shown below assistant messages */}
                      {msg.tokenUsage && showTokenUsage && (
                        <TokenUsageIndicator usage={msg.tokenUsage} />
                      )}
                      {/*
                        § 7.17 low-confidence chip — surfaced ONLY
                        when the backend's pre-emit grader returned
                        low-band confidence or a hallucinated_action
                        / low_confidence verdict. Anchoring guard
                        (Lee & See 2004 + Report 1 §3): no medium /
                        high band ever reaches the user as a
                        percentage or verbal label.
                      */}
                      {msg.lowConfidenceChip && (
                        <div
                          className="mt-1 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800 dark:border-amber-600/50 dark:bg-amber-900/20 dark:text-amber-300"
                          data-testid="low-confidence-chip"
                          title="Felix is less certain about parts of this response. Double-check before acting."
                        >
                          <span aria-hidden="true">⚠️</span>
                          <span>low confidence</span>
                        </div>
                      )}
                      {/* KI-0210: post-action / post-upload suggestion chips */}
                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {msg.suggestions.map((chip) => (
                            <button
                              key={chip}
                              onClick={() => {
                                setMessages((prev) =>
                                  prev.map((m) =>
                                    m.id === msg.id ? { ...m, suggestions: [] } : m
                                  )
                                );
                                // Send directly — no setInput/setTimeout race (KI-0218 pattern).
                                void handleSend(chip);
                              }}
                              className="rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-xs text-brand-700 transition-colors hover:bg-brand-100 hover:border-brand-300 dark:border-brand-700/50 dark:bg-brand-900/20 dark:text-brand-300 dark:hover:bg-brand-900/40"
                            >
                              {chip.replace(/\s*\(import_id:[^)]+\)/g, "")}
                            </button>
                          ))}
                        </div>
                      )}
                      {/* § 7.15 Phase 2 plan progress */}
                      {msg.plan && msg.plan.steps.length > 0 && (
                        <FelixChatPlan plan={msg.plan} />
                      )}
                      {/* § 7.15 Phase 1 clarifier — sends a structured
                          response back into the chat input so the next
                          turn carries the user's answer in a form the
                          backend can route to the same tool. */}
                      {msg.clarifierPayload && (
                        <FelixChatClarifier
                          payload={msg.clarifierPayload}
                          onSubmit={(composed) => {
                            // Clear the clarifier from this message so
                            // it can't be re-submitted, then push the
                            // composed answer as if the user typed it.
                            setMessages((prev) =>
                              prev.map((mm) =>
                                mm.id === msg.id
                                  ? { ...mm, clarifierPayload: null }
                                  : mm,
                              ),
                            );
                            // Send directly — no setInput/setTimeout race (KI-0218 pattern).
                            void handleSend(composed);
                          }}
                        />
                      )}
                      {/* Q&A Entity Review — Approve/Reject buttons */}
                      {msg.metadata?.type === "qa_review" && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            onClick={async () => {
                              try {
                                await qaSessionsApi.approve(msg.metadata!.session_id as string);
                                setMessages((prev) =>
                                  prev.map((m) =>
                                    m.id === msg.id
                                      ? { ...m, metadata: { ...m.metadata!, type: "qa_approved" } }
                                      : m,
                                  ),
                                );
                                setMessages((prev) => [
                                  ...prev,
                                  {
                                    id: `approved-${Date.now()}`,
                                    role: "assistant",
                                    content: `**${msg.metadata!.entity_title}** has been approved and is now active.`,
                                    timestamp: new Date(),
                                  },
                                ]);
                              } catch {
                                setMessages((prev) => [
                                  ...prev,
                                  {
                                    id: `err-approve-${Date.now()}`,
                                    role: "assistant",
                                    content: "Failed to approve entity. Please try again.",
                                    timestamp: new Date(),
                                  },
                                ]);
                              }
                            }}
                            className="flex items-center gap-1 rounded-md bg-green-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-800 transition-colors"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Approve
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await qaSessionsApi.reject(msg.metadata!.session_id as string);
                                setMessages((prev) =>
                                  prev.map((m) =>
                                    m.id === msg.id
                                      ? { ...m, metadata: { ...m.metadata!, type: "qa_rejected" } }
                                      : m,
                                  ),
                                );
                                setMessages((prev) => [
                                  ...prev,
                                  {
                                    id: `rejected-${Date.now()}`,
                                    role: "assistant",
                                    content: `**${msg.metadata!.entity_title}** has been sent back to DRAFT for rework.`,
                                    timestamp: new Date(),
                                  },
                                ]);
                              } catch {
                                setMessages((prev) => [
                                  ...prev,
                                  {
                                    id: `err-reject-${Date.now()}`,
                                    role: "assistant",
                                    content: "Failed to reject entity. Please try again.",
                                    timestamp: new Date(),
                                  },
                                ]);
                              }
                            }}
                            className="flex items-center gap-1 rounded-md border border-red-300 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors dark:text-red-400"
                          >
                            <X className="h-3.5 w-3.5" />
                            Reject
                          </button>
                        </div>
                      )}
                      {msg.metadata?.type === "qa_approved" && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          <span>Approved</span>
                        </div>
                      )}
                      {msg.metadata?.type === "qa_rejected" && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                          <XCircle className="h-3.5 w-3.5" />
                          <span>Sent back to DRAFT</span>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex-1" />
                    {/* KI-0230 defense: max-w-[85%] resolves against
                        the flex parent's content width, which can grow
                        past the panel if the row wrapper above lost
                        its constraint. Cap with min(85%, 100%-2rem)
                        so the wrapper is never wider than its row,
                        regardless of percentage-resolution quirks. */}
                    <div
                      data-felix-message-column="user"
                      className="min-w-0 space-y-1 [&_*]:max-w-full [&_*]:min-w-0"
                      style={{
                        maxWidth: "min(85%, calc(100% - 2rem))",
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {/* FB-0052: same wrap-aggressive CSS as the
                          assistant bubble so user messages with long
                          unbroken strings (URLs, error traces) wrap
                          instead of clipping. */}
                      <div
                        data-felix-bubble="user"
                        className="rounded-2xl rounded-tr-sm bg-brand-600 px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap overflow-hidden shadow-sm [&_*]:max-w-full [&_*]:min-w-0"
                        style={{
                          overflowWrap: "anywhere",
                          wordBreak: "break-word",
                          hyphens: "auto",
                        }}
                      >
                        {msg.content.replace(/\s*\(import_id:[^)]+\)/g, "")}
                      </div>
                    </div>
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
                      <User className="h-3.5 w-3.5 text-slate-600 dark:text-slate-300" />
                    </div>
                  </>
                )}
              </div>

              {/* Action confirmation prompt */}
              {msg.pendingConfirmation && msg.actions && (
                <div className="ml-9 mt-2 space-y-2">
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="mb-2 text-xs font-medium text-amber-800">
                      Felix wants to perform {msg.actions.length} action{msg.actions.length > 1 ? "s" : ""}:
                    </p>
                    <ul className="mb-2 space-y-1">
                      {msg.actions.map((a, idx) => (
                        <li key={idx} className="flex items-center gap-1.5 text-xs text-amber-700">
                          <Zap className="h-3 w-3 shrink-0" />
                          {a.label || a.type}
                        </li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleConfirmActions(msg.id)}
                        className="h-7 text-xs"
                      >
                        Go ahead
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancelActions(msg.id)}
                        className="h-7 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                    {/* Autonomous mode tip for bulk actions */}
                    {!autonomousMode && msg.actions.length >= 3 && (
                      <button
                        onClick={() => setAutonomousMode(true)}
                        className="mt-2 flex items-center gap-1.5 text-xs text-amber-700 hover:text-amber-800 transition-colors"
                      >
                        <Zap className="h-3 w-3" />
                        <span>
                          <span className="font-medium">Tip:</span> Enable Autonomous Mode to
                          skip individual confirmations for bulk operations
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Row fad9bff7: Felix's approval cards. He never approves or
                  advances himself; the person's Approve runs it, as them. */}
              {msg.proposals && msg.proposals.length > 0 && (
                <div className="ml-9 mt-2 space-y-2">
                  {msg.proposals.map((p) => (
                    <FelixProposalCard
                      key={p.id}
                      proposal={p}
                      onChange={(prompt) => {
                        setInput(prompt);
                        inputRef.current?.focus();
                      }}
                    />
                  ))}
                </div>
              )}

              {/* Tools used — reasoning transparency */}
              {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                <div className="ml-9 mt-2">
                  <details className="group">
                    <summary className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                      <svg className="h-3 w-3 transition-transform group-open:rotate-90" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 2l4 4-4 4" /></svg>
                      Felix used {msg.toolsUsed.length} tool{msg.toolsUsed.length !== 1 ? "s" : ""} to answer
                    </summary>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {msg.toolsUsed.map((tool, idx) => (
                        <span key={idx} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-50 dark:bg-brand-900/20 text-xs font-medium text-brand-600 dark:text-brand-400 ring-1 ring-brand-200/50 dark:ring-brand-700/50">
                          {tool.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  </details>
                </div>
              )}

              {/* Action execution status */}
              {msg.actionResults && msg.actionResults.length > 0 && (
                <div className="ml-9 mt-2 space-y-1.5">
                  {msg.actionResults.map((ar, idx) => (
                    <div
                      key={idx}
                      className={cn(
                        "flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs",
                        ar.status === "success" &&
                          "border-green-200 bg-green-50 text-green-700",
                        ar.status === "error" &&
                          "border-red-200 bg-red-50 text-red-700",
                        ar.status === "running" &&
                          "border-brand-200 bg-brand-50 text-brand-700",
                        ar.status === "pending" &&
                          "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-600 dark:text-slate-300",
                      )}
                    >
                      {ar.status === "success" && (
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                      )}
                      {ar.status === "error" && (
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                      )}
                      {ar.status === "running" && (
                        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                      )}
                      {ar.status === "pending" && (
                        <Zap className="h-3.5 w-3.5 shrink-0 opacity-40" />
                      )}
                      <span className="truncate">
                        {ar.detail || ar.action.label || ar.action.type}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </m.div>
          ))}

          {((isThinking && !isStreaming) || isWaitingForQAQuestion || felixError) && (
            <div
              className={cn(
                "flex justify-center py-1",
                isThinking && !felixError && "cursor-pointer group",
              )}
              onClick={isThinking && !felixError ? handleStop : undefined}
              title={isThinking && !felixError ? "Click to stop Felix" : undefined}
            >
              <FelixDog
                state={dogState}
                label={
                  felixError
                    ? undefined
                    : qaSessionId
                      ? "Preparing next question..."
                      : "Felix is thinking... (click to stop)"
                }
              />
            </div>
          )}
          {/* KI-0120: anchor used by the scroll-to-bottom effect so re-opening
              the chat always lands on the most recent message. */}
          <div ref={bottomAnchorRef} aria-hidden="true" />
        </div>
      </ScrollArea>
      </div>
      {/* /Phase 4 sidebar + thread flex wrapper */}

      {/* Parked entities (noted items) */}
      <ParkedEntitiesPanel />

      {/* Row f14fbbc8: a failed request opened Felix on its own */}
      <FelixAppErrorNotice />

      {/* Background tasks indicator */}
      {autonomousMode && (
        <BackgroundTasksWidget />
      )}

      {/* Preview Approval Panel */}
      {previewArtifacts.length > 0 && !isStreaming && (
        <div className="shrink-0 border-t border-blue-300/50 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 space-y-2 max-h-[200px] overflow-y-auto">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-blue-700 dark:text-blue-300 flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {previewArtifacts.filter(a => a.result.success).length} artifact{previewArtifacts.filter(a => a.result.success).length !== 1 ? "s" : ""} ready for review
            </span>
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDiscardPreviews}
                disabled={commitPending}
                className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 px-2"
              >
                <X className="h-3 w-3 mr-1" />
                Discard All
              </Button>
              <Button
                size="sm"
                onClick={handleCommitPreviews}
                disabled={commitPending || previewArtifacts.filter(a => a.approved && a.result.success).length === 0}
                className="h-6 text-xs bg-blue-600 hover:bg-blue-700 text-white px-2"
              >
                {commitPending ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Check className="h-3 w-3 mr-1" />
                )}
                Approve All ({previewArtifacts.filter(a => a.approved && a.result.success).length})
              </Button>
            </div>
          </div>
          {previewArtifacts.filter(a => a.result.success).map((artifact, i) => (
            <div
              key={i}
              className={cn(
                "flex items-center gap-2 rounded border px-2 py-1.5 text-xs",
                artifact.approved
                  ? "border-blue-200 bg-white dark:bg-blue-900/30 dark:border-blue-700"
                  : "border-gray-200 bg-gray-50 dark:bg-gray-800/30 dark:border-gray-700 opacity-60",
              )}
            >
              <button
                onClick={() =>
                  setPreviewArtifacts((prev) =>
                    prev.map((a, j) =>
                      j === i ? { ...a, approved: !a.approved } : a
                    )
                  )
                }
                className={cn(
                  "h-4 w-4 rounded border flex items-center justify-center shrink-0",
                  artifact.approved
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "border-gray-300 dark:border-gray-600",
                )}
              >
                {artifact.approved && <Check className="h-2.5 w-2.5" />}
              </button>
              <span className={cn(
                "inline-block rounded px-1.5 py-0.5 text-xs font-medium uppercase",
                artifact.result.action === "create" && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                artifact.result.action === "update" && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                artifact.result.action === "delete" && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
              )}>
                {artifact.result.action ?? "create"}
              </span>
              <span className="text-slate-600 dark:text-slate-300 truncate flex-1">
                {artifact.result.entity_type}: {artifact.result.title || artifact.result.detail || artifact.tool}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Row fad9bff7: approval cards still waiting (e.g. proposed in a meeting) */}
      {waitingProposals.length > 0 && (
        <div
          data-testid="felix-waiting-proposals"
          className="shrink-0 max-h-64 space-y-2 overflow-y-auto border-t border-slate-200 px-3 py-2 dark:border-slate-700"
        >
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200">Waiting for your approval</p>
          {waitingProposals.map((p) => (
            <FelixProposalCard
              key={p.id}
              proposal={p}
              onChange={(prompt) => {
                setInput(prompt);
                inputRef.current?.focus();
              }}
            />
          ))}
        </div>
      )}

      {/* Quick Reply Buttons — shown when Felix asks a binary/ternary question */}
      {quickReplies.length > 0 && !isThinking && !isExecuting && !input.trim() && (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-3 py-2">
          <div className="flex flex-wrap gap-2">
            {quickReplies.map((qr, i) => (
              <button
                key={`qr-${i}-${qr.label}`}
                type="button"
                className={cn(
                  "px-3 py-1.5 rounded-lg text-sm font-medium transition-all active:scale-[0.98]",
                  qr.style === "primary" && "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700",
                  qr.style === "danger" && "bg-red-600 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700",
                  (!qr.style || qr.style === "secondary") && "bg-slate-50 dark:bg-slate-800/40 text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800-hover",
                )}
                onClick={() => {
                  setQuickReplies([]);
                  // Send directly with the button's message — no
                  // setInput/effect round-trip (KI-0218).
                  void handleSend(qr.message);
                }}
                aria-label={`Quick reply: ${qr.label}`}
              >
                {qr.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggestion Chips — hidden when quick replies are active */}
      {showSuggestions && quickReplies.length === 0 && (
        <div className="shrink-0 border-t border-slate-200 dark:border-slate-700 px-3 pt-2 pb-0">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-thin pb-1">
            <Sparkles className="h-3 w-3 shrink-0 text-amber-500" />
            {suggestions.map((chip) => (
              <button
                key={chip.label}
                type="button"
                className="shrink-0 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-2.5 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 transition-all hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 hover:shadow-sm dark:hover:bg-brand-900/20 dark:hover:text-brand-400 felix-chip-enter"
                onClick={() => {
                  pendingSuggestionRef.current = chip.message;
                  setInput(chip.message);
                }}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className={cn("shrink-0 border-t border-slate-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-900", (showSuggestions && quickReplies.length === 0) && "border-t-0 pt-1.5")}>
        <div
          onDragOver={handleComposerDragOver}
          onDragLeave={handleComposerDragLeave}
          onDrop={handleComposerDrop}
          className={cn(
            "relative flex items-end gap-2 rounded-xl border bg-slate-50/60 dark:bg-slate-800/40 px-2 py-1.5 transition-colors",
            isDragOver
              ? "border-brand-400 border-dashed bg-brand-50/60 dark:bg-brand-900/20 ring-2 ring-brand-300/50"
              : "border-slate-200/70 dark:border-slate-800",
          )}
          data-testid="felix-composer"
        >
          {isDragOver && projectId && (
            <div className="pointer-events-none absolute inset-x-3 -top-6 flex justify-center">
              <span className="rounded-full bg-brand-600 px-3 py-0.5 text-xs font-medium text-white shadow">
                Drop to attach for Felix to analyze
              </span>
            </div>
          )}
          {/* Hidden file input for document upload */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx,.pdf,.txt,.md,.csv,.xlsx,.json"
            onChange={handleFileUpload}
            className="hidden"
            aria-label="Upload document"
          />
          {/* Upload button — only visible when in a project context */}
          {projectId && (
            <Button
              size="icon"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isThinking || isExecuting || isUploading}
              className="shrink-0"
              aria-label="Upload document"
              title="Upload a document for Felix to analyze"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </Button>
          )}
          <Textarea
            ref={inputRef}
            autoResize
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              qaSessionId
                ? "Type your answer..."
                : "Ask Felix anything..."
            }
            disabled={isThinking || isExecuting}
            data-testid="felix-chat-input"
            className="min-h-[40px] max-h-[120px] text-sm border-0 bg-transparent shadow-none focus-visible:ring-0 px-1"
          />
          {(micStatusHint || micError) && (
            <div
              className={cn(
                "px-2 py-1 text-xs rounded-md self-center shrink-0",
                micError
                  ? "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                  : "bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
              )}
              aria-live="polite"
            >
              {micError || micStatusHint}
            </div>
          )}
          {micVisible && (
            <Button
              size="icon"
              variant={micIsRecording ? "destructive" : micError ? "outline" : "outline"}
              onClick={handleMicToggle}
              disabled={micDisabled}
              className={cn(
                "shrink-0 transition-all",
                micIsRecording && !deepgram.transcribing && "animate-pulse",
                micError && "border-red-300",
              )}
              aria-label={micIsRecording ? "Stop recording" : "Voice input"}
              title={micTitle}
            >
              {deepgram.transcribing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : micIsRecording ? (
                <MicOff className="h-4 w-4" />
              ) : (
                <Mic className={cn("h-4 w-4", !micSupported && "opacity-40", micError && "text-red-500")} />
              )}
            </Button>
          )}

          {canStop ? (
            <Button
              size="icon"
              variant="destructive"
              onClick={handleStop}
              className="shrink-0"
              aria-label="Stop Felix"
              title="Stop Felix"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          ) : (
            <Button
              size="icon"
              onClick={() => void handleSend()}
              disabled={!input.trim()}
              className="shrink-0"
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Left-edge resize is handled by the absolute div at the top of the panel */}
    </m.div>
  );

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <>
      {/* Mobile backdrop overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Manifestation burst — a brand-colored radial glow that
          expands behind the panel when Felix opens. Only fires on
          desktop (lg+) and only when motion is allowed. The panel
          itself is always mounted (for spring animation), but this
          burst is gated by AnimatePresence so it reruns each open. */}
      <AnimatePresence>
        {isOpen && !isPopout && !felixChatReducedMotion && (
          <m.div
            key="felix-manifest-burst"
            aria-hidden
            className="pointer-events-none fixed top-0 left-[var(--sidebar-width)] z-30 hidden lg:block h-[100dvh]"
            style={{ width: `${felixWidth * 1.6}px` }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.45, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.95, times: [0, 0.28, 1], ease: "easeOut" as const }}
          >
            <div
              className="h-full w-full blur-3xl"
              style={{
                background:
                  "radial-gradient(ellipse 55% 85% at 8% 50%, rgba(59,122,255,0.6) 0%, rgba(0,200,255,0.3) 35%, transparent 72%)",
              }}
            />
          </m.div>
        )}
      </AnimatePresence>

      {/* Right-edge manifestation shimmer — a thin vertical light
          stripe that sweeps down the panel's right border on open,
          reinforcing that the panel just arrived. */}
      <AnimatePresence>
        {isOpen && !isPopout && !felixChatReducedMotion && (
          <m.div
            key="felix-edge-shimmer"
            aria-hidden
            className="pointer-events-none fixed top-0 z-40 hidden lg:block h-[100dvh] w-[3px]"
            style={{
              left: `calc(var(--sidebar-width) + ${felixWidth}px - 2px)`,
              background:
                "linear-gradient(180deg, transparent 0%, rgba(125,250,255,0.9) 45%, rgba(0,200,255,0.6) 55%, transparent 100%)",
              filter: "blur(1px)",
            }}
            initial={{ opacity: 0, scaleY: 0, y: -80 }}
            animate={{ opacity: [0, 1, 0], scaleY: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 0.7, times: [0, 0.4, 1], ease: "easeOut" as const },
              scaleY: { duration: 0.42, ease: [0.2, 0.8, 0.2, 1] },
              y: { duration: 0.42, ease: [0.2, 0.8, 0.2, 1] },
            }}
          />
        )}
      </AnimatePresence>

      {/* Chat panel — single render slot. Pop-out toggles between
          inline render and createPortal render. Using two sibling
          slots (one for isPopout=false, one for isPopout=true) made
          React see the panel as a different child position when the
          flag flipped, causing it to UNMOUNT the inline panel and
          MOUNT a fresh portal copy with empty `useState` for
          messages — so the conversation appeared blank when Claire
          expanded to full-page (FB-0127 / KI-0209, 2026-05-04).
          A single ternary keeps the same React instance — only the
          DOM container moves — so messages, scroll state, draft
          input, and stream subscriptions all survive the swap. */}
      {isPopout && popoutContainer
        ? createPortal(felixPanel, popoutContainer)
        : felixPanel}
    </>
  );
}

// ---------------------------------------------------------------------------
// FelixUtilityToolbar — animated horizontal row of utility icons from ribbon
// ---------------------------------------------------------------------------

const FELIX_THEME_CYCLE: Record<ThemeMode, ThemeMode> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const FELIX_THEME_ICON: Record<ThemeMode, typeof Sun> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const FELIX_THEME_LABEL: Record<ThemeMode, string> = {
  light: "Dark mode",
  dark: "System theme",
  system: "Light mode",
};

function FelixUtilityToolbar() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearUser = useAuthStore((s) => s.clearUser);
  const { mode: themeMode, setMode: setThemeMode } = useThemeStore();

  // Notifications
  const { data: notifData } = useNotifications({ limit: 5, unread_only: true });
  const unreadCount = notifData?.total ?? 0;

  // Gmail unread
  const { data: gmailData } = useQuery({
    queryKey: ["gmail-unread-count"],
    queryFn: () => gmailApi.getUnreadCount(),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const gmailCount = (gmailData as Record<string, number> | undefined)?.count ?? 0;

  // Next meeting
  const { data: calData } = useQuery({
    queryKey: ["calendar-next-meeting"],
    queryFn: () => calendarApi.listEvents({ max_results: 1 }),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: false,
  });
  const events = (calData as Record<string, unknown> | undefined)?.events as Array<Record<string, unknown>> | undefined;
  const nextMeeting = events?.[0];
  const meetingVisible = (() => {
    if (!nextMeeting?.start_time) return false;
    const diffMin = Math.round((new Date(nextMeeting.start_time as string).getTime() - Date.now()) / 60000);
    return diffMin >= 0 && diffMin <= 120;
  })();

  // State
  const [composeOpen, setComposeOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayName = user?.name ?? "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "U";

  async function handleLogout() {
    try { await authApi.logout(); } catch { /* ignore */ }
    clearTokens();
    clearUser();
    navigate("/login");
  }

  const ThemeIcon = FELIX_THEME_ICON[themeMode];

  return (
    <>
      <div className="flex items-center gap-1 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 px-3 py-1.5 overflow-x-auto scrollbar-thin">
        {/* Gmail */}
        {gmailCount > 0 && (
          <button
            onClick={() => navigate("/gmail")}
            className="animate-to-header relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            title={`${gmailCount} unread email${gmailCount !== 1 ? "s" : ""}`}
            style={{ animationDelay: "0ms" }}
          >
            <Mail className="h-3.5 w-3.5" />
            <span className="absolute -right-0.5 -top-0.5 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-brand-500 px-0.5 text-[7px] font-bold text-white">
              {gmailCount > 9 ? "!" : gmailCount}
            </span>
          </button>
        )}

        {/* Next meeting */}
        {meetingVisible && nextMeeting && (
          <button
            onClick={() => {
              const meetUrl = nextMeeting.meeting_url as string | undefined;
              if (meetUrl) window.open(meetUrl, "_blank"); else navigate("/calendar");
            }}
            className="animate-to-header flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            title={`${nextMeeting.summary}`}
            style={{ animationDelay: "30ms" }}
          >
            {nextMeeting.meeting_url ? <Video className="h-3.5 w-3.5" /> : <CalendarClock className="h-3.5 w-3.5" />}
          </button>
        )}

        {/* Compose */}
        <button
          onClick={() => setComposeOpen(true)}
          className="animate-to-header flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          title="Compose email"
          style={{ animationDelay: "60ms" }}
        >
          <PenSquare className="h-3.5 w-3.5" />
        </button>

        {/* Notifications — popover is portaled to document.body so it
            escapes Felix's ``overflow-hidden`` panel (which previously
            clipped it, making the popover appear "behind Felix"). */}
        <div ref={notifRef} className="animate-to-header" style={{ animationDelay: "90ms" }}>
          <button
            onClick={() => { setNotifOpen(!notifOpen); setUserMenuOpen(false); }}
            className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            title="Notifications"
          >
            <Bell className="h-3.5 w-3.5" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-error px-0.5 text-[7px] font-bold text-white">
                {unreadCount > 9 ? "!" : unreadCount}
              </span>
            )}
          </button>
          {notifOpen &&
            notifRef.current &&
            createPortal(
              <div
                className="fixed z-[60] w-80 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                style={{
                  top: notifRef.current.getBoundingClientRect().bottom + 6,
                  left: notifRef.current.getBoundingClientRect().left,
                }}
              >
                <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
                  <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">Notifications</span>
                  {unreadCount > 0 && (
                    <span className="rounded-full bg-brand-100 px-2 py-0.5 text-xs font-medium text-brand-700">
                      {unreadCount} new
                    </span>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifData?.items && notifData.items.length > 0 ? (
                    notifData.items.map((n) => (
                      <div
                        key={n.id}
                        className={cn(
                          "border-b border-slate-200/70 px-3 py-2 text-xs hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-800/40",
                          !n.read_at && "bg-brand-50/50 dark:bg-brand-500/10",
                        )}
                      >
                        <p className="text-slate-900 dark:text-slate-100">{n.message}</p>
                        <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
                          {new Date(n.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="px-3 py-6 text-center text-xs text-slate-600 dark:text-slate-300">
                      No new notifications
                    </div>
                  )}
                </div>
              </div>,
              document.body,
            )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Theme toggle */}
        <button
          onClick={() => setThemeMode(FELIX_THEME_CYCLE[themeMode])}
          className="animate-to-header flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
          title={FELIX_THEME_LABEL[themeMode]}
          style={{ animationDelay: "120ms" }}
        >
          <ThemeIcon className="h-3.5 w-3.5" />
        </button>

        {/* User avatar — menu is portaled to escape Felix overflow. */}
        <div ref={userRef} className="animate-to-header" style={{ animationDelay: "150ms" }}>
          <button
            onClick={() => { setUserMenuOpen(!userMenuOpen); setNotifOpen(false); }}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-xs font-semibold text-white shadow-sm"
            title={displayName}
          >
            {initials}
          </button>
          {userMenuOpen &&
            userRef.current &&
            createPortal(
              <div
                className="fixed z-[60] w-52 rounded-lg border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900"
                style={{
                  top: userRef.current.getBoundingClientRect().bottom + 6,
                  right: Math.max(
                    8,
                    window.innerWidth - userRef.current.getBoundingClientRect().right,
                  ),
                }}
              >
                <div className="border-b border-slate-200 px-3 py-2 dark:border-slate-700">
                  <p className="text-xs font-medium text-slate-900 dark:text-slate-100">{displayName}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-300">{user?.email}</p>
                </div>
                <div className="py-1">
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800/40 dark:hover:text-slate-100"
                  >
                    <Settings className="h-3 w-3" />
                    Settings
                  </Link>
                </div>
                <div className="border-t border-slate-200 py-1 dark:border-slate-700">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-700 hover:bg-error/5 dark:text-red-400"
                  >
                    <LogOut className="h-3 w-3" />
                    Sign out
                  </button>
                </div>
              </div>,
              document.body,
            )}
        </div>
      </div>

      <ComposeEmailDialog open={composeOpen} onClose={() => setComposeOpen(false)} />
    </>
  );
}
